/**
 * Content schemas — champions, abilities, effects, maps, tuning knobs.
 *
 * These are parsed at server boot by @pit/content. Any parse failure ⇒
 * server refuses to start (§0.2 boundary rule).
 */

import { z } from "zod";
import {
  ABILITY_ID_SCHEMA,
  ABILITY_SLOT_SCHEMA,
  AURA_DEF_ID_SCHEMA,
  CHAMPION_ID_SCHEMA,
  HP_SCHEMA,
  MAP_ID_SCHEMA,
  POINTS_ID_SCHEMA,
  RESOURCE_ID_SCHEMA,
  SEAT_INDEX_SCHEMA,
  TEAM_INDEX_SCHEMA,
  UNITS_SCHEMA,
  UNITS_PER_TICK_SCHEMA,
  WEIGHT_SCHEMA,
  toUnits,
} from "./branded-schemas.js";
import { MAX_ABILITY_SLOT } from "./limits.js";
import type { AbilityId, AbilitySlot, AuraDefId } from "./brands.js";

/**
 * #163: a content def's human-readable `slug`. A REQUIRED field on every minted
 * content def (`AURA_DEF_SCHEMA`/`CHAMPION_SCHEMA`/`MAP_SCHEMA`) — a real
 * authoring decision (like `schools`), so it has NO `.default`. It is
 * rename-safe (the opaque `id` is the stable identity; the slug can change) and
 * soft-unique per set — that uniqueness is enforced in the `content:new` tool +
 * CI over `data/**`, NOT the boot loader (the loader only ever hard-fails, and
 * has no non-fatal warning channel; §7.3). The loader still validates the
 * slug's SHAPE (non-empty, ≤ 64 chars) via this schema. Slug-on-the-wire is
 * deferred (§7.5), so the sim/wire never read it — it is a pure authoring/tooling
 * + client-display concern.
 */
export const SLUG_SCHEMA = z.string().min(1).max(64);

/**
 * #203: content-migration schema-version stamp. An INTEGER version axis tracked
 * PER content kind (abilities / auras / champions / maps each carry their own
 * HEAD, derived from `@pit/content`'s ordered migration chain). OPTIONAL on
 * disk/wire: ABSENT ⇒ the pre-migration baseline `v1`; every shipped file is
 * stamped to head by `pnpm content:migrate`, and the content loader runs a
 * migrate-to-head pass (`migrateToHead`) BEFORE this schema validates, so at
 * parse time a file is always at head. It is pure MIGRATION PROVENANCE: retained
 * through parse (so a round-trip is lossless) but deliberately EXCLUDED from the
 * content hash (`@pit/content` `hash.ts`) — stamping a version must never move a
 * golden — and NEVER read by the sim. Bounded like the other integer content
 * fields so a garbage value fails loud at the boundary.
 */
export const SCHEMA_VERSION_SCHEMA = z.number().int().gte(1).lte(1 << 20).optional();

// ─── Aura tags & selectors (#30) ───────────────────────────────────────────
// Tags are free-form content strings (e.g. "movement", "spell", "magic",
// "curse", "dot"). Rule semantics (immunity / suppression / dispel-class /
// gating / DR) key off tag SELECTORS so new mechanics compose from data
// without touching sim code. The registry itself is content, not code.

export const AURA_TAG_SCHEMA = z.string().min(1).max(32);
export type AuraTag = z.infer<typeof AURA_TAG_SCHEMA>;

/** Match ANY of the tags, or ALL of them. */
export const TAG_SELECTOR_SCHEMA = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("any"), tags: z.array(AURA_TAG_SCHEMA).min(1) }),
  z.strictObject({ kind: z.literal("all"), tags: z.array(AURA_TAG_SCHEMA).min(1) }),
]);
export type TagSelector = z.infer<typeof TAG_SELECTOR_SCHEMA>;

/**
 * Team selector RELATIVE TO A REFERENCE ENTITY (the caster for an ability's
 * `Area`, the wall owner for a wall hazard, #146 item 3, and — #327 — the
 * dashing caster for a `dash` effect's swept-body selector): `"enemies"`
 * spares allies, `"allies"` spares enemies, `"all"` hits both. Declared here
 * (ABOVE the Effects section) rather than down by `AREA_CIRCLE_SCHEMA` /
 * `AREA_CONE_SCHEMA` (its original home, #51/#128) so the `dash` effect below
 * — itself one of the `SIMPLE_EFFECT_SCHEMA` options — can reference it
 * directly instead of a `z.lazy` forward-reference or a duplicated literal
 * union. One vocabulary, one schema, referenced from every distance.
 */
export const AREA_TARGETS_SCHEMA = z.union([
  z.literal("enemies"),
  z.literal("allies"),
  z.literal("all"),
]);
export type AreaTargets = z.infer<typeof AREA_TARGETS_SCHEMA>;

// ─── Effects ──────────────────────────────────────────────────────────────
// Discriminated union on `kind`. MVP effect is `damage`; heal/stun/interrupt
// are declared so downstream code (schemas, sim router) doesn't need editing
// when content enables them — content-gated in WP2. (#142 D2: the `slow` effect
// was REMOVED — a slow is now an ordinary aura def, not a bespoke effect kind.)

/**
 * Conditional damage multiplier keyed to a held-aura TAG on the target (#83).
 * When the target holds any aura whose def carries `tag`, the base `amount`
 * is scaled by `mulBp` basis points: 20000 = ×2, 40000 = ×4. Integer-only
 * (`idiv(amount * mulBp, 10000)`), read BEFORE the same cast's other effects
 * mutate the target's auras (so a hit can scale off `frozen` AND dispel it in
 * the same effect list). Omit ⇒ no scaling (plain damage, hash-identical).
 */
export const DAMAGE_SCALE_IF_TARGET_TAG_SCHEMA = z.strictObject({
  tag: AURA_TAG_SCHEMA,
  /** Multiplier in basis points; 10000 = ×1. `>= 1` (a 0× would be a heal-shaped footgun). */
  mulBp: z.number().int().gte(1).lte(1 << 20),
});
export type DamageScaleIfTargetTag = z.infer<typeof DAMAGE_SCALE_IF_TARGET_TAG_SCHEMA>;

/**
 * #328: CLEAVE secondary-target selector. Redirects a `damage` effect away
 * from the ability's normal resolved target (the seat the cast pipeline
 * already picked — single-target `targetType: "enemy"` resolution, or the
 * post-reflect seat) to a DIFFERENT body, resolved fresh at apply time:
 *
 *   - `nearestOtherEnemyInRange` — the CLOSEST living enemy to the CASTER
 *     (straight-line distance from the caster's position, NOT the primary
 *     target's — "secondary target in melee" is read as melee-range-FROM-
 *     THE-CASTER, matching the `weapon.range` convention every other melee
 *     primitive already uses), EXCLUDING the effect's own primary target
 *     seat, within `rangeUnits` (inclusive). Ties (equidistant candidates)
 *     break by ascending seat order — the SAME first-encountered-wins
 *     discipline `projectile.ts`'s closest-entry-t search uses.
 *
 * A discriminated union of ONE member today so a future sibling selector
 * (e.g. an ally-scoped cleave-heal) appends without reshaping this field.
 *
 * NO ELIGIBLE BODY (no living enemy in range, or the only candidate in range
 * IS the primary target) ⇒ the damage effect is a clean NO-OP — no hp change,
 * no event, no error (Cleave with nothing else nearby just lands its primary
 * hit, per spec). Omit the field entirely ⇒ pre-#328 behavior (apply to the
 * seat the caller passed), byte-identical.
 */
export const DAMAGE_TARGET_SELECTOR_SCHEMA = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("nearestOtherEnemyInRange"),
    /** Search radius from the CASTER, in world units. Must be > 0 — a
     *  zero-range selector could never find anything (a silent-no-op
     *  footgun, rejected like a zero-radius area). */
    rangeUnits: UNITS_SCHEMA.refine((r) => r > 0, {
      message: "damage targetSelector rangeUnits must be > 0",
    }),
  }),
]);
export type DamageTargetSelector = z.infer<typeof DAMAGE_TARGET_SELECTOR_SCHEMA>;

/**
 * #329: which SELF pool a `damage.perPointBonus` (or a future sibling scaler)
 * reads. `"points"` is the ORIGINAL #90 meaning (the builder/spender combo-
 * point pool) and stays the DEFAULT when `perPointBonusPool` is omitted, so
 * every pre-#329 `perPointBonus` author is byte-identical. `"resource"` reads
 * the PRIMARY mana/rage/energy pool instead (`Entity.resource.current`) — the
 * Execute-style "hits harder the more rage/energy you're banking" hook.
 * Generalizes the ONE existing scale-by-X mechanism to be POOL-PARAMETERIZED
 * rather than adding a parallel `resourceScaledBonus`-shaped field (#329
 * locked decision) — one shared, reusable scaling path for any future
 * resource-scaled ability.
 */
export const SCALE_BY_POOL_SCHEMA = z.union([z.literal("points"), z.literal("resource")]);
export type ScaleByPool = z.infer<typeof SCALE_BY_POOL_SCHEMA>;

/**
 * #447: a per-combo-point LOOKUP TABLE — the non-linear sibling of the
 * `perPointBonus`/`durationPerPoint` LINEAR scalers (`base + perPoint × points`).
 * A linear scaler gives a CONSTANT marginal value per point, which flattens the
 * builder/spender incentive to hold points to the pool max before spending; a
 * table lets each point threshold carry its own authored value so late points
 * can be worth meaningfully more than early ones.
 *
 * Lookup is `table[comboPoints - 1]` — 0-indexed table, 1-indexed points (a
 * finisher is never meaningfully cast at 0 points; the sim clamps the read to
 * `>= 1` defensively, mirroring how `perPointBonus`/`durationPerPoint` already
 * degrade to their unscaled base at 0 points rather than erroring). crossValidate
 * requires `table.length === <the casting champion's declared points.max>`
 * EXACTLY (no silent truncation/padding) and requires the champion to declare a
 * `points` pool, the same coupling `perPointBonus`/`durationPerPoint` already
 * have. Bounded `<= 1<<16` per entry (matches `perPointBonus`'s own ceiling);
 * length bounded to a generous `64` (every shipped points pool is `<= 5`).
 */
export const PER_POINT_TABLE_SCHEMA = z.array(z.number().int().gte(0).lte(1 << 16)).min(1).max(64);
export type PerPointTable = z.infer<typeof PER_POINT_TABLE_SCHEMA>;

export const EFFECT_DAMAGE_SCHEMA = z.strictObject({
  kind: z.literal("damage"),
  amount: HP_SCHEMA,
  /** Optional target-state damage scaling (#83). */
  scaleIfTargetTag: DAMAGE_SCALE_IF_TARGET_TAG_SCHEMA.optional(),
  /**
   * Scale-by-X bonus (#90, pool-parameterized #329). When set, the hit's
   * magnitude is `amount + perPointBonus * <the SOURCE's current balance in
   * perPointBonusPool>` — the finisher hook that lets a combo-point spender
   * (or a resource-banking Execute) hit harder the more it's holding.
   * Integer-only (`amount + perPointBonus * balance`), read from the SOURCE's
   * pool at damage time BEFORE a later `spendPoints` in the same effect list
   * zeroes it (the #83 read-then-mutate discipline, but keyed to a SELF pool
   * balance rather than a TARGET aura tag). Omit ⇒ no scaling (plain damage,
   * hash-identical). crossValidate requires the champion to declare the
   * SELECTED pool (`perPointBonusPool`, default `"points"`) when this is set.
   * Bounded `<= 1<<16` so the boosted magnitude (`amount + perPointBonus *
   * balance`, then any #83 mulBp) stays a safe integer even at either pool's
   * cap.
   */
  perPointBonus: z.number().int().gte(0).lte(1 << 16).optional(),
  /** Optional secondary-target redirect (#328, Cleave). Omit ⇒ applies to the
   *  seat the caller resolved (pre-#328 behavior). See
   *  `DAMAGE_TARGET_SELECTOR_SCHEMA` for the resolution rule. */
  targetSelector: DAMAGE_TARGET_SELECTOR_SCHEMA.optional(),
  /**
   * #329: which self pool `perPointBonus` reads — see `SCALE_BY_POOL_SCHEMA`.
   * Omit ⇒ `"points"` (the original #90 behavior, byte-identical for every
   * pre-#329 author). Only meaningful alongside `perPointBonus`; crossValidate
   * rejects it when `perPointBonus` is absent (an inert config).
   */
  perPointBonusPool: SCALE_BY_POOL_SCHEMA.optional(),
  /**
   * #447: TABLE-based scale-by-points bonus — the non-linear sibling of
   * `perPointBonus`. When set, the hit's magnitude is `amount +
   * perPointTable[<the SOURCE's current "points" balance> - 1]` (read at the
   * same read-then-mutate moment `perPointBonus` reads — BEFORE a later
   * `spendPoints` in the same effect list zeroes the pool). Always reads the
   * `points` pool (no pool selector — a table over the primary resource pool is
   * not an authored use case at v1). Mutually exclusive with `perPointBonus` on
   * the same effect (crossValidate rejects both set — one scaling mode per
   * ability). crossValidate also requires the champion to declare a `points`
   * pool and the table's length to exactly equal `points.max`. Omit ⇒ no
   * scaling (plain damage, hash-identical to pre-#447 content).
   */
  perPointTable: PER_POINT_TABLE_SCHEMA.optional(),
});

export const EFFECT_HEAL_SCHEMA = z.strictObject({
  kind: z.literal("heal"),
  amount: HP_SCHEMA,
});

// #142 (D2): the standalone `slow` EFFECT was REMOVED. A slow is now authored as
// an ordinary aura def carrying `ccClasses: ["slow"]` + a `statMod { moveSpeed,
// addBp, -bp }` primitive, applied via `applyAura` — flowing through the SAME
// def/aura/hash path as every other aura (one code path, one hasher), so a
// trinket/cleanse can select it by CC class. The old def-less transient
// `__pit.slow` aura (and its per-instance `statMod`) is gone.

export const EFFECT_STUN_SCHEMA = z.strictObject({
  kind: z.literal("stun"),
  durationTicks: z.number().int().gte(0).lte(1 << 20),
});

/**
 * #107: the well-known content key of the engine's stun aura def. The `stun`
 * EFFECT (a time-limited move+cast+attack disable) carries no def reference; it
 * attaches THIS content-authored aura — a bundle of `controlFlag` primitives —
 * programmatically, overriding the def's default lifetime with the effect's
 * `durationTicks`. Shared between the sim (which resolves the def from the
 * bundle at apply time) and the content cross-validator (which requires the def
 * whenever an ability uses `stun`), so the engine and the loader can never
 * drift on the id. The def carries `ccClasses: ["stun"]` so a future
 * dispel/immunity can select it by CC class — removal itself is NOT built here
 * (#107 addendum).
 *
 * #163: this is an ENGINE-OWNED id, so its minted `aur_<nanoid>` VALUE lives here
 * as a protocol constant (not just in `stun.json`), in lockstep with
 * `data/auras/stun.json`'s `id` + slug `"stun"`. `isTargetableBy`/the stun effect
 * compare opaque strings, so the migration only re-mints the value.
 *
 * #448: `stun.json`'s `durationTicks` is authored `"scaledOnly"` (see
 * `AURA_DURATION_SCHEMA`) — this def's lifetime ALWAYS comes from the `stun`
 * effect's own `durationTicks` (the shallow-copy override above), so it never
 * carried a meaningful authored number in the first place; `"scaledOnly"`
 * says so honestly instead of a placeholder numeric floor.
 */
export const STUN_AURA_DEF_ID: AuraDefId = "aur_J8eoAOc8sD" as AuraDefId;

export const EFFECT_INTERRUPT_SCHEMA = z.strictObject({
  kind: z.literal("interrupt"),
  /** Lockout duration in ticks. 0 ⇒ pure cancel with no lockout. */
  lockoutTicks: z.number().int().gte(0).lte(1 << 20),
});

/**
 * Attach an aura (referenced by `AuraDef` id) to the effect's victim. The
 * aura system (#30) resolves the def, builds an instance honouring its
 * stacking rule, and attaches it via the same effect pipeline as damage — so
 * an aura applied by instant cast, projectile impact, or an interceptor eating
 * a projectile all share one code path.
 */
export const EFFECT_APPLY_AURA_SCHEMA = z.strictObject({
  kind: z.literal("applyAura"),
  /** Content key into `ContentBundle.auras`. Branded `AuraDefId`. */
  def: AURA_DEF_ID_SCHEMA,
  /**
   * Scale-by-points DURATION bonus (#90). When set, the attached aura's
   * lifetime is `def.durationTicks + durationPerPoint * <the SOURCE's current
   * point balance>` — the duration-scaling finisher variant (a longer debuff
   * with more points). Read from the SOURCE's `Entity.points.current` at apply
   * time (same read-then-mutate discipline as `damage.perPointBonus`). Omit ⇒
   * the aura uses its authored duration. crossValidate requires the champion to
   * declare a `points` pool AND the referenced aura def to carry a NUMERIC
   * (non-`permanent`) duration when this is set. Bounded `<= 1<<16` so the
   * scaled `expiresAtTick` (`appliedAtTick + durationTicks + durationPerPoint *
   * points`) stays well under the hasher's 2^31 encoding bound at the point-cap
   * (`MAX_TICK` is 1<<24).
   */
  durationPerPoint: z.number().int().gte(0).lte(1 << 16).optional(),
  /**
   * #182: override the attached instance's initial absorb-pool balance
   * (`absorbRemaining`) with this exact value, instead of summing the def's
   * `absorbPool` primitive amounts. A generic "apply this aura, sized to a
   * runtime magnitude" knob: a shield of a computed size, or — the first
   * consumer — a Chaos Void collapse bolt seeding a `reservoir`-mode
   * periodic-damage DoT with the well's ACCUMULATED total (the bolt's DoT
   * magnitude = the absorbed pool). Omit ⇒ seed from the def (byte-identical to
   * pre-#182 content). Bounded `<= 1<<24` (the absorbPool ceiling). crossValidate
   * requires the referenced def to carry an `absorbPool` primitive when set (a
   * seed with no pool to seed is a footgun).
   */
  absorbSeed: z.number().int().gte(0).lte(1 << 24).optional(),
  /**
   * #447: TABLE-based scale-by-points DURATION bonus — the non-linear sibling
   * of `durationPerPoint`. When set, the attached aura's lifetime is
   * `<numeric base> + durationPerPointTable[<the SOURCE's current "points"
   * balance> - 1]`, where `<numeric base>` is `def.durationTicks` when it's a
   * plain number, or `0` when the def is `"scaledOnly"` (#448) — the table
   * alone supplies the ENTIRE duration in that case, since a `scaledOnly` def
   * carries no authored base to add on top of. This is what lets the generic
   * `applyAura` effect legally reference a `scaledOnly` def (normally rejected
   * outright, see the `checkEffectShape` rule): the table GUARANTEES a fully
   * concrete resultant duration, the same guarantee the `stun` effect's own
   * per-cast override provides. Mutually exclusive with `durationPerPoint` on
   * the same effect. crossValidate requires the champion to declare a `points`
   * pool, the referenced aura to have a NUMERIC or `"scaledOnly"` duration
   * (never `"permanent"`), and the table's length to exactly equal
   * `points.max`. Bounded `<= 1<<16` per entry (mirrors `durationPerPoint`'s
   * ceiling).
   */
  durationPerPointTable: PER_POINT_TABLE_SCHEMA.optional(),
  /**
   * #447: TABLE-based scale-by-points RESERVOIR SEED — the non-linear sibling
   * of `absorbSeed`. When set, the attached instance's initial
   * `absorbRemaining` is `absorbSeedPerPointTable[<the SOURCE's current
   * "points" balance> - 1]` instead of the def's authored `absorbPool` amount
   * — the hook a combo-point finisher DoT needs to fund an EXACT total-damage
   * budget per point spent (Rupture: `reservoirPerTick`'s existing
   * remaining/firings-left division already drains ANY seeded total to
   * exactly 0 by the aura's last firing, so a non-linear per-CP total table
   * composes for free with zero new tick-loop logic). Mutually exclusive with
   * `absorbSeed` on the same effect. crossValidate requires the champion to
   * declare a `points` pool, the referenced def to carry a `reservoir`-mode
   * `absorbPool` primitive, and the table's length to exactly equal
   * `points.max`. Bounded `<= 1<<24` (mirrors `absorbSeed`'s ceiling).
   */
  absorbSeedPerPointTable: z.array(z.number().int().gte(0).lte(1 << 24)).min(1).max(64).optional(),
});

/**
 * Build points (#90) — add `amount` to the SOURCE's point pool, clamped to the
 * champion's `points.max`. A no-op on a champion with no pool (crossValidate
 * requires the pool). Operates on the CASTER (points are tracked on the entity,
 * target-agnostic), not the effect's target. The builder half of the
 * builder/spender framework (combo points / holy power / runes).
 */
export const EFFECT_BUILD_POINTS_SCHEMA = z.strictObject({
  kind: z.literal("buildPoints"),
  /** Points added per application; `>= 1`. */
  amount: z.number().int().gte(1).lte(1 << 12),
});

/**
 * Spend points (#90) — consume ALL of the SOURCE's current points (set the
 * pool to 0). The spender half of the builder/spender framework. Pairs with a
 * `damage.perPointBonus` / `applyAura.durationPerPoint` sibling effect authored
 * EARLIER in the same effect list, which reads the point count BEFORE this
 * effect zeroes it (the #83 read-then-mutate ordering — pinned in tests).
 * Operates on the CASTER, not the effect's target.
 */
export const EFFECT_SPEND_POINTS_SCHEMA = z.strictObject({
  kind: z.literal("spendPoints"),
});

/**
 * Gain resource (#324) — add `amount` to the SOURCE's PRIMARY resource pool
 * (mana/rage/energy), clamped to the champion's `resource.max`. A no-op on a
 * resource-less champion (crossValidate requires the pool). Operates on the
 * CASTER (the resource pool is caster-owned, target-agnostic), exactly
 * mirroring `buildPoints` but for `Entity.resource` instead of `Entity.points`.
 *
 * The general "gain resource on event" primitive (#324): rather than a NEW
 * aura primitive, this composes with the EXISTING `trigger` primitive (#55) —
 * `trigger.effect` already accepts any `Effect`, so a permanent self-aura
 * carrying `{ kind: "trigger", on: "onHitTaken", everyN: 1, effect: {
 * kind: "gainResource", amount } }` grants resource every time its holder
 * takes damage (a passive rage-from-damage-taken hook) with ZERO new sim
 * plumbing. The SAME effect is also the payload a weapon's on-hit `effects`
 * (#325) or an ability's own `effects` fire — authoring it inside an
 * AREA-fanned ability's effects list already yields a PER-EVENT VARIABLE
 * total (N enemies struck ⇒ N applications) with no extra mechanism, and a
 * `trigger`'s `everyN` cadence composes for a per-N-events gain — so a fixed
 * per-application `amount` is never a "just a constant" limitation.
 *
 * #548: the authored `amount` is SCALED at apply time by the SOURCE's
 * `resourceGainBp` stat (a `statMod`-only, base-less bp key — see
 * `STAT_KEY_SCHEMA`) before the max-clamp, e.g. a Stealth aura carrying
 * `{ kind: "statMod", stat: "resourceGainBp", op: "add", value: 10000 }`
 * (+100%) doubles whatever `gainResource` amount fires while active — no
 * changes needed to the firing content itself (trigger/periodic/ability
 * effects all compose automatically). See `packages/sim/src/effects.ts`'s
 * `gainResource` case.
 */
export const EFFECT_GAIN_RESOURCE_SCHEMA = z.strictObject({
  kind: z.literal("gainResource"),
  /** Resource gained per application; `>= 1`. */
  amount: z.number().int().gte(1).lte(1 << 16),
});
export type EffectGainResource = z.infer<typeof EFFECT_GAIN_RESOURCE_SCHEMA>;

/**
 * Spend resource (#382) — consume ALL of the SOURCE's current PRIMARY
 * resource pool (set `resource.current` to 0). Operates on the CASTER (the
 * resource pool is caster-owned, target-agnostic). The `resource`-pool
 * analogue of `spendPoints` above — exactly mirroring how `gainResource`
 * (#324) is `buildPoints`'s analogue for `Entity.resource` instead of
 * `Entity.points`. A no-op on a resource-less source or an already-empty pool
 * (crossValidate requires the pool, mirroring `spendPoints`/`gainResource`).
 *
 * Pairs with a `damage.perPointBonus` (`perPointBonusPool: "resource"`, #329)
 * authored EARLIER in the same effect list, which reads the balance BEFORE
 * this effect zeroes it — the SAME #83 read-then-mutate discipline
 * `spendPoints` already pins for the `points` pool (pinned in tests). First
 * consumer: Warrior's Execute ("consumes ALL current rage; damage scales with
 * rage consumed").
 */
export const EFFECT_SPEND_RESOURCE_SCHEMA = z.strictObject({
  kind: z.literal("spendResource"),
});
export type EffectSpendResource = z.infer<typeof EFFECT_SPEND_RESOURCE_SCHEMA>;

/**
 * Remove up to `count` auras matching a tag selector (#135). WIRED in
 * `applyEffect`: walks the target's auras, removes up to `count` whose def
 * `tags` satisfy the `selector` (`any` = intersects; `all` = superset),
 * NEWEST-applied first (highest `appliedAtTick`, tie-broken by canonical hash
 * order). The ability's `targetType` + the selector's tags express polarity
 * (a friendly-target cleanse selects debuff tags; an enemy-target purge selects
 * buff tags) — there is NO engine polarity enum (#135 Q2).
 *
 * `ignoreUndispellable` (#135 Q4) — a per-ability OVERRIDE. Default (omit/false)
 * ⇒ the dispel SKIPS any aura flagged `AURA_DEF.undispellable` (hard defensives
 * like Ice Block / bubble), even on an exact selector match. `true` ⇒ this
 * specific dispel strips normally-undispellable auras too (Mass Dispel /
 * Shattering Throw class). Two static levers: the aura def says "I am
 * (un)dispellable"; the dispel effect says "I (do/don't) respect that flag."
 */
export const EFFECT_DISPEL_SCHEMA = z.strictObject({
  kind: z.literal("dispel"),
  selector: TAG_SELECTOR_SCHEMA,
  count: z.number().int().gte(1).lte(1 << 8),
  /** #135 Q4: strip even `undispellable` auras (Mass-Dispel-class override).
   *  Omit/false ⇒ respect the flag (the normal case). */
  ignoreUndispellable: z.boolean().optional(),
});

/**
 * Blink / teleport the CASTER toward the cast aim point (#85), capped at
 * `maxDistanceUnits` and landing on a legal position. Resolves in the cast
 * Phase-B relocation step (where the aim point is available) and on the #86
 * off-cast inline path, not in the generic per-target effect router.
 * Integer-only landing.
 *
 * #733: the landing is STOP-AT-FIRST-OBSTACLE, not "clamp to the arena
 * rectangle" — the caster marches toward the aim point and stops at the last
 * position `legalVsStatic` accepts. A pillar, an active wall (incl. placed
 * terrain like Ice Wall) or the arena edge (radius-aware, so the body never
 * overhangs) ends the blink there; other PLAYERS never do — a blink always
 * passes through allies and enemies alike. `phasing` (#199) is honored, so a
 * caster phasing pillars/walls ignores that geometry. No authored knobs: this
 * is uniform for every ability carrying a `blink` effect.
 */
export const EFFECT_BLINK_SCHEMA = z.strictObject({
  kind: z.literal("blink"),
  maxDistanceUnits: UNITS_SCHEMA,
});

/**
 * Displacement direction (#119, Foundation B1). A closed enum — the ONE
 * displacement primitive covers knockback / Death Grip / Leap of Faith /
 * charge with a single flag:
 *   - `awayFromSource` — push the target directly away from the source
 *     (caster). Knockback.
 *   - `towardSource`   — pull the target toward the source. Death Grip on an
 *     enemy target; Leap of Faith on an ally target (same effect, differs only
 *     by the ability's `targetType` + animation).
 *   - `towardPoint`    — move the target toward the authored `aim` point
 *     (self-charge / leap; overlaps `blink` — kept distinct, see #119).
 *   - `behindTarget`   — (#134 Shadowstep) SELF-relocation: move the CASTER to a
 *     point directly behind the effect's TARGET (relative to the target's
 *     facing), landing flush against the target's back (bodies tangent, not
 *     overlapping) by default. Moves the caster (source) rather than the
 *     target; the landing is derived from the two body radii + the target's
 *     facing, so `distance` is IGNORED (must be ABSENT — crossValidate). #512:
 *     the radius-derived flush distance may be SCALED via the optional
 *     `flushDistanceScaleBp` (see `EFFECT_DISPLACE_SCHEMA`) — landing farther
 *     than flush contact, never a different `distance` field/unit.
 *   - `toTarget`       — (#206 Seek Cover) SELF-relocation: move the CASTER to a
 *     point adjacent to the effect's TARGET on the caster's OWN APPROACH SIDE
 *     (the near side, along target→caster), landing flush against the target
 *     (bodies tangent, not overlapping) by default. NOT behind the target
 *     (that is `behindTarget`) — it lands on whichever side the caster is
 *     coming from. Like `behindTarget` it moves the caster; the landing is
 *     radius-derived (`targetRadius + casterRadius`), so `distance` is
 *     IGNORED (must be ABSENT — same crossValidate rule as `behindTarget`).
 *     Target-team-agnostic: authored with `targetType: ally` (Seek Cover) but
 *     the direction itself works for any target team. #512: also scalable via
 *     `flushDistanceScaleBp`, same as `behindTarget`.
 */
export const DISPLACE_DIRECTION_SCHEMA = z.union([
  z.literal("awayFromSource"),
  z.literal("towardSource"),
  z.literal("towardPoint"),
  z.literal("behindTarget"),
  z.literal("toTarget"),
]);
export type DisplaceDirection = z.infer<typeof DISPLACE_DIRECTION_SCHEMA>;

/**
 * Displace / forced-movement effect (#119, Foundation B1). Applies a single
 * collision-resolved position change to the effect's TARGET this tick: the
 * target steps up to `distance` world units along a direction computed from
 * the RESOLVE-time source↔target positions (or toward `aim`), stopping at the
 * first obstacle (pillar / entity / arena edge) and cancelling its active cast
 * iff it actually moves. NOT an aura — one instantaneous relocation. See
 * @pit/sim `applyEffect` (`displace` arm) + `resolveDisplace`.
 *
 * `aim` is REQUIRED iff `direction === "towardPoint"` and must be ABSENT
 * otherwise (enforced in crossValidate — a discriminatedUnion member cannot
 * carry a `.refine`). `distance` is a world-units integer, OPTIONAL at the
 * schema level: crossValidate REQUIRES it (`>= 1`) for the three target-moving
 * directions (a 0-distance displace is a no-op footgun) and REQUIRES it ABSENT
 * for the SELF-relocation directions `behindTarget` (#134) and `toTarget`
 * (#206) — their landing is radius-derived, so a stray distance is rejected,
 * not silently ignored.
 *
 * #512: `flushDistanceScaleBp` is the mirror-image knob for the SAME two
 * self-relocation directions — a basis-points multiplier on the radius-
 * derived flush distance (`10000` = ×1, today's flush-contact landing;
 * `50000` = ×5, landing 5x farther out), mirroring this schema's own
 * `globalSpeedScaleBp`/`armorMaxReductionBp`/`strafeSpeedScaleBp` basis-points
 * convention. It is a DISTINCT field from `distance` (different units/
 * semantics — a radius-relative scale, not a flat world-units distance) and
 * crossValidate REQUIRES it ABSENT for the three target-moving directions
 * (same rule as `distance`, inverted). OPTIONAL for `behindTarget`/`toTarget`;
 * absent ⇒ ×1 (today's behavior, byte-identical for every currently-shipped
 * ability). The scale is applied via floored integer division
 * (`idiv((targetRadius + moverRadius) * flushDistanceScaleBp, 10000)`) — the
 * SAME collision-resolved `resolveFlushLanding` walk, just walked farther;
 * no new collision/legality logic.
 */
export const EFFECT_DISPLACE_SCHEMA = z.strictObject({
  kind: z.literal("displace"),
  direction: DISPLACE_DIRECTION_SCHEMA,
  /** Displacement distance in world units; REQUIRED `>= 1` for
   *  awayFromSource/towardSource/towardPoint, ABSENT for the radius-derived
   *  self-relocations `behindTarget`/`toTarget` (validated in crossValidate). */
  distance: UNITS_SCHEMA.optional(),
  /** #512: basis-points multiplier on the radius-derived flush distance for
   *  `behindTarget`/`toTarget` (`10000` = ×1); OPTIONAL, absent ⇒ ×1
   *  (byte-identical to pre-#512 behavior). MUST be ABSENT for the three
   *  target-moving directions (validated in crossValidate, same rule as
   *  `distance`). `>= 1` (a 0× would collapse the caster onto the target's
   *  center — the same no-op footgun `distance >= 1` guards against on the
   *  other three directions); capped at `100000` (×10), mirroring
   *  `globalSpeedScaleBp`'s bound. */
  flushDistanceScaleBp: z.number().int().gte(1).lte(100000).optional(),
  /** Target point for `towardPoint`; present iff `towardPoint` (crossValidate). */
  aim: z.strictObject({ x: UNITS_SCHEMA, y: UNITS_SCHEMA }).optional(),
});

/**
 * #327: self-propelled PATH movement — the CASTER relocates itself toward the
 * resolved cast aim point (gap-closer / dash / charge), and every OTHER body
 * whose collision circle intersects the SWEPT SEGMENT (the caster's start
 * point → its actual landing point, a capsule sweep, #327 item 2) is knocked
 * clear of the path and takes `hitEffects`. This is the ONE primitive that
 * moves the CASTER while ALSO detecting + affecting bodies along the way —
 * distinct from `blink` (caster relocation, no sweep-hit detection) and
 * `displace` (moves the TARGET, never the caster, except for the two radius-
 * derived self-relocation directions which carry no path-sweep either).
 * Resolves at the cast machine's Phase-B relocation step (like `blink` /
 * `spawnWall` — the resolved aim point + a caster position are needed), and
 * ALSO inline on the #86 off-cast path (mirroring `blink`'s dual resolution
 * site) — see @pit/sim `cast.ts` `resolveDash`. The generic per-target
 * `applyEffect` router treats it as a deliberate no-op (never reached via the
 * normal effect loop — same discipline as `blink`/`spawnWall`).
 *
 * DECISIONS LOCKED (flagged explicitly per the #327 design-question list,
 * rather than silently picked):
 *   1. INSTANT, single-tick resolution — NOT a multi-tick, interruptible
 *      traversal. The whole sweep (relocate + hit-detect + knock + affect) is
 *      one resolve-time computation, exactly like `blink`'s teleport. A body
 *      cannot react/reposition mid-dash at v1 (it can only react to the
 *      knockback + `hitEffects` AFTER the fact). A multi-tick, CC-able charge
 *      (self-propelled `forcedMovement`-style per-tick travel) is a real,
 *      deliberate FOLLOW-UP, out of scope here — it would need new per-tick
 *      hashed "currently charging" state this issue does not introduce.
 *   2. The caster's own march STOPS at the first pillar / active wall / arena
 *      edge (mirrors `displace`'s stop-at-first-obstacle discipline) but
 *      passes THROUGH living entities — a charge bulldozes multiple bodies in
 *      one sweep, it does not stop at the first one. `maxDistanceUnits` is
 *      therefore a CAP, not a guarantee: a dash into a pillar three units in
 *      travels three units, exactly like a displace stopped short.
 *   3. "Knocked out of the way": each swept body is pushed `knockbackDistanceUnits`
 *      world units PERPENDICULAR to the caster's actual travel line, on
 *      whichever side the body's center currently sits (integer cross-product
 *      sign test) — a stable, deterministic, side-preserving push (never
 *      arbitrary/toward-nearest-open-space, which would need a second spatial
 *      search). A body sitting exactly ON the line (degenerate, cross === 0)
 *      falls back to a fixed canonical side (rotate the travel direction +90°),
 *      the same "deterministic fallback axis" discipline `separatePair` uses
 *      for perfectly stacked centres. Reuses `resolveDisplace`'s march-and-stop
 *      collision resolution verbatim (pillars/walls/arena/OTHER entities all
 *      block the shove, exactly like any other displacement) — see
 *      `resolveDashKnockback`. Omitted/0 ⇒ no knockback (a pure "hit + affect,
 *      don't move them" dash is legal).
 *   4. Per-hit resource generation (Charge's "10 rage per enemy hit") is
 *      DELIBERATELY NOT built here — #324 (on-event resource generation) has
 *      not landed yet, and there is no `Effect` kind that credits the caster's
 *      `resource` pool. `hitEffects` is the generic hook a future rage-gen
 *      effect slots into with NO further sim change once #324 ships (content-
 *      only follow-up, per the #327 issue's own "Not in scope" note).
 *
 * `targets` selects which teams (relative to the caster) are swept — Charge
 * hits only enemies, but the primitive is team-agnostic (an ally-supportive
 * "leap to defend" dash is representable with `targets: "allies"`).
 *
 * `hitEffects` fires once per swept body (source = the dashing caster, target
 * = the swept body), via the SAME `applyEffect` router every other delivery
 * path uses — so death cleanup / cosmetic + causal events fall out per hit,
 * for free. It is `DASH_HIT_EFFECT_SCHEMA` — every ALREADY-DEFINED, non-
 * recursive effect kind (`damage`/`heal`/`stun`/`interrupt`/`applyAura`/
 * `dispel`/`displace`/`buildPoints`/`spendPoints`/`gainResource`), deliberately
 * EXCLUDING `blink`/`spawnWall` (Phase-B-only — no caster+aim plumbing of
 * their own inside a per-body hit callback, same exclusion `checkEffectShape`
 * already applies to a wall hazard's `effects`), `dash` itself (no nested
 * dashes), and `teamConditional` (declared even later in the file than
 * `dash`, so including it here would reintroduce the exact forward-reference
 * cycle this narrower union exists to avoid — a real, narrow v1 scope limit,
 * not an oversight). Empty ⇒ a pure movement + knockback dash with no on-hit
 * payload (legal — mirrors a 0-amount heal's harmless no-op).
 *
 * #324/#379: `gainResource` was ADDED to this union once #324 shipped —
 * exactly the "slots in here with NO further sim change" follow-up this
 * comment originally flagged. `gainResource` mutates the SOURCE's (the
 * dashing caster's) own resource pool regardless of which body it's routed
 * against (see its `applyEffect` arm), so authoring it once in `hitEffects`
 * credits the caster once PER SWEPT BODY — Charge's "10 rage per enemy hit"
 * is `{ kind: "gainResource", amount: 10 }` in this list, zero bespoke sim
 * code. Pure protocol schema widening (no new hashed state, no new `Effect`
 * kind, `applyEffect`'s `gainResource` arm and `mixEffect`'s `gainResource`
 * ordinal already exist) — content-only follow-up, no `SIM_VERSION` bump.
 */
export const DASH_HIT_EFFECT_SCHEMA = z.discriminatedUnion("kind", [
  EFFECT_DAMAGE_SCHEMA,
  EFFECT_HEAL_SCHEMA,
  EFFECT_STUN_SCHEMA,
  EFFECT_INTERRUPT_SCHEMA,
  EFFECT_APPLY_AURA_SCHEMA,
  EFFECT_DISPEL_SCHEMA,
  EFFECT_DISPLACE_SCHEMA,
  EFFECT_BUILD_POINTS_SCHEMA,
  EFFECT_SPEND_POINTS_SCHEMA,
  EFFECT_GAIN_RESOURCE_SCHEMA,
]);
export type DashHitEffect = z.infer<typeof DASH_HIT_EFFECT_SCHEMA>;

export const EFFECT_DASH_SCHEMA = z.strictObject({
  kind: z.literal("dash"),
  maxDistanceUnits: UNITS_SCHEMA,
  knockbackDistanceUnits: UNITS_SCHEMA.optional(),
  targets: AREA_TARGETS_SCHEMA,
  hitEffects: z.array(DASH_HIT_EFFECT_SCHEMA).default([]),
});
export type EffectDash = z.infer<typeof EFFECT_DASH_SCHEMA>;

/**
 * #486: `charge` — a REAL multi-tick self-propelled travel primitive, the
 * deliberate SIBLING of `dash` rather than an in-place rework of it. `dash`
 * (above) resolves the ENTIRE march + sweep-hit atomically at cast-resolve
 * (the caster's intermediate positions never exist as real per-tick state —
 * see `EFFECT_DASH_SCHEMA`'s doc comment, decision 1, which flagged this exact
 * follow-up as out of scope). `charge` closes that gap: the caster becomes a
 * genuinely moving body for `ceil(totalDistanceUnits / speedUnitsPerTick)`
 * ticks, occupying a REAL position each tick — so it can be caught by a
 * `GroundZone` firing mid-flight, intercepted by an already-in-flight enemy
 * projectile (`advanceProjectiles`), and disrupted by a cast-blocking CC or a
 * forced-movement drag landing on it mid-charge — none of which a `dash` can
 * ever experience (see @pit/sim `charge.ts` `advanceCharges` + `types.ts`
 * `CastCharging`, the new multi-tick castState mode this effect drives).
 *
 * DECISIONS LOCKED for this v1 primitive (flagged explicitly, not silently
 * picked — see issue #486's "open questions" list):
 *
 *   1. AUTHORING SHAPE (OQ3): `speedUnitsPerTick` (a fixed per-tick travel
 *      rate, `UNITS_PER_TICK_SCHEMA` — the SAME branded type a projectile's
 *      `delivery.speedUnitsPerTick` / a zone-collapse bolt's
 *      `speedUnitsPerTick` already use) + `maxDistanceUnits` (a travel CAP,
 *      mirroring `dash`). The sim derives the inclusive travel-tick count as
 *      `ceil(totalDistanceUnits / speedUnitsPerTick)` at cast-resolve —
 *      composing cleanly with the existing tick-rate/UnitsPerTick conventions
 *      rather than authoring a fixed total DURATION (which would make speed a
 *      derived, non-integer quantity).
 *   2. INDEPENDENT OF MOVESPEED (OQ4): `speedUnitsPerTick` is a fixed OVERRIDE
 *      — the charge's per-tick travel is NOT scaled by the caster's aura-
 *      modified `moveSpeed` stat (so a charge closes distance at its own
 *      authored rate regardless of a slow currently on the caster; the whole
 *      point of a gap-closer). It is likewise NOT gated by a `move`
 *      `controlFlag` (a root) — a charge, once launched, is a self-propelled
 *      override of the caster's own locomotion, the same "not the entity's own
 *      intent" treatment `driveForcedMovement` already gives fear/charm. It
 *      IS gated by a `cast`-class `controlFlag` (stun/silence/mez) and by a
 *      forced-movement drag actually relocating the caster (fear) — either
 *      ends the charge in place (see `advanceCharges`'s CC-break gate +
 *      `forcedMovement.ts`'s drag-break extension) — this is the concrete
 *      "disrupted mid-charge" mechanism the issue's Fear-projectile scenario
 *      needs.
 *   3. TARGETABILITY (OQ2): a charging unit is granted NO untargetability —
 *      it remains a completely ordinary target (for lock-on casts, LoS, and a
 *      skillshot's aim) throughout the charge. Untargetability would make the
 *      issue's own headline scenario ("a Fear projectile lands on the
 *      charging player mid-charge and disrupts it") impossible, so this is a
 *      requirement of the design, not a simplification.
 *   4. BODY-VS-BODY COLLISION (OQ1): a charging caster bulldozes through other
 *      living bodies exactly like `dash` — pillars / active walls / the arena
 *      edge stop it (mirroring `resolveDashSelfMove`'s stop-at-first-obstacle
 *      march, reused verbatim per-tick), other entities never do. Two
 *      simultaneously-charging casters can occupy overlapping space.
 *   5. LANDING: reaching `maxDistanceUnits`, hitting a blocker, or being
 *      disrupted all resolve the SAME way — the charge simply ENDS where the
 *      caster currently stands (mirrors `dash`'s "stop short, entities pass
 *      through" rule; there is no separate "collided with terrain" outcome at
 *      v1 — e.g. no stun-self-on-wall-collision variant).
 *   6. CANCELLATION: a charge is NOT cancelled by an ordinary
 *      `moveDir` re-aim (there is nothing to re-aim — the caster is already
 *      self-propelled toward the frozen aim point and player movement input
 *      is inert while `rooted`), but a player's explicit stop-cast key DOES
 *      end it early (`breakCastOrChannel`, mirroring a channel's OQ2 — no
 *      cooldown refund, the cooldown started at cast-resolve like a channel's
 *      OQ3).
 *
 * `hitEffects` reuses `DASH_HIT_EFFECT_SCHEMA` VERBATIM (not a bespoke
 * narrower union) — the exact same vocabulary `dash`'s on-hit payload already
 * supports (damage/heal/stun/interrupt/applyAura/dispel/displace/
 * buildPoints/spendPoints/gainResource), now evaluated against bodies swept
 * TICK-BY-TICK as the caster actually passes them (`advanceCharges`) instead
 * of a single pre-computed segment. A body is affected AT MOST ONCE per
 * charge (mirrors `dash`'s one-hit-per-swept-body rule) even though the sweep
 * is now spread over multiple ticks — see `CastCharging.hitSeats`.
 *
 * Resolved at the cast machine's Phase-B relocation step ONLY (like `blink`/
 * `dash`) — UNLIKE `dash`, it is REJECTED on an offCast ability (a multi-tick
 * castState transition has no meaning on the "resolves inline, never touches
 * castState" off-cast path) and on a `channel` ability (a channel's per-fire
 * effect application has no Phase-B top-level scan to find it). The generic
 * per-target `applyEffect` router treats it as a deliberate no-op (never
 * reached via the normal effect loop — Phase B strips it into `CastCharging`
 * instead), mirroring `dash`'s own no-op arm.
 *
 * #746 — `stopOnFirstTarget`: an OPT-IN reversal of decision 4 above, for
 * THIS effect only (`dash` is untouched and still always bulldozes). Default
 * `false` ⇒ the sweep-through-everything behaviour decision 4 locked, exactly
 * as before (a true no-op — the flag is read per tick by `advanceCharges` and
 * changes nothing when clear). Set `true` ⇒ the charge is a "collide with the
 * first body you run into" gap-closer:
 *
 *   a. The tick's march still stops ONLY at terrain (pillar / active wall /
 *      arena edge) as far as `resolveDashSelfMove` is concerned; the body stop
 *      is layered ON TOP, so terrain still wins when it comes first.
 *   b. Of every `targets`-matching living body whose collision circle
 *      intersects THIS tick's travelled segment, exactly ONE is picked: the
 *      EARLIEST contact along the segment (swept circle-vs-circle entry-t, the
 *      same `segmentCircleEntryT` ordering projectiles use to decide which
 *      body they hit first), with the LOWER SEAT INDEX breaking an exact tie.
 *      Lowest-seat-wins-outright was explicitly rejected: it would make the
 *      ability position-independent (a charge would "prefer" seat 1 over a
 *      body it physically reaches first).
 *   c. The caster stops at the last legal position BEFORE that body's circle
 *      (centre separation = caster radius + body radius, both read LIVE from
 *      `getStat`-derived radii each tick, never a hardcoded constant) — i.e.
 *      it comes to rest in melee contact, not inside the body.
 *   d. `hitEffects` fires against that ONE body only. This is the whole point
 *      of the flag: `gainResource` credits the CASTER once per swept body (see
 *      `DASH_HIT_EFFECT_SCHEMA`), so a multi-body tick would otherwise grant
 *      N x the authored rage and root N enemies off a single cast.
 *   e. Contact is a new END condition — the charge ends that tick, alongside
 *      the existing blocked / budget-spent / `endsAtTick` conditions.
 *
 * Deliberately a BOOLEAN, not a collision-policy enum: there are exactly two
 * behaviours wanted (sweep-all, stop-at-first), and a richer policy system
 * would be speculative. Note `targets` still selects WHICH bodies count, so
 * `targets: "enemies"` + `stopOnFirstTarget: true` phases straight through
 * allies and stops at the first ENEMY (the shipped Fighter "Charge" shape).
 */
export const EFFECT_CHARGE_SCHEMA = z.strictObject({
  kind: z.literal("charge"),
  speedUnitsPerTick: UNITS_PER_TICK_SCHEMA,
  maxDistanceUnits: UNITS_SCHEMA,
  knockbackDistanceUnits: UNITS_SCHEMA.optional(),
  targets: AREA_TARGETS_SCHEMA,
  hitEffects: z.array(DASH_HIT_EFFECT_SCHEMA).default([]),
  /** #746: stop at (and hit only) the FIRST `targets`-matching body the charge
   *  reaches, instead of bulldozing through every body in the path. Defaulted
   *  (not optional) so `advanceCharges` reads a concrete boolean every tick
   *  with no nullish branch, and so pre-#746 content parses byte-identically
   *  as `false`. See this schema's doc comment, `#746`, for the full rule. */
  stopOnFirstTarget: z.boolean().default(false),
});
export type EffectCharge = z.infer<typeof EFFECT_CHARGE_SCHEMA>;

/**
 * Team-relative blocking selector for a spawned wall (#129, decision D2). A
 * CLOSED enum expressing WHICH teams the wall blocks, relative to its OWNER —
 * this one field turns the ice-wall (blocks everyone) into a one-way /
 * directional wall with zero engine change:
 *   - `all`     — blocks everyone (team-agnostic, like a pillar; the default).
 *   - `enemies` — blocks only enemies of the owner (owner's team passes).
 *   - `allies`  — blocks only allies of the owner.
 * Governs the MOVEMENT + PROJECTILE layers; LoS is symmetric (decision D6) and
 * ignores this. Mirrors the `AREA_TARGETS_SCHEMA` vocabulary.
 */
export const WALL_BLOCKED_TEAMS_SCHEMA = z.union([
  z.literal("all"),
  z.literal("enemies"),
  z.literal("allies"),
]);
export type WallBlockedTeams = z.infer<typeof WALL_BLOCKED_TEAMS_SCHEMA>;

/**
 * #146 item 5: FACING-GATED (directional) projectile blocking for a wall — a
 * real Yasuo Wind-Wall. A wall carries an orientation NORMAL (the caster→aim
 * direction, `perpendicular` to the wall's long axis). A crossing projectile's
 * per-tick velocity `V` is compared to that normal `N` by the SIGN of the
 * integer dot product `V·N`:
 *   - `both`     — block regardless of direction (the #129 default; a solid
 *                  wall stops shots either way).
 *   - `incoming` — block ONLY shots moving AGAINST the normal (`V·N < 0`, i.e.
 *                  crossing front→back, TOWARD the caster) — a Wind-Wall that
 *                  eats enemy shots but lets your own outgoing shots through.
 *   - `outgoing` — block ONLY shots moving ALONG the normal (`V·N > 0`, AWAY
 *                  from the caster) — the mirror.
 * A grazing shot exactly parallel to the wall (`V·N === 0`) is NOT blocked by a
 * directional wall (strict inequality). Governs the PROJECTILE layer only;
 * movement/LoS ignore it. Composes with the team flag (`blockedTeams`).
 */
export const BLOCK_DIRECTION_SCHEMA = z.union([
  z.literal("both"),
  z.literal("incoming"),
  z.literal("outgoing"),
]);
export type BlockDirection = z.infer<typeof BLOCK_DIRECTION_SCHEMA>;

/**
 * #146 item 4: one authored vertex of a POLYLINE wall — an INTEGER offset
 * (signed world units) from the resolved cast aim point (the wall center). The
 * spawn expands the polyline into the SAME row-of-circles sub-obstacle set a
 * straight wall uses (one circle per vertex), so collision / LoS / projectile
 * predicates are byte-identical over more points — ZERO new geometry math.
 * Bounds are `UNITS_SCHEMA` ([-MAX_UNITS, MAX_UNITS]); integer-only (no float).
 */
export const WALL_POLYLINE_POINT_SCHEMA = z.strictObject({
  dx: UNITS_SCHEMA,
  dy: UNITS_SCHEMA,
});
export type WallPolylinePoint = z.infer<typeof WALL_POLYLINE_POINT_SCHEMA>;

/**
 * #146 item 4: the GEOMETRY of a spawned wall. A CLOSED discriminated union so
 * the illegal "polyline without points" state is unrepresentable at the type
 * level (the sim switches over it exhaustively):
 *   - `straight` — the #129 default: a row of `subObstacleCount` circles evenly
 *                  spanning `lengthUnits`, centered on the aim, perpendicular to
 *                  the caster→aim direction. (`lengthUnits`/`subObstacleCount`
 *                  ride on the spawnWall effect itself.)
 *   - `polyline` — an authored ring / curve / box: one circle sub-obstacle at
 *                  each `points` vertex (offset from the aim center). `closed`
 *                  is cosmetic metadata for authors (the ring vs open curve
 *                  intent) — the sub-obstacle SET is the same either way, so it
 *                  does not change collision.
 * ABSENT on the spawnWall effect ⇒ `straight` (pre-#146 byte-identical).
 */
export const WALL_GEOMETRY_SCHEMA = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("straight") }),
  z.strictObject({
    kind: z.literal("polyline"),
    points: z.array(WALL_POLYLINE_POINT_SCHEMA).min(1).max(64),
    closed: z.boolean().default(false),
  }),
]);
export type WallGeometry = z.infer<typeof WALL_GEOMETRY_SCHEMA>;

/**
 * Spawn a dynamic-terrain WALL (#129) — a spawnable, hashed, temporary obstacle
 * that collision AND line-of-sight honor (Ice Wall / Anivia-style). Resolved at
 * cast Phase-B (like `blink`): the wall is placed CENTERED on the resolved aim
 * point, oriented PERPENDICULAR to the caster→aim direction, as a ROW OF N
 * circular sub-obstacles (geometry decision D1 — reuses the existing circle
 * collision/LoS predicates verbatim). Instant-delivery only (crossValidate
 * rejects projectile/zone delivery + offCast — the spawn needs the Phase-B aim
 * plumbing). NOT an aura, NOT per-target: the effect router treats it as a
 * no-op (the real spawn happens in the cast machine).
 *
 * BLOCKING FLAGS (the flexibility core) default to the ICE-WALL profile
 * (everything on, `all` teams); flip them for one-way walls / Wind-Wall
 * (projectiles+LoS, movement off) / smoke (LoS only) with no engine change.
 */
export const EFFECT_SPAWN_WALL_SCHEMA = z.strictObject({
  kind: z.literal("spawnWall"),
  /** Total wall length in world units (the row-of-circles spans this). > 0. */
  lengthUnits: UNITS_SCHEMA.refine((r) => r > 0, {
    message: "spawnWall lengthUnits must be > 0",
  }),
  /** Radius of each circular sub-obstacle (the wall's half-thickness). > 0. */
  thicknessUnits: UNITS_SCHEMA.refine((r) => r > 0, {
    message: "spawnWall thicknessUnits must be > 0",
  }),
  /** Number of sub-obstacle circles placed evenly along the wall line. Author
   *  enough that adjacent circles overlap so the edge reads as solid. */
  subObstacleCount: z.number().int().gte(1).lte(64),
  /** Lifetime in ticks; the wall is alive for exactly this many ticks. >= 1. */
  durationTicks: z.number().int().gte(1).lte(1 << 20),
  /** Blocks body movement (gated by `blockedTeams`). Default true. */
  blocksMovement: z.boolean().default(true),
  /** Blocks projectiles mid-flight (gated by `blockedTeams`). Default true. */
  blocksProjectiles: z.boolean().default(true),
  /** Blocks line-of-sight. Default true. Team-gated by `losBlockedTeams`
   *  (#146 item 1); the #129 default (`all`) is SYMMETRIC for everyone (D6). */
  blocksLoS: z.boolean().default(true),
  /** Which teams the wall blocks for movement/projectiles. Default `all`. */
  blockedTeams: WALL_BLOCKED_TEAMS_SCHEMA.default("all"),
  /** #146 item 1: which teams the wall blocks LINE-OF-SIGHT for, relative to the
   *  wall owner (`all|enemies|allies`). SEPARATE from `blockedTeams` (movement/
   *  projectiles) so a smoke wall can block sight for enemies but not movement.
   *  Default `all` = SYMMETRIC (the #129 D6 default — blocks everyone incl. the
   *  owner), so pre-#146 content is byte-identical. `enemies`/`allies` make a
   *  one-way / directional vision wall. Only consulted when `blocksLoS`. */
  losBlockedTeams: WALL_BLOCKED_TEAMS_SCHEMA.default("all"),
  /** #146 item 5: facing-gated PROJECTILE blocking (Wind-Wall). Default `both`
   *  = block regardless of direction (#129 byte-identical). `incoming`/`outgoing`
   *  gate by the sign of the projectile velocity dotted with the wall normal
   *  (caster→aim). Only meaningful when `blocksProjectiles`. */
  blockDirection: BLOCK_DIRECTION_SCHEMA.default("both"),
  /** #146 item 4: authored geometry. Absent ⇒ a straight row-of-circles using
   *  `lengthUnits`/`subObstacleCount` (pre-#146 byte-identical). A `polyline`
   *  places one circle per authored vertex (ring / curve / box). */
  geometry: WALL_GEOMETRY_SCHEMA.optional(),
  /** #146 item 3: HAZARD-HOST wall — an on-contact effect payload (fire / thorn
   *  / freeze wall) reusing the ground-zone firing model. Absent ⇒ inert
   *  terrain (pre-#146 byte-identical). Present ⇒ every `intervalTicks` (fires
   *  ON the spawn tick, like a zone) each living body OVERLAPPING the wall
   *  footprint takes `effects`, team-gated by `targets` relative to the owner. */
  hazard: z.lazy(() => WALL_HAZARD_SCHEMA).optional(),
});

/**
 * #450: set the MATCH-scoped `SimState.projectileTimeScale` modifier (the
 * sim primitive #402 shipped, exposed to content for the first time here) —
 * Bullet Time's payload. Resolved at the cast machine's Phase-B top-level
 * scan, exactly like `spawnWall`/`dash` above: the generic per-target effect
 * router (`applyEffect`) only ever sees an `Entity[]` draft, never the full
 * `SimState`, so this effect kind cannot write `state.projectileTimeScale`
 * from there. It REQUIRES instant delivery and a NON-offCast ability (same
 * two constraints as `spawnWall`, same reason: only the Phase-B resolve path
 * has a channel back to `step.ts` to carry the fresh value into the next
 * tick's state — crossValidate enforces both).
 *
 * `scope` is authored OWNER-RELATIVE (`AreaTargets`: `"enemies"` / `"allies"`
 * / `"all"`, the SAME reusable vocabulary `dash`/`Area` already use) rather
 * than mirroring the sim's `ProjectileTimeScaleScope` 1:1 — content has no
 * way to know a caster's concrete `TeamIndex` ahead of a match, so a raw
 * `team` field would be unauthorable. The resolve-time scan converts it to
 * the concrete sim scope using the CASTER's actual team: `"all"` → `{kind:
 * "all"}`, `"allies"` → `{kind: "team", team: casterTeam}`, `"enemies"` →
 * `{kind: "excludeTeam", team: casterTeam}` (see `cast.ts`
 * `resolveProjectileTimeScaleScope`). Bullet Time itself only ever authors
 * `"all"` (a genuinely global slow, not team-relative) — the other two
 * variants exist for a hypothetical future ability of this same shape,
 * exactly like `ProjectileTimeScaleScope`'s own doc comment anticipates.
 *
 * `multiplierBp` (10000 = unchanged speed; 5000 = half speed; capped at
 * `1 << 16` — 6.5536x, comfortably above any sane speed-up) and
 * `durationTicks` (>= 1) are resolved to `expiresAtTick = <resolve tick> +
 * durationTicks` at apply time — mirroring `stun.durationTicks`'s "resolved
 * to a concrete expiry at apply time, not authored as one" convention.
 *
 * CONCURRENT-CAST DESIGN CALL (flagged for review, not silently picked):
 * `SimState.projectileTimeScale` is a single NULLABLE field, not a stack/list
 * — so a fresh `projectileTimeScale` effect UNCONDITIONALLY OVERWRITES
 * whatever the field already held, even if a prior modifier's window is
 * still active. Multiple such effects resolving on the SAME tick apply in
 * ascending seat (resolve) order — last-writer-wins, deterministic. This
 * mirrors the "single nullable field, overwrite not merge" shape the field
 * already had before this issue (#402) rather than inventing a stacking
 * rule Bullet Time's own spec ("all projectiles... move at 50% speed for
 * 2s" — a non-stacking read) never asked for.
 */
export const EFFECT_PROJECTILE_TIME_SCALE_SCHEMA = z.strictObject({
  kind: z.literal("projectileTimeScale"),
  scope: AREA_TARGETS_SCHEMA,
  multiplierBp: z.number().int().gte(0).lte(1 << 16),
  durationTicks: z.number().int().gte(1).lte(1 << 20),
});
export type EffectProjectileTimeScale = z.infer<typeof EFFECT_PROJECTILE_TIME_SCALE_SCHEMA>;

/**
 * #257: every effect kind EXCLUDING `teamConditional` itself — the allowed
 * shape of a `teamConditional`'s `ifAlly`/`ifEnemy` legs. Declared BEFORE
 * `EFFECT_TEAM_CONDITIONAL_SCHEMA` (which does not exist yet at this point in
 * the file), so a leg referencing `teamConditional` is UNREPRESENTABLE at the
 * TYPE level — one level of nesting only, enforced by construction rather than
 * a runtime/crossValidate check (the #257 "no nested teamConditional" decision).
 */
export const SIMPLE_EFFECT_SCHEMA = z.discriminatedUnion("kind", [
  EFFECT_DAMAGE_SCHEMA,
  EFFECT_HEAL_SCHEMA,
  EFFECT_STUN_SCHEMA,
  EFFECT_INTERRUPT_SCHEMA,
  EFFECT_APPLY_AURA_SCHEMA,
  EFFECT_DISPEL_SCHEMA,
  EFFECT_BLINK_SCHEMA,
  EFFECT_DISPLACE_SCHEMA,
  EFFECT_DASH_SCHEMA,
  // #486: multi-tick self-propelled travel (charge). Appended so the
  // discriminated union stays additive.
  EFFECT_CHARGE_SCHEMA,
  EFFECT_BUILD_POINTS_SCHEMA,
  EFFECT_SPEND_POINTS_SCHEMA,
  EFFECT_SPAWN_WALL_SCHEMA,
  // #324: resource-pool gain (mirrors buildPoints for `resource` instead of
  // `points`). Appended so the discriminated union stays additive.
  EFFECT_GAIN_RESOURCE_SCHEMA,
  // #382: resource-pool spend-all (mirrors spendPoints for `resource` instead
  // of `points`). Appended so the discriminated union stays additive.
  EFFECT_SPEND_RESOURCE_SCHEMA,
  // #450: match-scoped projectile time-scale modifier (Bullet Time). Appended
  // so the discriminated union stays additive.
  EFFECT_PROJECTILE_TIME_SCALE_SCHEMA,
]);
export type SimpleEffect = z.infer<typeof SIMPLE_EFFECT_SCHEMA>;

/**
 * #257: team-conditional effect payload — resolves to a DIFFERENT inner
 * effect depending on whether the effect's TARGET is on the caster's (the
 * effect's SOURCE's) team or not, read at effect-APPLY time against the LIVE
 * `team` of both entities (never a fixed/global team — see
 * `packages/sim/src/effects.ts` `applyEffect`). Generalizes the "hits enemy →
 * damage, hits ally → heal" shape (Holy Orb, #256) into a reusable,
 * config-authored primitive — no per-ability sim code required.
 *
 * RESOLUTION RULE (LOCKED):
 *   - `ifAlly`  — the target is on the SAME team as the source, INCLUDING the
 *     degenerate case where the target IS the source (self-cast / self-hit).
 *     Self is a same-team SUBSET of "ally", not a third case — matching the
 *     ally/enemy roster split `targeting.ts` (`allyRoster`/`enemyRoster`) and
 *     the area fan-out's `teamSelected` helper, which already key off plain
 *     team equality (self included) everywhere else in the sim.
 *   - `ifEnemy` — the target is on a DIFFERENT team from the source.
 *
 * NESTING: `ifAlly`/`ifEnemy` are ORDINARY (non-conditional) effects — a
 * `teamConditional` may NOT nest inside another `teamConditional`. This is
 * unrepresentable at the TYPE level (`SIMPLE_EFFECT_SCHEMA`, declared above,
 * excludes `teamConditional` by construction), not merely rejected at
 * runtime.
 *
 * Two Phase-B-only effect kinds — `blink` (needs the cast's resolved aim
 * point) and `spawnWall` (needs a caster + aim + the wall-spawn collector) —
 * are schema-legal inside `ifAlly`/`ifEnemy` but REJECTED by crossValidate,
 * for the SAME reason the #146 wall-hazard `effects` list already rejects
 * them: both resolve ONLY via a top-level scan of an ability's OWN
 * `effects` array (cast Phase-B), so a copy buried inside a
 * `teamConditional` leg is never found by that scan and instead reaches the
 * generic per-target effect router, where both kinds are a deliberate no-op
 * — a silent footgun, rejected loudly instead.
 */
export const EFFECT_TEAM_CONDITIONAL_SCHEMA = z.strictObject({
  kind: z.literal("teamConditional"),
  /** Effect applied when the target is on the caster's team (ally, incl. self). */
  ifAlly: SIMPLE_EFFECT_SCHEMA,
  /** Effect applied when the target is on a different team (enemy). */
  ifEnemy: SIMPLE_EFFECT_SCHEMA,
});
export type EffectTeamConditional = z.infer<typeof EFFECT_TEAM_CONDITIONAL_SCHEMA>;

/**
 * #320: caster-targeted effect override. Applies `effect` to the CASTER (the
 * enclosing effect's SOURCE seat) INSTEAD OF the ability's normally-resolved
 * TARGET — independent of the ability's own target resolution. Resolved at
 * the very TOP of `applyEffect`, alongside `teamConditional`, by RECURSING
 * into the SAME router with `targetSeat` replaced by `sourceSeat` — so a
 * `self` effect is fully transparent: the immunity gate below and every
 * per-kind arm of the switch see a concrete effect applied to a concrete
 * (self) target, exactly as if it had been authored directly against the
 * caster. No OTHER exhaustive switch over `Effect` needs a "self" arm (only
 * `isHostileEffect`, which classifies by the WRAPPED effect, needs one —
 * mirroring `teamConditional`'s `ifAlly` classification).
 *
 * First consumer: Shadowstep (#134/#320) composes an enemy-targeted
 * `displace` with a `self`-wrapped `applyAura` (a caster-only move-speed
 * buff) on the SAME effect chain — `applyAura`'s own target resolution
 * (always the ability's TARGET) could not express "hit the enemy, buff
 * myself" before this primitive existed. Generically useful beyond
 * Shadowstep: any "hit an enemy, buff yourself" pattern (lifesteal-on-hit
 * style self-buffs) authors as `{ kind: "self", effect: {...} }`.
 *
 * NESTING: `effect` is a `SimpleEffect` — a `self` may NOT wrap a
 * `teamConditional` or another `self` (unrepresentable at the TYPE level, the
 * same one-level-of-nesting discipline #257 established for
 * `teamConditional`). `blink`/`spawnWall` are schema-legal inside `effect`
 * but REJECTED by crossValidate: both resolve ONLY via a top-level scan of
 * the ability's OWN `effects` array (cast Phase-B), so a copy buried inside
 * `self` is invisible to that scan and would silently no-op — the SAME
 * reserved-arm discipline the `teamConditional` legs already apply to their
 * own payload.
 */
export const EFFECT_SELF_SCHEMA = z.strictObject({
  kind: z.literal("self"),
  effect: SIMPLE_EFFECT_SCHEMA,
});
export type EffectSelf = z.infer<typeof EFFECT_SELF_SCHEMA>;

export const EFFECT_SCHEMA = z.discriminatedUnion("kind", [
  ...SIMPLE_EFFECT_SCHEMA.options,
  EFFECT_TEAM_CONDITIONAL_SCHEMA,
  EFFECT_SELF_SCHEMA,
]);

export type Effect = z.infer<typeof EFFECT_SCHEMA>;

// ─── Aura definitions (#30) ────────────────────────────────────────────
// An aura is a COMPOSITION of zero-or-more primitives. Six primitive kinds
// (stat-modifier, control-flag, absorb-pool, immunity, periodic, trigger)
// cover the whole design space; a seventh is a design-review trigger, not a
// hack. Abilities apply auras by referencing an AuraDef id via an
// `applyAura` effect. Definitions live in content (a bundle `auras` map), so
// tuning is data. Only the `periodic` primitive is wired in sim today; the
// rest ship as valid schema + inert type arms (reserved-arm pattern).

export const STAT_KEY_SCHEMA = z.union([
  z.literal("maxHp"),
  z.literal("moveSpeed"),
  z.literal("radius"),
  z.literal("weight"),
  // #121: armor is a first-class stat, aura-modifiable via `statMod`
  // (add/addBp) exactly like moveSpeed under slow (#106) — no new aura
  // primitive. Effective armor is read via `getStat(..., "armor", ...)`,
  // floored at 0, and mitigates purely-physical damage in the damage arm.
  z.literal("armor"),
  // #326: general, ALL-SCHOOL "percent more/less damage" modifiers — the
  // primitive Stand Behind Me's deferred "-30% damage taken" leg (#311/#321),
  // Defensive Strike (a stacking -2%/stack damage-taken debuff... on its own
  // holder — a self-buff), and Battleshout ("+10% damage dealt") all need.
  // UNLIKE `armor` (a real base value on the champion stats schema, physical-
  // school-only mitigation), these two carry NO champion base — they are
  // PURE aura-derived deltas around a 0 ("no modifier") baseline, read via
  // `getStat(..., "damageTakenBp"/"damageDealtBp", ...)` in the `damage`
  // effect arm. Because the base is always 0, only the `add` statMod op is
  // meaningful here (`addBp` scales the base, which is always 0 — a silent
  // no-op crossValidate rejects as a footgun); a content author expresses
  // "-30%" directly as `{ op: "add", value: -3000 }`. Signed, UNFLOORED by
  // `getStat` (a magnitude stat like armor/moveSpeed floors at 0 because
  // negative is invalid; these represent a signed delta, so a stack of
  // reductions can legitimately go very negative — the `damage` arm alone
  // clamps the PRACTICAL multiplier at 0, never negative/amplified-into-
  // healing). See `packages/sim/src/effects.ts`'s `damage` case for the
  // exact pipeline position (dealt-side folded into the source's own
  // magnitude before #83 tag-scaling; taken-side AFTER #121 armor mitigation,
  // BEFORE #103 shield absorb).
  z.literal("damageTakenBp"),
  z.literal("damageDealtBp"),
  // #548: `resourceGainBp` mirrors `damageDealtBp`/`damageTakenBp` exactly —
  // a third BASE-LESS bp-modifier key, this one scaling `gainResource`
  // effect firings (mana/rage/energy regeneration from ANY source: a
  // `trigger`-driven event, a `periodic` DoT/HoT tick — routed via the
  // periodic's `aura.sourceSeat`, so a bleed can grant its CASTER resource —
  // or a plain ability/weapon `effects` entry) instead of `damage`. Same
  // rules apply: no `Champion.stats` field backs it (`getStat`'s `readBase`
  // returns 0), so only `op: "add"` is meaningful (`addBp` scales the
  // always-0 base — crossValidate rejects it as the same silent-no-op
  // footgun as the other two base-less keys), and `getStat` leaves it
  // UNCLAMPED (a signed delta) — the `gainResource` effect arm alone clamps
  // the PRACTICAL multiplier at 0 (a "generate slower" debuff can zero a
  // firing's amount, never invert it into a resource DRAIN — `spendResource`
  // already owns that). Read off the effect's SOURCE (the resource pool is
  // always caster-owned), the same seat `gainResource` already mutates — see
  // `packages/sim/src/effects.ts`'s `gainResource` case.
  z.literal("resourceGainBp"),
]);
export type AuraStatKey = z.infer<typeof STAT_KEY_SCHEMA>;

export const CONTROL_FLAG_SCHEMA = z.union([
  z.literal("move"),
  z.literal("cast"),
  z.literal("attack"),
  z.literal("targetable"),
]);
export type ControlFlag = z.infer<typeof CONTROL_FLAG_SCHEMA>;

export const TRIGGER_EVENT_SCHEMA = z.union([
  z.literal("onHitTaken"),
  z.literal("onCastComplete"),
  z.literal("onExpire"),
  // #187: fires the instant a `dispel` REMOVES the aura holding this trigger —
  // the "Unstable Affliction" hook (dispel my debuff, it explodes on YOU). The
  // reacting effect targets the DISPELLER (the dispel's source; a self-dispel
  // targets the dispeller = self). Fired ONLY on the dispel path (effects.ts
  // `dispel` arm), NEVER on natural expiry (that is `onExpire`) — so it is
  // mapped to `null` in the seat-addressed `dispatchTriggers` table. A
  // dispel-immune / undispellable aura is never removed, so it never triggers.
  z.literal("onDispelled"),
  // #55: fires the instant a projectile strikes the aura holder (body-block
  // or direct entity hit) — the hook a reflect aura (#43) keys off. Distinct
  // from `onHitTaken` (damage-taken, delivery-agnostic): a projectile can be
  // reflected BEFORE its damage lands, so this event precedes effect apply.
  z.literal("onProjectileHit"),
  // #58: fires the instant a single-target, bodyless (direct-cast / lock-on)
  // effect would land on the aura holder — BEFORE it is applied — so a
  // reflect aura can cancel it on the holder and redirect it to the caster.
  // The bodyless sibling of `onProjectileHit`: no travelling body to re-own,
  // so "reflect" means cancel-here + re-apply-to-caster (attribution flips).
  // Not emitted for area/AoE effects (those are not reflectable — #58 scope).
  z.literal("onIncomingEffect"),
  // #669: fires the instant a `stack`-kind stacking aura's count REACHES (or
  // is already at/above, on the transition tick) the primitive's own
  // `stackThreshold` — the "Getting Hot" idiom (stack a self-buff to N, then
  // proc a payoff). Edge-triggered on the (prevStacks < threshold ≤
  // newStacks) transition, so re-stacking past an already-crossed threshold
  // does not refire, and a fresh attach after full expiry (stacks implicitly
  // reset) can refire. Requires `stackThreshold` on the SAME trigger
  // primitive (`AURA_PRIM_TRIGGER_SCHEMA`); crossValidate rejects this value
  // on a non-`stack` def and rejects `stackThreshold` on every OTHER `on`
  // value. Like `onDispelled`/`onExpire`, this is fired at its own dedicated
  // call site (the aura-attach path in `effects.ts`'s `applyAura` case), NOT
  // via the seat-addressed `dispatchTriggers` — mapped to `null` in
  // `TRIGGER_EVENT_TO_SIM_KIND` (sim `triggers.ts`).
  z.literal("onStackReached"),
]);
export type TriggerEvent = z.infer<typeof TRIGGER_EVENT_SCHEMA>;

/** Stat modifier: `addBp` scales by ±basis-points (slow = moveSpeed -3000);
 *  `add` is a flat integer offset. Integer math only. */
export const AURA_PRIM_STAT_MOD_SCHEMA = z.strictObject({
  kind: z.literal("statMod"),
  stat: STAT_KEY_SCHEMA,
  op: z.union([z.literal("addBp"), z.literal("add")]),
  value: z.number().int().gte(-(1 << 24)).lte(1 << 24),
});

export const AURA_PRIM_CONTROL_FLAG_SCHEMA = z.strictObject({
  kind: z.literal("controlFlag"),
  flag: CONTROL_FLAG_SCHEMA,
});

/**
 * Absorb-pool USE mode (#182). Default `shield` is the historical #103 meaning:
 * a pool that soaks INCOMING damage to the holder (drained by the damage arm),
 * byte-identical to pre-#182 content (which authors no `mode`). `reservoir`
 * flips the pool INSIDE-OUT: it does NOT soak incoming damage (the shield-drain
 * SKIPS it), and is instead a FUND that a `periodic` primitive on the same def
 * draws down each firing — a generic "release a stored magnitude over the
 * aura's window" pool. It is the SAME `absorbRemaining` runtime field either
 * way; only WHAT consumes it differs. The Chaos Void well accumulates absorbed
 * damage into a `reservoir` pool (on the zone), then the collapse bolt applies a
 * `reservoir`-mode periodic-damage aura seeded from it (`applyAura.absorbSeed`),
 * so the whole "accumulate → release as a DoT" chain rides ONE primitive with
 * NO bespoke accumulator.
 */
export const ABSORB_POOL_MODE_SCHEMA = z.union([
  z.literal("shield"),
  z.literal("reservoir"),
]);
export type AbsorbPoolMode = z.infer<typeof ABSORB_POOL_MODE_SCHEMA>;

export const AURA_PRIM_ABSORB_POOL_SCHEMA = z.strictObject({
  kind: z.literal("absorbPool"),
  amount: z.number().int().gte(0).lte(1 << 24),
  schoolFilter: z.array(AURA_TAG_SCHEMA).optional(),
  /** #182: shield (soak incoming damage, the default/historical meaning) vs
   *  reservoir (fund a `periodic` on the same def; skipped by the shield drain).
   *  Defaulted so pre-#182 content parses byte-identically as a shield. */
  mode: ABSORB_POOL_MODE_SCHEMA.default("shield"),
});

/** Absolute (tag-independent) immunity scope (#88, extended #142). `damage`
 *  blocks ALL `damage` effects; `hostile` blocks all hostile effects (damage +
 *  hostile aura application) regardless of content tags. Unblocks Frost Mage
 *  iceblock, where a tag selector can't express "block everything harmful".
 *
 *  `forcedMove` (#142) is an INDEPENDENT axis (NOT a CC class): it blocks
 *  INVOLUNTARY position changes driven against the holder's will — the
 *  `displace` effect (#119 knockback / pull / grip / charge; #134 shadowstep's
 *  self-relocation is EXEMPT — it moves the caster, not the immune target) AND
 *  the sustained `forcedMovement` aura drive (#147 fear / charm / disorient).
 *  It is deliberately SEPARATE from `hostile` (iceblock does not, by itself,
 *  grant knockback-immunity) — an "unstoppable"/"stalwart" buff is authored as
 *  an `immunity { all: "forcedMove" }` primitive. */
export const IMMUNITY_SCOPE_SCHEMA = z.union([
  z.literal("damage"),
  z.literal("hostile"),
  z.literal("forcedMove"),
]);
export type ImmunityScope = z.infer<typeof IMMUNITY_SCOPE_SCHEMA>;

/**
 * Immunity primitive (#88). Two mutually-exclusive forms, both discriminated
 * by `kind: "immunity"` (so the outer AURA_PRIMITIVE_SCHEMA discriminated
 * union stays intact):
 *   - `selector` — tag-selector immunity (schema-valid, reserved; matches an
 *     effect/aura's content tags). Kept for forward compatibility.
 *   - `all` — absolute scope immunity (#88), tag-independent.
 * Exactly one of `selector`/`all` must be present; enforced in crossValidate
 * (a discriminatedUnion member cannot carry a .refine, so the mutual-exclusion
 * invariant lives in the content validator, not the schema).
 */
export const AURA_PRIM_IMMUNITY_SCHEMA = z.strictObject({
  kind: z.literal("immunity"),
  selector: TAG_SELECTOR_SCHEMA.optional(),
  all: IMMUNITY_SCOPE_SCHEMA.optional(),
});

/**
 * #320: cast-time-snapshot scaling source for a periodic's per-tick
 * magnitude. Exactly one source at v1 — the applier's COMBO-POINT balance,
 * captured the moment this aura instance was (re)applied (never read LIVE
 * per tick, unlike `damage.perPointBonus` / `applyAura.durationPerPoint`,
 * which read the SOURCE's current pool at apply time but do not persist it
 * for later firings). Reserved as a union (not a bare literal) so a future
 * snapshot source (e.g. a resource-pool balance) appends without reshaping
 * the primitive.
 */
export const PERIODIC_SNAPSHOT_SOURCE_SCHEMA = z.union([z.literal("pointsAtApply")]);
export type PeriodicSnapshotSource = z.infer<typeof PERIODIC_SNAPSHOT_SOURCE_SCHEMA>;

/**
 * #320: scale a periodic's per-tick magnitude by a value SNAPSHOTTED at
 * cast/apply time rather than read live each firing. When set, every firing's
 * `effect.amount` becomes `effect.amount + perPoint * <the snapshot captured
 * when this aura instance was (re)applied>` — the finisher-DoT hook
 * (Rupture): casting with more combo points spent hits harder on EVERY
 * subsequent tick, independent of the caster's later point regeneration or
 * spending (which a LIVE read — `damage.perPointBonus` on the same inner
 * effect — cannot express, since Rupture's own `spendPoints` effect empties
 * the pool before the DoT ever ticks). Composes with `perPoint: 0` as a
 * documented no-op (byte-identical magnitude, still snapshots the value).
 * crossValidate requires the primitive's `effect` to be `damage` or `heal`
 * (the only kinds with an `amount` to scale) and rejects pairing this with a
 * `reservoir`-mode `absorbPool` on the same def (both override the SAME
 * per-firing `amount`, so combining them is ambiguous — reject loudly rather
 * than pick a silent precedence).
 */
export const PERIODIC_SNAPSHOT_SCALING_SCHEMA = z.strictObject({
  source: PERIODIC_SNAPSHOT_SOURCE_SCHEMA,
  /** Basis: `effect.amount + perPoint * <snapshot>`. Bounded like the live
   *  `damage.perPointBonus` / `applyAura.durationPerPoint` scalers. */
  perPoint: z.number().int().gte(0).lte(1 << 16),
});
export type PeriodicSnapshotScaling = z.infer<typeof PERIODIC_SNAPSHOT_SCALING_SCHEMA>;

/** Periodic (DoT/HoT): emit `effect` through the pipeline every
 *  `AuraDef.intervalTicks`, anchored to the aura's application tick.
 *  `snapshotScaling` (#320, optional) scales `effect`'s magnitude by a value
 *  captured once at apply time — see `PERIODIC_SNAPSHOT_SCALING_SCHEMA`. */
export const AURA_PRIM_PERIODIC_SCHEMA = z.strictObject({
  kind: z.literal("periodic"),
  effect: EFFECT_SCHEMA,
  snapshotScaling: PERIODIC_SNAPSHOT_SCALING_SCHEMA.optional(),
});

/** Deterministic trigger: fire on every Nth matching event (never %chance).
 *
 *  #669: `stackThreshold` is REQUIRED iff `on === "onStackReached"` and MUST
 *  be ABSENT otherwise (crossValidate — a discriminatedUnion member can't
 *  carry a `.refine`, so the IFF lives in the content validator, mirroring
 *  `forcedMovement.path`'s REQUIRED/ABSENT pattern). It names the stack count
 *  (1..stacking.max) whose crossing fires the primitive's `effect`; `everyN`
 *  is FIXED to `1` for this `on` value (edge-triggered by the stack
 *  transition itself, not a repeating cadence — crossValidate rejects any
 *  other value). */
export const AURA_PRIM_TRIGGER_SCHEMA = z.strictObject({
  kind: z.literal("trigger"),
  on: TRIGGER_EVENT_SCHEMA,
  everyN: z.number().int().gte(1).lte(1 << 16),
  effect: EFFECT_SCHEMA,
  /** #669: the stack count an `onStackReached` trigger fires at. See the
   *  field's doc comment above for the REQUIRED/ABSENT IFF rule. */
  stackThreshold: z.number().int().gte(1).lte(1 << 12).optional(),
});

// ─── Projectile reflect (#43) ───────────────────────────────────────────────
// The direction transform applied to a reflected projectile's velocity. A
// CONFIG-SELECTABLE STRATEGY, not a fixed rule — so reflect "feel" can iterate
// as content, not a sim rewrite. Only `reverse` is wired at v1; the rest are
// schema-valid but content-gated (crossValidate rejects them), the same
// reserved-arm pattern as the content-gated effect kinds. See issue #43.
//
//   reverse       — retrace the incoming vector exactly (negate dx/dy). The
//                   projectile goes back the way it came. Integer negation,
//                   zero float/sqrt. v1 default + only live strategy.
//   facingMirror  — RESERVED (#31): mirror the incoming direction across the
//                   reflecter's facing surface (8-octant normal table).
//   impactAngle   — LIVE (#60): deflection derived from where on the body the
//                   projectile struck (edge = shallow bounce, center = straight
//                   back). Normal = (contactPoint − bodyCenter), reflected
//                   integer-exact and rescaled to the incoming speed.
export const REDIRECT_STRATEGY_SCHEMA = z.union([
  z.literal("reverse"),
  z.literal("facingMirror"),
  z.literal("impactAngle"),
]);
export type RedirectStrategy = z.infer<typeof REDIRECT_STRATEGY_SCHEMA>;

/**
 * Reflect an incoming projectile back out (#43). Fires when a projectile
 * strikes the aura holder (`on` is fixed to `onProjectileHit` at v1 — the
 * only reflectable delivery today; direct-cast reflect is #58). On the every-
 * Nth boundary the struck projectile is REASSIGNED to the holder (ownerSeat/
 * ownerTeam flip → self-block + interceptable auto-invert against the new
 * owner), its velocity transformed per `redirect`, and its carried effect
 * payload SUPPRESSED on this strike (the reflecter takes no damage). The
 * projectile keeps flying under new ownership.
 *
 * Distinct from `trigger`: a trigger fires an entity `Effect`; a reflect
 * MUTATES the in-flight projectile + suppresses the payload — neither is an
 * entity-effect, so it is its own primitive kind.
 */
export const AURA_PRIM_REFLECT_SCHEMA = z.strictObject({
  kind: z.literal("reflectProjectile"),
  /** Fixed to `onProjectileHit` at v1 (only projectiles are reflectable).
   *  Kept as the full enum so direct-cast reflect (#58) is an additive value,
   *  not a schema reshape; crossValidate gates non-projectile values. */
  on: TRIGGER_EVENT_SCHEMA,
  /** Reflect on every Nth matching projectile hit (never %chance). */
  everyN: z.number().int().gte(1).lte(1 << 16),
  /** Outgoing-direction strategy. Only `reverse` is wired at v1. */
  redirect: REDIRECT_STRATEGY_SCHEMA,
});

/**
 * Reflect an incoming BODYLESS single-target effect (#58) — the direct-cast /
 * lock-on sibling of `reflectProjectile`. Fires when a single-target instant
 * effect would land on the aura holder (`on` is fixed to `onIncomingEffect`
 * at v1; crossValidate gates any other value). On the every-Nth boundary the
 * effect is CANCELLED on the holder and RE-APPLIED to the original caster with
 * attribution flipped to the reflecter (kill-credit / combat-log truth).
 *
 * Deliberately smaller than `reflectProjectile`: a bodyless effect has no
 * travelling body and no velocity, so there is NO `redirect` field — the
 * effect is simply re-aimed at the caster. crossValidate rejects a `redirect`
 * authored here rather than silently ignoring it (fail loud). Area/AoE effects
 * are NOT reflectable and never emit `onIncomingEffect` (#58 scope).
 *
 * Shares the per-instance every-Nth counter discipline with `trigger` and
 * `reflectProjectile` (see `isCountedPrimitive`), so counter slots stay
 * aligned across all counted primitives.
 */
export const AURA_PRIM_REFLECT_EFFECT_SCHEMA = z.strictObject({
  kind: z.literal("reflectEffect"),
  /** Fixed to `onIncomingEffect` at v1; crossValidate gates other values.
   *  Kept as the full enum for symmetry with `reflectProjectile.on`. */
  on: TRIGGER_EVENT_SCHEMA,
  /** Reflect on every Nth matching incoming effect (never %chance). */
  everyN: z.number().int().gte(1).lte(1 << 16),
});

// ─── Forced movement (#147) ──────────────────────────────────────────────────
// A SUSTAINED, sim-driven movement primitive — fear (flee the source), charm
// (walk to the source), disorient (stagger a fixed, looping authored path).
// Delivered as an AURA primitive so it inherits the whole aura lifecycle for
// free: duration (clean expiry → player control resumes), suppression, and —
// crucially — break-on-damage (`breakOnDamageThreshold`, #118) COMPOSITION (a
// fear that shatters when the victim takes enough damage is authored, not
// coded). The sim's forced-movement authority runs BEFORE player-intent
// movement each tick and reuses the SHARED integer collision/movement path (no
// parallel position writer). Config-only: every fear/charm/disorient is a pure
// content composition — ZERO engine code per CC.
//
// AUTHORING RECIPE (#157 amendment to the locked #147 D1 record — read this
// before composing a fear): a fear is authored as a `forcedMovement { mode:
// "awayFromSource" }` primitive ALONE. The forced-movement authority ITSELF
// suppresses the holder's own steering the ticks it drives them (it overrides
// the holder's own movement intent), so `forcedMovement` alone already produces a proper flee.
// Do NOT ALSO add a `controlFlag: "move"` (a ROOT) — the literal #147 D1 recipe
// ("fear = controlFlag:move + forcedMovement") is WRONG in this implementation:
// a root zeroes effective moveSpeed, so the forced step becomes a no-op and the
// unit FREEZES in place instead of fleeing. "Root beats fear" is DELIBERATE and
// tested (forcedMovement.test.ts §9) — it is how you author a stun-that-also-
// prevents-flee, NOT how you author a fear.

/**
 * How a `forcedMovement` primitive drives the holder each tick. A CLOSED
 * discriminated union (the sim switches over it exhaustively, no default
 * fall-through). Names mirror the `displace` effect's `awayFromSource`/
 * `towardSource` directions (#157) for cross-primitive consistency:
 *   - `awayFromSource` — FEAR: step directly away from the aura's source each
 *                   tick (recomputed from the source's LIVE position, so it
 *                   tracks a chasing caster).
 *   - `towardSource`   — CHARM: step toward the source's live position (walks
 *                   into melee contact, then collision-holds).
 *   - `fixedPath` — DISORIENT: walk a fixed, LOOPING polygon of authored
 *                   waypoints anchored at the holder's position when the forced
 *                   movement first drives it. REQUIRES `path`.
 */
export const FORCED_MOVEMENT_MODE_SCHEMA = z.union([
  z.literal("awayFromSource"),
  z.literal("towardSource"),
  z.literal("fixedPath"),
]);
export type ForcedMovementMode = z.infer<typeof FORCED_MOVEMENT_MODE_SCHEMA>;

/**
 * One waypoint of a `fixedPath` forced-movement path (#147) — an INTEGER offset
 * (signed world units) from the path ORIGIN (the holder's position the tick the
 * forced movement first drives it). The holder walks `origin+path[0]` →
 * `origin+path[1]` → … → `origin+path[n-1]` → back to `origin+path[0]` (loop).
 * Bounds are `UNITS_SCHEMA` ([-MAX_UNITS, MAX_UNITS]) so an offset can span the
 * arena; integer-only (no float).
 */
export const FORCED_MOVEMENT_WAYPOINT_SCHEMA = z.strictObject({
  dx: UNITS_SCHEMA,
  dy: UNITS_SCHEMA,
});
export type ForcedMovementWaypoint = z.infer<typeof FORCED_MOVEMENT_WAYPOINT_SCHEMA>;

/**
 * Forced-movement aura primitive (#147). Drives the holder's movement each tick
 * per `mode`, via the sim's forced-movement authority (runs before player
 * intent). `path` is REQUIRED iff `mode === "fixedPath"` and MUST be ABSENT
 * otherwise — mirrors the `displace` `distance`/`aim` IFF pattern: a
 * discriminatedUnion member can't carry a `.refine`, so the invariant is pinned
 * in crossValidate, while the closed `mode` union + the sim's exhaustive switch
 * make the illegal combination unreachable in the engine. Integer-only; the sim
 * recomputes `awayFromSource`/`towardSource` from live positions each tick and
 * advances the `fixedPath` cursor deterministically (hashed per-instance
 * runtime state).
 */
export const AURA_PRIM_FORCED_MOVEMENT_SCHEMA = z.strictObject({
  kind: z.literal("forcedMovement"),
  mode: FORCED_MOVEMENT_MODE_SCHEMA,
  /** Authored looping waypoints; REQUIRED iff `mode === "fixedPath"`, ABSENT
   *  otherwise (crossValidate). Non-empty, bounded to 64 waypoints. */
  path: z.array(FORCED_MOVEMENT_WAYPOINT_SCHEMA).min(1).max(64).optional(),
});

/**
 * #158: BLOCKS-LINE-OF-SIGHT primitive — an entity BODY that team-blocks
 * line-of-sight ("hide behind the big guy"). While the holder carries an ACTIVE
 * `blocksLoS` aura, its body (the `getStat` radius) occludes sight the SAME way
 * a pillar / wall circle does — reusing the shared `segmentHitsCircle` predicate
 * (LoS-check-only; it does NOT touch projectile-body collision, decision D2).
 * `teams` gates WHICH viewers it blocks, relative to the HOLDER'S team
 * (`all|enemies|allies`, default `enemies` — the big guy blocks enemy sight of
 * your team). INERT in stat reads (like `controlFlag`): it drives LoS, not a
 * stat. Carries NO per-instance runtime state, so it adds NO hashed AuraInstance
 * field — a def-authored flag, resolved from the bundle each LoS query.
 */
export const AURA_PRIM_BLOCKS_LOS_SCHEMA = z.strictObject({
  kind: z.literal("blocksLoS"),
  teams: WALL_BLOCKED_TEAMS_SCHEMA.default("enemies"),
});

/**
 * #199: PHASING primitive — pass-through movement/overlap toggles. FOUR
 * INDEPENDENT booleans, each gating its matching collision check while an
 * active phasing aura holds the holder:
 *   - `projectiles` — projectiles PASS THROUGH the holder's body: a true
 *     pass-through (the shot keeps flying and can still strike someone BEHIND
 *     the holder), NOT the immunity path — the projectile is never fizzled or
 *     absorbed, it simply does not body-block on this holder.
 *   - `entities`    — champion↔champion body overlap is skipped
 *     BIDIRECTIONALLY: the pair may overlap if EITHER party is phasing.
 *   - `pillars`     — the holder ignores static map-pillar collision (BUILT;
 *     the shipped content leaves it OFF).
 *   - `walls`       — the holder ignores dynamic-wall (#129) movement collision
 *     (walk through an ice wall); on expiry it is push-apart-ejected.
 * SCOPE FENCE: movement/overlap ONLY. Phasing does NOT change targeting, LoS,
 * or damage — it is "pass through", never "disappear". Each toggle DEFAULTS to
 * `false` so an author opts into exactly the axes they want (parsed defs carry
 * concrete booleans — no nullish branch); crossValidate REJECTS an all-false
 * (inert) phasing primitive. INERT in stat reads (like `controlFlag` /
 * `blocksLoS`): it drives collision, not a stat, and carries NO per-instance
 * runtime state, so it adds NO hashed `AuraInstance` field — a def-authored
 * flag resolved from the bundle each collision query (mirrors #158 blocksLoS).
 */
export const AURA_PRIM_PHASING_SCHEMA = z.strictObject({
  kind: z.literal("phasing"),
  projectiles: z.boolean().default(false),
  entities: z.boolean().default(false),
  pillars: z.boolean().default(false),
  walls: z.boolean().default(false),
});

/**
 * #669: NEXT-CAST-INSTANT primitive — the generic "stack a buff to N, then
 * your next cast of ABILITY X is free of its cast time" proc idiom (Fireblast
 * "Getting Hot" → Fireball; WoW's Hot Streak/Clearcasting is the same shape).
 * `abilityId` names the SPECIFIC ability this primitive fast-tracks — a
 * content→content reference, checked against the loaded ability registry by
 * crossValidate (mirrors `checkEffectAuraRefs`'s aura-ref checking). It is
 * deliberately ability-SPECIFIC, not "the next cast of anything": a generic
 * "next cast, any ability" flag could silently fast-track an unrelated spell,
 * which is not what a hand-authored proc like this ever means.
 *
 * An aura carrying this primitive IS the time-limited "window" (its own
 * `durationTicks` is the window length, e.g. 5s of ticks) — no separate timer
 * field is needed. The cast pipeline (`packages/sim/src/cast.ts`,
 * `evaluateCastStart`) reads it as a pure existence check
 * (`findNextCastInstantAura`, `auras.ts`) at the ONE site `ability
 * .castTimeTicks` is consumed: if the caster holds an ACTIVE aura with this
 * primitive whose `abilityId` matches the ability being cast, that cast's
 * effective cast time collapses to 0 for THIS cast only, and the granting
 * aura instance is CONSUMED (removed) — a one-shot proc, purely time-limited
 * (casting something else does NOT consume or cancel it; only its own
 * duration expiring, or the named ability actually being cast, ends it).
 *
 * INERT in stat reads and carries NO per-instance runtime state of its own
 * (like `blocksLoS`/`phasing`) — the sim reads it directly off the bundle
 * def each cast-start check, so it adds NO hashed `AuraInstance` field; the
 * per-entity STATE-hash SHAPE is unchanged by this primitive's existence.
 */
export const AURA_PRIM_NEXT_CAST_INSTANT_SCHEMA = z.strictObject({
  kind: z.literal("nextCastInstant"),
  abilityId: ABILITY_ID_SCHEMA,
});

export const AURA_PRIMITIVE_SCHEMA = z.discriminatedUnion("kind", [
  AURA_PRIM_STAT_MOD_SCHEMA,
  AURA_PRIM_CONTROL_FLAG_SCHEMA,
  AURA_PRIM_ABSORB_POOL_SCHEMA,
  AURA_PRIM_IMMUNITY_SCHEMA,
  AURA_PRIM_PERIODIC_SCHEMA,
  AURA_PRIM_TRIGGER_SCHEMA,
  AURA_PRIM_REFLECT_SCHEMA,
  AURA_PRIM_REFLECT_EFFECT_SCHEMA,
  // #147: sustained forced movement (fear/charm/disorient). Appended so the
  // discriminated union stays additive.
  AURA_PRIM_FORCED_MOVEMENT_SCHEMA,
  // #158: entity-body line-of-sight blocker. Appended so the union stays
  // additive.
  AURA_PRIM_BLOCKS_LOS_SCHEMA,
  // #199: pass-through phasing (projectiles/entities/pillars/walls). Appended
  // so the union stays additive.
  AURA_PRIM_PHASING_SCHEMA,
  // #669: next-cast-instant proc modifier (Getting Hot / Hot Streak idiom).
  // Appended so the union stays additive.
  AURA_PRIM_NEXT_CAST_INSTANT_SCHEMA,
]);
export type AuraPrimitive = z.infer<typeof AURA_PRIMITIVE_SCHEMA>;

/** Stacking policy when the same def is applied again. */
export const STACKING_RULE_SCHEMA = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("refresh") }),
  z.strictObject({ kind: z.literal("stack"), max: z.number().int().gte(1).lte(1 << 12) }),
  z.strictObject({ kind: z.literal("strongestWins") }),
  z.strictObject({ kind: z.literal("independentPerSource") }),
  // #218: independent per-INSTANCE stacking (multi-DoT). UNLIKE
  // `independentPerSource` (which keeps ONE shared-timer instance per source and
  // merely refreshes it on reapply), EVERY application from a given source mints
  // a FRESH instance with its OWN appliedAtTick/expiry — so N same-source DoTs
  // run N independent timers (Warlock Corruption/Immolate-style multi-stacking).
  // Cross-caster is uncapped (each source keeps its own instances); WITHIN a
  // (def, source) group the count is capped at `maxInstances`, and an overflow
  // application EVICTS the oldest same-source instance first (oldest = lowest
  // appliedAtTick, tie → array order). Count-based dispel (#135/#142) removes
  // instances one at a time. Adds NO new hashed AuraInstance field — each
  // instance is an ordinary aura; canonical ordering (defOrdinal, sourceSeat,
  // appliedAtTick) already separates instances by their distinct appliedAtTick.
  z.strictObject({
    kind: z.literal("independentInstances"),
    maxInstances: z.number().int().gte(1).lte(1 << 12),
  }),
]);
export type StackingRule = z.infer<typeof STACKING_RULE_SCHEMA>;

/**
 * #448: `"scaledOnly"` — a THIRD duration variant alongside a numeric literal
 * and `"permanent"`. Means "this def carries NO base lifetime of its own; the
 * APPLYING code path supplies the aura's ENTIRE real duration at attach time"
 * (e.g. the `stun` effect's `durationTicks`, always shallow-copied onto the
 * attached instance's def before `computeExpiry` runs — see `effects.ts`'s
 * `stun` case). Before this variant existed, a def whose true lifetime is
 * always caller-supplied still had to author SOME numeric floor to satisfy
 * `AURA_DURATION_SCHEMA`'s `gte(1)` — a fake placeholder number that lied
 * about the aura's real duration. `"scaledOnly"` removes that workaround
 * entirely: the def declares honestly that it has no authored duration.
 *
 * `computeExpiry` never actually needs to resolve a RAW `"scaledOnly"` value —
 * crossValidate rejects a generic `applyAura` effect from referencing a
 * `"scaledOnly"` def UNLESS it carries a `durationPerPointTable` (#447): the
 * table guarantees a fully concrete resultant duration (same guarantee the
 * `stun` effect's own per-cast override provides), so the ONLY paths that can
 * ever attach one are (a) an engine-internal effect (today: `stun`) that
 * ALWAYS overrides `durationTicks` to a concrete number first, or (b) a
 * generic `applyAura` carrying `durationPerPointTable`, which shallow-copies
 * the SAME kind of concrete override before attach (see `effects.ts`'s
 * `applyAura` case). `stun.json` is the first (and reference) consumer of
 * (a) — see `STUN_AURA_DEF_ID`'s doc comment; `kidney-shot.json` is the first
 * consumer of (b).
 */
export const AURA_DURATION_SCHEMA = z.union([
  z.number().int().gte(1).lte(1 << 20),
  z.literal("permanent"),
  z.literal("scaledOnly"),
]);

// ─── CC classification (#142) ────────────────────────────────────────────────
// A fixed, CLOSED 7-class crowd-control taxonomy — the first-class categorical
// axis trinkets, cleanses, purges, and CC immunities select on. DISTINCT from
// the free-form `AURA_TAG_SCHEMA`: a CC class is a real enum value, NEVER a
// content tag — this mirrors the `SCHOOL_SCHEMA` precedent (#100), NOT free-form
// tags. Invalid states are unrepresentable, and a content author cannot invent
// six spellings of "root".
//
// The class is ORTHOGONAL to mechanics: it is PURELY a selection vocabulary
// answering "what can remove/prevent this". It deliberately does NOT encode
// behavior — mechanical variation composes from the existing aura primitives.
// A `stun` and a `mez` are the SAME move+cast+attack `controlFlag` bundle; the
// `mez` additionally carries `breakOnDamageThreshold` (#118) so it breaks on
// hit. The class value is only what lets a trinket/dispel/immunity select one
// distinctly from the other. Damage-immunity while CC'd (Cyclone) composes an
// `immunity` primitive (#88) onto a `mez`-classed aura — a trinket that selects
// `mez` removes the aura and the immunity vanishes with it (intended).
//
// The ORDER here is NORMATIVE (like `SCHOOL_SCHEMA`): it fixes the canonical
// ordinal any hash or FUTURE diminishing-returns (DR) vector would key off. DR
// is deliberately DEFERRED but NOT foreclosed (#142 D4): a DR system would map
// its categories directly onto these classes, so the 7 classes are kept
// DISTINCT — no incapacitate/disorient split at v1, but the enum stays DR-ready.
// The classes are looked up (not per-instance sim state): an `AuraInstance`
// stores only its def id, so a def's `ccClasses` ride the CONTENT hash (like
// `tags`/`undispellable`), never the per-entity STATE hash.
export const CC_CLASS_SCHEMA = z.enum([
  "stun",
  "mez",
  "slow",
  "fear",
  "silence",
  "disarm",
  "root",
]);
export type CcClass = z.infer<typeof CC_CLASS_SCHEMA>;

/**
 * Canonical CC-class order (identical to `CC_CLASS_SCHEMA`'s value list).
 * Exported for content-side iteration/validation. The sim mirrors this order in
 * its own `ccClasses.ts` (it may only import protocol TYPES at runtime); a test
 * asserts the two never drift — same discipline as `SCHOOLS`/`SCHOOL_ORDER`.
 */
export const CC_CLASSES: readonly CcClass[] = CC_CLASS_SCHEMA.options;

/**
 * CC-class ROLLUP selectors (#142) — convenience group names that expand to a
 * fixed set of CC classes, COMPUTED from the taxonomy (never authored per-aura).
 * A dispel/immunity/suppression selector naming a rollup selects every member
 * class:
 *   - `hardCC`         → stun + mez + fear (the "can't act at all" group).
 *   - `movementImpair` → slow + root       (the "can't move freely" group).
 * Selection matches an aura against its class values AND the rollup names its
 * classes belong to (see the sim's `ccSelectionTokens`), so a `{tags:["hardCC"]}`
 * dispel removes any stun/mez/fear. The rollup NAMES are reserved CC vocabulary
 * alongside the class names — a content aura may not reuse either as a free-form
 * `tag` (crossValidate rejects it, keeping the enum the single source of truth).
 */
export const CC_ROLLUPS = {
  hardCC: ["stun", "mez", "fear"],
  movementImpair: ["slow", "root"],
} as const satisfies Record<string, readonly CcClass[]>;
export type CcRollup = keyof typeof CC_ROLLUPS;

// ─── CC dispel/immunity school (#448) ────────────────────────────────────────
// A fixed, CLOSED 2-value axis — PHYSICAL vs MAGICAL — that a (not-yet-built)
// magic-dispel ability and a (not-yet-built) physical-immunity effect will
// select on. Prior to #448 this distinction lived ONLY as decorative free-form
// `tags` (a mix of `"physical"`/`"magic"`/specific school names like `"frost"`/
// `"arcane"`/`"holy"` — never consumed by any sim/crossValidate code). This
// promotes it to a real enum value, mirroring the `CC_CLASS_SCHEMA` precedent
// (#142): a CC's dispel school is never a free-form tag, it is a closed field
// so invalid/missing states are unrepresentable-or-caught-at-load.
//
// DISTINCT from `dispelClass` (still RESERVED/unconsumed — see `AURA_DEF_SCHEMA`
// doc comment): `dispelClass` is reserved for a FINER-grained future taxonomy
// (magic/poison/curse — a DoT/debuff dispel-type axis), whereas
// `dispelSchool` is specifically the binary CC axis this issue builds. Both
// fields may exist on the schema without competing — but a CC aura's decorative
// free-form physical/magic/school TAG usage is superseded by this field and is
// retired from every shipped CC aura (#448).
//
// This issue builds the CLASSIFICATION AXIS ONLY: neither a magic-dispel
// ability nor a physical-immunity effect exists yet. `dispelSchool` is
// currently NEVER READ by `@pit/sim` — it rides the CONTENT hash (like
// `ccClasses`/`tags`), never the per-entity STATE hash, exactly like every
// other pure-classification aura-def field.
export const CC_DISPEL_SCHOOL_SCHEMA = z.enum(["physical", "magical"]);
export type CcDispelSchool = z.infer<typeof CC_DISPEL_SCHOOL_SCHEMA>;

/**
 * Canonical dispel-school value list (identical to `CC_DISPEL_SCHOOL_SCHEMA`'s
 * value list). Exported for content-side iteration/validation — mirrors
 * `CC_CLASSES`/`SCHOOLS`.
 */
export const CC_DISPEL_SCHOOLS: readonly CcDispelSchool[] = CC_DISPEL_SCHOOL_SCHEMA.options;

/**
 * #168: aura buff/debuff POLARITY — the server-authored classification the
 * client reads off the wire (WIRE_AURA) to color a buff row (gold border) vs a
 * debuff row (red), split the self-aura bar, and drive nameplate curation. A
 * CLOSED binary enum promoting the client's former local `AuraKind` convention
 * to first-class server content:
 *   - `buff`   — a defensive / utility self-aura (shield, spell-reflect, a
 *                passive, a beneficial mark).
 *   - `debuff` — a harmful aura ON its holder (a DoT, CC, or applied enemy mark).
 * REQUIRED on every aura def with NO `.default` — a real authoring decision
 * (like `schools`/`slug`), so a def author MUST classify it. There is
 * deliberately NO silent "unknown → buff" fallback: that footgun lived in the
 * client mirror table this field replaces, and is gone by construction (an
 * unclassified aura fails to parse at load). Part of the CONTENT hash (a def
 * property, like `tags`/`ccClasses`/`undispellable`) and surfaced on the wire,
 * but NEVER the per-entity STATE hash — an `AuraInstance` stores only its def
 * id, so polarity is looked up (not hashed) each snapshot.
 */
export const AURA_POLARITY_SCHEMA = z.union([z.literal("buff"), z.literal("debuff")]);
export type AuraPolarity = z.infer<typeof AURA_POLARITY_SCHEMA>;

export const AURA_DEF_SCHEMA = z.strictObject({
  /** #203: content-migration version stamp (see `SCHEMA_VERSION_SCHEMA`). */
  schemaVersion: SCHEMA_VERSION_SCHEMA,
  id: AURA_DEF_ID_SCHEMA,
  /** #163: rename-safe human name (opaque `id` is the stable identity). */
  slug: SLUG_SCHEMA,
  /**
   * #336: authored, player-facing display name (e.g. "Rend", "Frost Nova
   * Root"), mirroring `ABILITY_SCHEMA.name`'s convention exactly — but,
   * UNLIKE that field, `.optional()` rather than required. `ABILITY_SCHEMA
   * .name` has been required since day one, so no fixture ever had to grow
   * one retroactively; `AuraDef` is a much older, much more widely
   * hand-constructed shape (~30 sim test files build raw `AuraDef` literals
   * directly, bypassing this schema's `.parse()`), so making `name` REQUIRED
   * here would force every one of those literals to grow a value they have
   * no use for, purely to satisfy the TS shape — the exact churn
   * `ABILITY_SCHEMA.description` (see below) was deliberately built
   * `.optional()` to avoid. PURELY COSMETIC (never read by the sim, by
   * `crossValidate`'s gating logic, or by the state/content hash's behavior —
   * it IS part of the content hash as an ordinary def property, but no sim
   * code branches on it). Every shipped aura in
   * `packages/content/data/auras/*.json` still authors a real, non-empty
   * `name` — `.optional()` is a fixture-churn accommodation, not license to
   * ship undescribed content.
   */
  name: z.string().min(1).max(64).optional(),
  /**
   * #336: authored, player-facing tooltip text (e.g. "Bleeding for 22 damage
   * every second for 10s."), mirroring `ABILITY_SCHEMA.description` exactly
   * (same `.optional()` rationale — see `name` above and that field's
   * comment). STATIC authored text, not templated/interpolated from the
   * aura's own `durationTicks`/primitive magnitudes: simpler, matches how
   * `ABILITY_SCHEMA.description` already works, and every shipped ability
   * description is likewise static prose, not a computed string. The
   * tradeoff (flagged, not silently picked): a static description CAN drift
   * from the authored numbers if a balance pass changes `durationTicks` or a
   * primitive's magnitude without updating this string — there is no
   * build-time check tying the two together. Mitigation is process, not
   * code: a content-authoring change to an aura's numbers should always
   * re-read (and, if needed, re-word) its `description` in the same pass,
   * the same discipline `crossValidate` cannot enforce for
   * `ABILITY_SCHEMA.description` either (free-form prose has no
   * machine-checkable link to the numbers it describes). Never read by the
   * sim/crossValidate — client tooltip text only.
   */
  description: z.string().max(280).optional(),
  /** #168: server-authored buff/debuff polarity (see `AURA_POLARITY_SCHEMA`).
   *  REQUIRED — a real authoring decision, no silent default. */
  polarity: AURA_POLARITY_SCHEMA,
  tags: z.array(AURA_TAG_SCHEMA),
  /**
   * #142: the aura's CC classification — the closed-enum axis trinkets /
   * cleanses / purges / CC immunities select on (distinct from free-form
   * `tags`). Empty for a non-CC aura (a buff/DoT/shield). An aura is normally
   * ONE class (a stun is `["stun"]`), but the field is an ARRAY (mirroring
   * ability `schools`) so a future compound CC can carry more than one class
   * without a schema reshape. Defaulted to `[]` so sim/content code branches
   * without a nullish check. Part of the CONTENT hash (a def property), never
   * per-entity STATE hash. See `CC_CLASS_SCHEMA` for the no-DR-yet / DR-ready
   * rationale.
   */
  ccClasses: z.array(CC_CLASS_SCHEMA).default([]),
  /**
   * #448: the CC dispel/immunity SCHOOL — physical vs magical (see
   * `CC_DISPEL_SCHOOL_SCHEMA`). `.optional()` at the schema level (a non-CC
   * aura has no use for it), but crossValidate REQUIRES it whenever `ccClasses`
   * is non-empty — a CC aura with no classification is a content gap, not a
   * legal "unclassified" state (mirrors `polarity`'s "no silent buff/debuff
   * fallback" discipline, scoped to CC content only).
   */
  dispelSchool: CC_DISPEL_SCHOOL_SCHEMA.optional(),
  durationTicks: AURA_DURATION_SCHEMA,
  /** Periodic cadence in ticks; omit for a non-periodic aura. */
  intervalTicks: z.number().int().gte(1).lte(1 << 20).optional(),
  stacking: STACKING_RULE_SCHEMA,
  /**
   * #41 cross-ref audit: `dispelClass` and `drCategory` are RESERVED free-form
   * vocabularies (dispel type-axis magic/poison/curse; diminishing-returns
   * category). They are deliberately NOT registry-validated cross-references:
   * there is no referencing side and no registry to resolve them against yet —
   * nothing else in content consumes these values at v1. When a consumer lands
   * (a DR system / a dispel-by-type system), promote them to a closed enum or a
   * registry-checked id then. Until then, a bounded free string is correct;
   * there is no unresolved-reference class to sweep here.
   */
  dispelClass: z.string().min(1).max(32).optional(),
  drCategory: z.string().min(1).max(32).optional(),
  /** 0/omit ⇒ never breaks on damage. */
  breakOnDamageThreshold: z.number().int().gte(0).lte(1 << 24).optional(),
  /**
   * #91 (stealth): break when the HOLDER deals actual damage. `true` ⇒ the aura
   * is dropped the instant the holder's `damage` effect removes hp from a target
   * (post-absorb `dealt > 0`), resolved AFTER the hit lands (the opener benefits
   * from the aura, then it clears — Rogue-opener contract). `false`/omit ⇒ never
   * breaks on dealing damage. The deal-damage twin of `breakOnDamageThreshold`
   * (which breaks on TAKING damage); a stealth aura carries both.
   */
  breakOnDealDamage: z.boolean().optional(),
  /**
   * #135 Q4: this aura resists ORDINARY dispels. `true` ⇒ a normal `dispel`
   * effect SKIPS this aura regardless of selector match (hard defensives —
   * Ice Block, bubble-style immunities). `false`/omit ⇒ dispellable (the common
   * case). A dispel authored with `ignoreUndispellable: true` (Mass-Dispel
   * class) overrides this and strips it anyway. A STATIC content-def property
   * (part of the content hash), never per-entity sim state — no hashed-state
   * impact. The `dispelClass` type-axis (magic/poison/curse) stays RESERVED
   * (unwired) at v1; undispellable is the binary dispellable/not gate (#135 Q3).
   */
  undispellable: z.boolean().optional(),
  /** Magnitude snapshot point. Defaults to "impact"; "launch" reserved. */
  snapshotAt: z.union([z.literal("launch"), z.literal("impact")]).optional(),
  /** Tags this aura suppresses (renders inactive) while itself active (§4). */
  suppresses: TAG_SELECTOR_SCHEMA.optional(),
  /** Abilities carrying any of these tags cannot be cast while this holds (§3). */
  blocksAbilityTags: z.array(AURA_TAG_SCHEMA).optional(),
  /**
   * #637/#635: a PURE tag-carrier aura (e.g. `frozen`, decoupled from the CC
   * aura that applies it) has no mechanical primitive of its own — `tags` is
   * the entire payload, read by other content via `scaleIfTargetTag`/
   * `targetHoldsTag`. `min(0)` (not `min(1)`) so that shape is legal; every
   * primitive-bearing aura is unaffected (still authors its primitives array
   * as before).
   */
  primitives: z.array(AURA_PRIMITIVE_SCHEMA).min(0),
});
export type AuraDef = z.infer<typeof AURA_DEF_SCHEMA>;

/**
 * #91 (stealth): the well-known content key of the stealth aura def. Stealth is
 * authored entirely as an aura (duration + break rules), applied by a stealth
 * ability via `applyAura { def: "stealth" }`. This id is the ONE identity the
 * enemy-targeting gate keys off: `isTargetableBy` (in @pit/sim `targeting.ts`)
 * is a pure, BUNDLE-FREE read over hashed state — an `AuraInstance` carries only
 * its def id (not its tags), so a well-known def id is the only content identity
 * available without a content-bundle lookup. Mirrors `STUN_AURA_DEF_ID`. A
 * second stealth-like aura would reuse this id (Vanish's re-stealch), so the gate
 * stays a single predicate. The break rules themselves are DEF-authored and
 * general (`breakOnDamageThreshold` + `breakOnDealDamage`), so they work for any
 * aura — only the targeting gate is keyed to this id.
 *
 * #163: an ENGINE-OWNED id (like `STUN_AURA_DEF_ID`), so its minted `aur_<nanoid>`
 * VALUE lives here as a protocol constant. Stealth ships NO content JSON at v1
 * (no shipped stealth ability), so this constant is the sole home of the id; the
 * sim-test stealth fixture keys its def to this constant. Slug: `"stealth"`.
 */
export const STEALTH_AURA_DEF_ID: AuraDefId = "aur_7r3ZkYzAeH" as AuraDefId;

// ─── Spell schools (#100) ────────────────────────────────────────────────────
// A fixed, CLOSED 7-school enum — the first-class categorical axis champions,
// abilities, interrupts, immunities, dispels, and resist/DR systems build on.
// DISTINCT from the free-form `AURA_TAG_SCHEMA` (immunity/dispel selectors): a
// school is a real enum value, never a content tag — do NOT reuse the tag type
// for schools.
//
// The ORDER here is NORMATIVE. It fixes the canonical index used by the sim's
// per-entity `schoolLockedUntil` interrupt-lockout vector AND the state hash
// (see @pit/sim `schools.ts`, which mirrors this order locally under the
// types-only import rule; a test pins the two together). Reordering or
// inserting a school is a hashed-state change (SIM_VERSION bump + golden regen).
export const SCHOOL_SCHEMA = z.enum([
  "frost",
  "fire",
  "arcane",
  "shadow",
  "holy",
  "nature",
  "physical",
]);
export type School = z.infer<typeof SCHOOL_SCHEMA>;

/**
 * Canonical school order (identical to `SCHOOL_SCHEMA`'s value list). Exported
 * for content-side iteration/validation. The sim mirrors this order in its own
 * `schools.ts` (it may only import protocol TYPES at runtime); a test asserts
 * the two never drift.
 */
export const SCHOOLS: readonly School[] = SCHOOL_SCHEMA.options;

// ─── Abilities ─────────────────────────────────────────────────────────────

export const TARGET_TYPE_SCHEMA = z.union([
  z.literal("enemy"),
  z.literal("ally"),
  z.literal("self"),
  z.literal("ground"),
  z.literal("none"),
]);

export type TargetType = z.infer<typeof TARGET_TYPE_SCHEMA>;

// ─── Aim-snapshot timing (#36) ───────────────────────────────────────────────
// WHEN an ability locks its aim, relative to the cast wind-up. Applies to
// BOTH aim kinds (entity lock-on AND ground skillshot) — it's a timing policy
// orthogonal to WHAT is aimed at.
//   "start"  — freeze the aim at cast START (commit-on-press; wind-up
//              telegraphs the committed target/point). Default.
//   "finish" — resolve the aim at cast RESOLVE (re-read target position /
//              cursor at fire). Lock-on "finish" == today's Throw Spear.
// For an instant cast (castTimeTicks === 0) start == finish (no-op).
export const AIM_SNAPSHOT_SCHEMA = z.union([
  z.literal("start"),
  z.literal("finish"),
]);

export type AimSnapshot = z.infer<typeof AIM_SNAPSHOT_SCHEMA>;

// ─── LoS policy ────────────────────────────────────────────────────────────
// Per-ability line-of-sight policy. The cast state machine ONLY consults
// this object — no inlined `hasLineOfSight` calls at fixed points. This
// lets content express WoW-style semantics distinctly per ability:
//   polymorph  = { atCastStart: true,  atResolve: true,  duringChannel: false }
//   penance    = { atCastStart: true,  atResolve: false, duringChannel: false }
//   frost nova = { atCastStart: false, atResolve: false, duringChannel: false }
// See wp4-sim-combat.md §"LoS policy".
export const LOS_POLICY_SCHEMA = z.strictObject({
  atCastStart: z.boolean(),
  atResolve: z.boolean(),
  /** Reserved — channels don't exist yet; field ships so the policy
   * shape is complete. */
  duringChannel: z.boolean(),
});

export type LosPolicy = z.infer<typeof LOS_POLICY_SCHEMA>;

// ─── Facing policy (#27) ─────────────────────────────────────────────────────
// Per-ability CASTER-facing policy. Mirrors LOS_POLICY_SCHEMA's shape (a
// strictObject the cast machine consults). Distinct from `positionalPolicy`
// (#92), which gates on the TARGET's facing (backstab); this gates on the
// CASTER's own facing toward the target/aim.
//
//   requireAtCastStart — the caster must be facing the target (within the arc)
//                        to BEGIN the cast, else `notFacing` fizzle.
//   requireAtResolve   — the caster must STILL be facing at the resolve tick,
//                        else `facingBrokenAtResolve` fizzle. Only meaningful
//                        for `castTimeTicks > 0` (crossValidate rejects it on
//                        instants — they resolve the same tick they start).
//   snapToTargetOnUse  — auto-face the target on cast start ("lock on"). This
//                        is the historically-unconditional snap made opt-in;
//                        `false` leaves the caster's facing untouched.
//   arcCosThresholdBp  — optional per-ability arc override. See TUNING_SCHEMA's
//                        `facingArcCosThresholdBp` for the representation; omit
//                        to use that global default.
//
// ARC REPRESENTATION (integer-only, deterministic — no trig, no sector count):
// facing is a `FacingDir` vector, so "within arc" is expressed via the exact
// integer dot/cross products (`facingDot`/`facingCross`), NOT sector math. The
// arc's half-angle α is encoded by its cosine in basis points:
// `arcCosThresholdBp = round(10000·cos(α))`. The caster faces the target iff
// the angle between its facing and the direction to the target is ≤ α, i.e.
// `10000·dot ≥ arcCosThresholdBp · isqrt(dot² + cross²)` (the isqrt term is the
// floored integer length |facing|·|offset|). Reference values:
//   10000 → α=0°   (must face EXACTLY at the target)
//    7071 → α=45°  (90° total arc)
//       0 → α=90°  (180° total arc — target anywhere in the frontal half-plane;
//                   the default; reduces to `dot ≥ 0`)
//   -7071 → α=135° (270° total arc)
//  -10000 → α=180° (360° — always facing; the gate never rejects)
// Boundary (exact equality) counts as facing (arc edge inclusive).
export const FACING_POLICY_SCHEMA = z.strictObject({
  requireAtCastStart: z.boolean(),
  requireAtResolve: z.boolean(),
  snapToTargetOnUse: z.boolean(),
  /** Optional per-ability arc override (cosine of the arc half-angle ×10000).
   *  Omit → use `Tuning.facingArcCosThresholdBp`. */
  arcCosThresholdBp: z.number().int().gte(-10000).lte(10000).optional(),
});

export type FacingPolicy = z.infer<typeof FACING_POLICY_SCHEMA>;

// ─── Delivery ──────────────────────────────────────────────────────────────
// How an ability's effects reach the target: `instant` applies them inline at
// resolve (today's behavior); `projectile` spawns a travelling entity at
// resolve that carries the effects and applies them on the first swept-collision
// impact — enabling body-blocking (#29). Additive + content-gated: the field
// DEFAULTS to `instant`, so every existing ability parses byte-identically and
// the content hash of instant-only bundles is unchanged.
//
// `interceptable` selects which bodies can eat the projectile off the path:
//   { allies: true, enemies: true }  — first living body of either team blocks
//   { allies: false, enemies: true } — enemies-only (nobody peels for you)
// A projectile never collides with its own owner. See wp11-projectiles.
export const INTERCEPTABLE_SCHEMA = z.strictObject({
  /** Can a living ALLY of the owner intercept the projectile? */
  allies: z.boolean(),
  /** Can a living ENEMY of the owner intercept the projectile? */
  enemies: z.boolean(),
});

export type Interceptable = z.infer<typeof INTERCEPTABLE_SCHEMA>;

// ─── Projectile-vs-projectile interception (#205) ───────────────────────────
// DISTINCT from `interceptable` above (which gates which BODIES can body-block a
// shot). `interceptsProjectiles` marks a projectile as an INTERCEPTOR: each tick
// it additionally tests swept collision against every OTHER in-flight projectile
// and, on the earliest contact, DESTROYS the target (which never delivers its
// payload). First consumer: Priest "Protect the Weak" (shoots enemy shots out of
// the air).
//
// `teams` selects which projectiles it may hit relative to its LIVE owner:
//   "enemies" — only projectiles whose live ownerTeam != this projectile's
//               ownerTeam (auto-inverts through a #43 reflect, mirroring
//               `canIntercept`). DEFAULT for the first consumer.
//   "all"     — any other in-flight projectile, including allies' (grief/utility
//               content; not the first consumer's value).
export const INTERCEPTS_TEAMS_SCHEMA = z.enum(["enemies", "all"]);
export type InterceptsTeams = z.infer<typeof INTERCEPTS_TEAMS_SCHEMA>;

export const INTERCEPTS_PROJECTILES_SCHEMA = z.strictObject({
  /** Which OTHER projectiles this interceptor may destroy, relative to its
   *  owner. */
  teams: INTERCEPTS_TEAMS_SCHEMA,
  /**
   * #205 burst variant. Absent ⇒ POINT interceptor: one-for-one, the
   * interceptor is consumed the instant it destroys its first target (no
   * splash). Present ⇒ BURST interceptor: on the first projectile contact this
   * tick it detonates and destroys EVERY eligible (per `teams`) projectile
   * within `burstRadius` of the contact point in a SINGLE snapshot (no cascade —
   * a projectile the burst destroys does not itself intercept), then is
   * consumed. Ally projectiles in radius survive the enemies-only filter.
   * Squared-distance compare from the contact point, integer-only.
   */
  burstRadius: UNITS_SCHEMA.optional(),
});
export type InterceptsProjectiles = z.infer<typeof INTERCEPTS_PROJECTILES_SCHEMA>;

// ─── Projectile ricochet / bounce (#44) ──────────────────────────────────────
// Opt-in ricochet: a projectile that REFLECTS off obstacles (pillars + the arena
// edge, and — with `bounceOffChampions` — off struck champion bodies) instead of
// being consumed, keeping its payload across bounces until it runs out of either
// its `maxBounces` budget OR its SHARED travel-distance budget (the ability's
// `rangeUnits`; a bounce never extends how far the projectile can fly). Absent ⇒
// no ricochet (today's behavior, byte-identical: an obstacle hit consumes the
// projectile). First consumer: a wall-ricochet skillshot (content, later).
//
// DISTINCT from #43 reflect (a target aura reassigns owner + reverses) and from
// #205 interception (a projectile shoots down other projectiles). Bounce is a
// pure geometry reflection off static/edge/body surfaces, opt-in per delivery.
export const BOUNCE_SCHEMA = z.strictObject({
  /** Maximum number of ricochets before the projectile is consumed on its next
   *  obstacle hit. >= 1 (a 0-bounce projectile simply omits the block). */
  maxBounces: z.number().int().gte(1).lte(1 << 8),
  /**
   * Also ricochet off struck CHAMPION bodies (default off). When off (default),
   * a champion body-block delivers the payload and consumes the projectile as
   * usual — only pillars/walls/the arena edge bounce. When on, a champion hit
   * DELIVERS the payload AND ricochets off the body (a chaining ricochet), until
   * the bounce/travel budget runs out. Independent of the `interceptable` gate
   * (which still decides WHICH bodies register a hit).
   */
  bounceOffChampions: z.boolean().default(false),
});
export type Bounce = z.infer<typeof BOUNCE_SCHEMA>;

export const DELIVERY_INSTANT_SCHEMA = z.strictObject({
  kind: z.literal("instant"),
});

// ─── Ground zones (#52) ─────────────────────────────────────────────
// A persistent stationary hazard dropped on the map that re-applies the
// ability's effects to every selected unit in radius every `intervalTicks`
// for `durationTicks`, then expires. Reuses #51's area fan-out; requires an
// `area` block on the owning ability (the zone's radius/targets come from it).
//
// FIRING SEMANTICS (#52, decision B — DELIBERATE divergence from auras):
// a zone fires ON its spawn tick (delta 0 counts), then every interval. This
// is unlike periodic AURAS, which skip their application tick (delta 0). The
// rationale: a zone's spawn is an "arm on the ground" event (the spear has
// LANDED and is live), whereas an aura's application tick IS the cast itself.
// See @pit/sim advanceZones — do not "fix" this to match auras.
export const ZONE_SPAWN_SCHEMA = z.strictObject({
  kind: z.literal("zone"),
  /** Total lifetime in ticks. Must be > 0. */
  durationTicks: z.number().int().gte(1).lte(1 << 20),
  /** Ticks between firings. Must be >= 1 and <= durationTicks (checked in
   *  crossValidate against duration). */
  intervalTicks: z.number().int().gte(1).lte(1 << 20),
});

export type ZoneSpawn = z.infer<typeof ZONE_SPAWN_SCHEMA>;

export const DELIVERY_PROJECTILE_SCHEMA = z.strictObject({
  kind: z.literal("projectile"),
  /** Travel speed in world-units per tick. Must be > 0; swept collision
   *  handles fast projectiles that cross many units per tick. */
  speedUnitsPerTick: UNITS_PER_TICK_SCHEMA,
  /** Projectile body radius for the swept segment-vs-circle test. */
  radius: UNITS_SCHEMA,
  /** Which bodies can body-block the projectile off its path. */
  interceptable: INTERCEPTABLE_SCHEMA,
  /**
   * #149: projectile MOMENTUM — inherit the caster's movement velocity
   * (direction AND speed) at spawn, expressed in BASIS POINTS. Absent ⇒ 0
   * (today's behavior, byte-identical: a stand-still spell is unaffected).
   * 10000 ⇒ the caster's FULL per-tick move step is ADDED to the aim step, so a
   * shot fired while running skews toward + speeds up in the travel direction;
   * a NEGATIVE value subtracts (counter-momentum). RANGE stays FIXED (the aim
   * distance) — momentum only changes how FAST the projectile crosses its range
   * and which way it DRIFTS, so a momentum-boosted shot expires in FEWER ticks.
   * The combine is integer-only in @pit/sim `spawnProjectile`
   * (`dx' = dx + idiv(mvx·momentumBp, 10000)`) and is NOT renormalized (that
   * would strip the speed change). Bounded [-10000, 10000]; a zero move step
   * (standing still / rooted / no move target) makes it a no-op regardless.
   */
  momentumBp: z.number().int().gte(-10000).lte(10000).optional(),
  /**
   * #207: ARMING DISTANCE — the projectile is fully INERT (collides with
   * NOTHING — bodies, pillars, walls — and delivers no payload; it flies
   * THROUGH everything) until it has TRAVELLED at least this many world units
   * from its frozen spawn origin, then behaves as a normal projectile for the
   * rest of its flight. Absent ⇒ 0 ⇒ armed from spawn (byte-identical to
   * pre-#207: a projectile with no arming distance is live from tick one).
   *
   * DISTINCT from the ability's `minRangeUnits` deadzone (#125): min-range is a
   * per-BODY pass-through keyed off the body's distance from spawn (pillars and
   * walls still block in the deadzone); arming is a whole-projectile inert phase
   * keyed off the PROJECTILE's own travelled distance (nothing blocks it while
   * inert). The two are orthogonal and may combine on one projectile.
   *
   * BOUNDARY (@pit/sim `advanceProjectiles`, LOCKED = next-full-tick): armed-ness
   * is evaluated ONCE at the START of each tick from the projectile's current
   * position (squared-distance vs `armAfterUnits²`, no sqrt). The swept segment
   * that CROSSES the arming radius is flown through INERT in full; collision
   * resumes on the NEXT tick. crossValidate rejects `armAfterUnits >= rangeUnits`
   * (a projectile that could never arm within its range is a footgun).
   */
  armAfterUnits: UNITS_SCHEMA.optional(),
  /**
   * #205: mark this projectile as an INTERCEPTOR — each tick it additionally
   * tests swept collision against every OTHER in-flight projectile and, on the
   * earliest contact, DESTROYS the target (which does NOT deliver its payload).
   * Absent ⇒ NOT an interceptor (today's behavior, byte-identical: normal
   * projectiles are blind to each other, no P-vs-P cost). `teams` selects which
   * projectiles it may hit relative to its owner (default authored `enemies`);
   * `burstRadius` toggles the point (absent) vs burst (present) variant. Point:
   * the interceptor is CONSUMED on its first kill (one-for-one). Distinct from
   * `interceptable` (which gates BODY body-blocking), so no name collision.
   */
  interceptsProjectiles: INTERCEPTS_PROJECTILES_SCHEMA.optional(),
  /**
   * #44: projectile RICOCHET / bounce. Absent ⇒ no ricochet (today's behavior,
   * byte-identical: an obstacle hit consumes the projectile). Present ⇒ the
   * projectile REFLECTS off pillars + the arena edge (and, with
   * `bounceOffChampions`, off struck champion bodies) up to `maxBounces` times,
   * preserving its payload across bounces and sharing the SAME total
   * travel-distance budget (`rangeUnits`) — a bounce redirects but never extends
   * the range. A harmful payload can never self-hit the owner on a bounce; a
   * beneficial (non-hostile) one can. See @pit/sim `advanceProjectiles`.
   */
  bounce: BOUNCE_SCHEMA.optional(),
  /** #52: what the projectile leaves behind on impact/landing. Optional —
   *  absent ⇒ a plain projectile (detonates once via #51 area, or
   *  single-target). Present ⇒ spawn a persistent ground zone at the hit
   *  point. Requires the owning ability to carry an `area`. */
  onExpire: ZONE_SPAWN_SCHEMA.optional(),
  /**
   * #389: VOLLEY — number of simultaneous, fully INDEPENDENT projectiles
   * (own collision, own body-block, own damage instance — never a shared
   * hitbox) spawned by one cast/fire. Absent ⇒ 1 (today's behavior, byte-
   * identical: a single projectile straight at the resolved aim point).
   * `> 1` fans them out evenly across `spreadArc` (required whenever this is
   * `> 1`, crossValidate-enforced), centered on the resolved aim direction.
   * General delivery primitive (a Shotgun-style cone of real pellets, or a
   * boss's fan pattern) — not champion-specific. See `@pit/sim`
   * `spawnProjectileVolley` for the deterministic fan-angle math.
   */
  projectileCount: z.number().int().gte(1).lte(16).optional(),
  /**
   * #389: total angular spread of the `projectileCount` fan, in WHOLE
   * DEGREES `[0, 180]`, centered on the resolved aim direction. Only
   * meaningful alongside `projectileCount > 1` — crossValidate rejects it
   * set without `projectileCount > 1` (an inert-config footgun, same
   * discipline as `damage.perPointBonusPool` requiring `perPointBonus`).
   *
   * DELIBERATELY NOT the cone's `arcCosThresholdBp` (#128) representation:
   * that field is a bp-COSINE *membership threshold* (used only to test
   * "is this point inside the wedge", never to construct a rotated
   * direction), whereas a volley needs `projectileCount` concrete, evenly-
   * spaced ROTATED aim points — an actual angle, not a threshold. Plain
   * whole degrees is the simplest integer-native unit at the resolution the
   * table below supports and needs no bp-of-180° conversion.
   *
   * DETERMINISM: fan angles are computed via a PRECOMPUTED integer
   * cos/sin-in-basis-points table (`@pit/sim` `fanAngle.ts`), baked as
   * literal source constants exactly like `facing.ts`'s
   * `SECTOR_BOUNDARY_TANS` — no `Math.sin`/`Math.cos` call anywhere in the
   * sim, at load time or per-tick. Per-pellet offsets are ROUNDED (half away
   * from zero, symmetric) integer degrees so the fan is exactly mirror-
   * symmetric about the aim direction regardless of `projectileCount`
   * parity. Canonical spawn order is LEFT-TO-RIGHT (ascending offset, most
   * counter-clockwise pellet first) — see `spawnProjectileVolley`.
   */
  spreadArc: z.number().int().gte(0).lte(180).optional(),
});

// ─── Projectile-absorbing zones + collapse (#182) ──────────────────────────
// A GENERIC zone modifier set — the composable decomposition of "Chaos Void"
// (which is a CONTENT name; the engine exposes only these mechanic-named
// primitives). Two orthogonal, opt-in extensions of the #52 `zone` delivery:
//   • `absorb` — the zone is a projectile SINK: a shot swept into its radius is
//     consumed, and a per-strike HARVEST magnitude accumulates into the zone's
//     `absorbPool` (reused verbatim — no bespoke accumulator).
//   • `onExpire` — when the zone ENDS (duration expiry OR a #183 recast
//     re-press) it SPAWNS a projectile from its center (the dual of the
//     projectile's `onExpire: ZONE_SPAWN`), carrying a `reservoir`-mode DoT
//     whose total is SEEDED from the accumulated pool.
// Compose them and you get Chaos Void; compose `absorb{teams:allies}` + a heal
// DoT and you get a heal-well — zero engine code per ability.

/** Which projectiles a zone absorbs, RELATIVE TO THE ZONE OWNER (#182). Mirrors
 *  the `AreaTargets` team-selector vocabulary (defined inline to avoid a
 *  forward-reference — `AREA_TARGETS_SCHEMA` lives later in the file):
 *  `enemies` spares the owner's team, `allies` spares enemies, `all` eats BOTH
 *  teams' shots incl. the owner's own (chaos). Resolved against the zone's
 *  frozen `ownerTeam`. */
export const ZONE_ABSORB_TEAMS_SCHEMA = z.union([
  z.literal("enemies"),
  z.literal("allies"),
  z.literal("all"),
]);
export type ZoneAbsorbTeams = z.infer<typeof ZONE_ABSORB_TEAMS_SCHEMA>;

/**
 * What a zone HARVESTS from each absorbed projectile into its pool (#182). A
 * CONFIG-SELECTABLE STRATEGY (reserved-arm pattern, exactly like
 * `REDIRECT_STRATEGY_SCHEMA`): only `firstTickDamage` is wired at v1; the rest
 * are schema-valid but content-gated (crossValidate rejects them).
 *   firstTickDamage — RAW (pre-mitigation) first-tick damage of the shot's
 *                     payload: a `damage` effect's `amount`, or a periodic-damage
 *                     `applyAura`'s per-tick worth. v1 default + only live arm.
 *   fullImpact      — RESERVED: the payload's FULL magnitude (sum over all ticks).
 *   count           — RESERVED: +1 per absorbed shot (a counter, magnitude-blind).
 */
export const ZONE_HARVEST_SCHEMA = z.union([
  z.literal("firstTickDamage"),
  z.literal("fullImpact"),
  z.literal("count"),
]);
export type ZoneHarvest = z.infer<typeof ZONE_HARVEST_SCHEMA>;

/**
 * #182: a zone's projectile-absorption modifier. Generic, config-authored:
 *   - `teams`   — which projectiles are absorbable (relative to owner).
 *   - `harvest` — what accumulates per absorbed shot (strategy).
 *   - `pool`    — the ACCUMULATION reused verbatim from the `absorbPool` aura
 *                 primitive: `amount` is the cap, `schoolFilter` (unwired) the
 *                 filter, `mode` MUST be `reservoir` (it accumulates then feeds a
 *                 collapse DoT — never a shield; crossValidate pins this).
 */
export const ZONE_ABSORB_SCHEMA = z.strictObject({
  teams: ZONE_ABSORB_TEAMS_SCHEMA,
  harvest: ZONE_HARVEST_SCHEMA,
  pool: AURA_PRIM_ABSORB_POOL_SCHEMA,
});
export type ZoneAbsorb = z.infer<typeof ZONE_ABSORB_SCHEMA>;

/**
 * #182: what a zone SPAWNS when it ENDS (duration expiry OR a #183 recast
 * re-press) — the dual of the projectile's `onExpire: ZONE_SPAWN`. It fires ONE
 * projectile from the zone CENTER toward the collapse aim (the recast press's
 * cursor, or the owner's facing on a duration expiry), carrying a `reservoir`-
 * mode DoT (`dotDef`) applied on hit. Gated on the owner's `canAct` at collapse
 * (silence/stun/mez/fear ⇒ the zone vanishes with no bolt; a root does NOT block
 * it) — a property of THIS trigger, not a bespoke path. The DoT's total is
 * SEEDED from the zone's accumulated pool (or `baseAmount` on an empty timeout),
 * via `applyAura.absorbSeed`.
 */
export const ZONE_COLLAPSE_SCHEMA = z.strictObject({
  /** Collapse bolt travel speed (world-units/tick). > 0. */
  speedUnitsPerTick: UNITS_PER_TICK_SCHEMA,
  /** Collapse bolt body radius for swept collision. > 0. */
  radius: UNITS_SCHEMA.refine((r) => r > 0, {
    message: "zone onExpire collapse radius must be > 0",
  }),
  /** Which bodies can body-block the collapse bolt. Defaults to BOTH teams
   *  (the bolt DoTs whoever it strikes). */
  interceptable: INTERCEPTABLE_SCHEMA.default({ allies: true, enemies: true }),
  /** The DoT aura applied on hit — a `reservoir`-mode periodic-damage/heal aura
   *  (crossValidated). Its `durationTicks`/`intervalTicks` fix the DoT window +
   *  cadence; the seeded pool is paid out across its firings. */
  dotDef: AURA_DEF_ID_SCHEMA,
  /** Total the DoT deals when the pool is EMPTY (the weak base bolt on a
   *  duration-expiry timeout that absorbed nothing), so the ability always does
   *  something on timeout. Bounded `<= 1<<24` (the pool ceiling). */
  baseAmount: z.number().int().gte(0).lte(1 << 24),
});
export type ZoneCollapse = z.infer<typeof ZONE_COLLAPSE_SCHEMA>;

// #52: instant delivery that drops a zone at the resolved cast aim point
// immediately (a summoned puddle, no travel). #182: an optional projectile-
// absorbing modifier + an optional on-end projectile spawn (both generic).
export const DELIVERY_ZONE_SCHEMA = z.strictObject({
  kind: z.literal("zone"),
  durationTicks: z.number().int().gte(1).lte(1 << 20),
  intervalTicks: z.number().int().gte(1).lte(1 << 20),
  /** #182: absorb projectiles entering the zone into an `absorbPool`. Absent ⇒
   *  an ordinary zone (byte-identical to pre-#182 content). */
  absorb: ZONE_ABSORB_SCHEMA.optional(),
  /** #182: spawn a projectile when the zone ends (duration OR recast). Absent ⇒
   *  the zone just disappears (byte-identical to pre-#182 content). */
  onExpire: ZONE_COLLAPSE_SCHEMA.optional(),
});

export const DELIVERY_SCHEMA = z.discriminatedUnion("kind", [
  DELIVERY_INSTANT_SCHEMA,
  DELIVERY_PROJECTILE_SCHEMA,
  DELIVERY_ZONE_SCHEMA,
]);

export type Delivery = z.infer<typeof DELIVERY_SCHEMA>;

// ─── Area of effect (#51) ───────────────────────────────────────────────
// HOW an ability's effects reach targets: absent ⇒ single-target (today's
// behavior, byte-identical); present ⇒ a radial fan-out at the resolved
// origin point (aim point for a ground cast, target position for an entity
// cast, impact/landing point for a projectile). Orthogonal to `delivery`:
// the same block works on instant, projectile, and lock-on abilities.
//
// Deterministic by construction — the sim fans out in seat order with an
// integer `distSq <= radius²` test (see @pit/sim applyAreaEffects). Absent
// keeps the content hash of single-target bundles unchanged.
//
// `targets` is a team selector RELATIVE TO THE CASTER: "enemies" spares
// allies, "allies" spares enemies, "all" hits both. This one field yields
// friendly-fire, team heals, and free-for-all.
//
// `includeDirectHit` (projectile only): whether the body that BLOCKED the
// projectile also eats the splash. No-op for instant / ground-landing.
//
// `falloff` is RESERVED (not built): distance-scaled magnitude. Naming it
// now keeps a later add additive — no schema reshape.
//
// (`AREA_TARGETS_SCHEMA` itself now lives ABOVE the Effects section — #327's
// `dash` effect needs the same team-selector vocabulary and is declared
// alongside the other effect schemas, well before this point in the file.)

// #128: the origin/direction a cone is fired along. `casterFacing` uses the
// caster's precise #62 `FacingDir` as the cone axis; `aimPoint` uses the
// direction from the caster toward the cursor/aim point. In BOTH cases the
// cone's APEX is the caster's position (non-caster-apex cones are out of scope
// at v1).
export const AREA_CONE_ORIGIN_SCHEMA = z.union([
  z.literal("casterFacing"),
  z.literal("aimPoint"),
]);
export type AreaConeOrigin = z.infer<typeof AREA_CONE_ORIGIN_SCHEMA>;

/**
 * CIRCLE area (#51) — the default shape. `shape` is optional in JSON and
 * defaults to `"circle"`, so pre-#128 content (which authored `{ radius,
 * targets, ... }` with no `shape`) parses byte-identically. Membership is the
 * radial `distSq(body, origin) <= radius²` test.
 */
export const AREA_CIRCLE_SCHEMA = z.strictObject({
  shape: z.literal("circle").default("circle"),
  /** Splash radius in world units from the origin point. Must be > 0 — a
   *  zero-radius area is a validation error, not a silent single-target. */
  radius: UNITS_SCHEMA.refine((r) => r > 0, {
    message: "area radius must be > 0",
  }),
  /** Team selector relative to the caster. */
  targets: AREA_TARGETS_SCHEMA,
  /** Projectile-delivery only: does the blocking body also take the splash?
   *  Defaults true (the struck body is at the blast center). No-op for
   *  instant / ground-landing delivery. */
  includeDirectHit: z.boolean().default(true),
});
export type AreaCircle = z.infer<typeof AREA_CIRCLE_SCHEMA>;

/**
 * CONE area (#128) — a facing/aim-relative WEDGE. Membership is an ANNULUS
 * (`minRangeUnits² <= d² <= maxRangeUnits²`, both bounds INCLUSIVE) AND the
 * SHARED bp-cosine arc predicate around the cone axis — the EXACT same
 * `arcCosThresholdBp` representation + integer test the facing gate (#27) and
 * positional gate (#92) use (`10000·dot >= arcCosThresholdBp · isqrt(dot² +
 * cross²)`), so cone selection can never disagree with those gates about "is
 * this offset inside the arc". No trig, no normalization, no float. Any effect
 * rides a cone exactly as it rides a circle — the shape decides only WHO is
 * hit, not WHAT happens. Cones are INSTANT-delivery only (the apex is the
 * caster); crossValidate rejects a cone on projectile/zone delivery.
 *
 * `arcCosThresholdBp` is the half-angle α as a bp cosine (`round(10000·cos α)`);
 * see FACING_POLICY_SCHEMA for the reference table (10000 = 0° / dead-ahead,
 * 0 = 90° half-angle / 180° total wedge, −10000 = full circle). A WIDER cone is
 * a LOWER threshold.
 */
export const AREA_CONE_SCHEMA = z.strictObject({
  shape: z.literal("cone"),
  /** Half-angle of the wedge as a bp cosine (same representation as facing
   *  #27). Required — the ANGLE is a real design decision, no default. */
  arcCosThresholdBp: z.number().int().gte(-10000).lte(10000),
  /** Inner radius / deadzone (absent ⇒ 0, no deadzone). A body strictly inside
   *  `minRangeUnits` of the apex is excluded. crossValidate rejects
   *  `minRangeUnits >= maxRangeUnits` (an empty annulus). */
  minRangeUnits: UNITS_SCHEMA.optional(),
  /** Outer radius — the cone's reach. Must be > 0. */
  maxRangeUnits: UNITS_SCHEMA.refine((r) => r > 0, {
    message: "cone maxRangeUnits must be > 0",
  }),
  /** Fired along the caster's facing, or toward the cursor/aim point. */
  origin: AREA_CONE_ORIGIN_SCHEMA,
  /** Team selector relative to the caster. */
  targets: AREA_TARGETS_SCHEMA,
  /** Carried for a uniform fan-out loop; a no-op for a cone (an instant cone
   *  has no single "direct-hit" body). Defaults true. */
  includeDirectHit: z.boolean().default(true),
});
export type AreaCone = z.infer<typeof AREA_CONE_SCHEMA>;

// The area concept is a discriminated `shape` union (#128): a CIRCLE (the
// default) or a CONE. `z.union` (not `z.discriminatedUnion`) so a shape-less
// object still parses as a circle via the `shape` default — pre-#128 content is
// byte-identical. A cone object (`shape: "cone"`) fails the circle arm (strict
// keys + literal mismatch) and matches the cone arm.
export const AREA_SCHEMA = z.union([AREA_CIRCLE_SCHEMA, AREA_CONE_SCHEMA]);

export type Area = z.infer<typeof AREA_SCHEMA>;

/**
 * #146 item 3: the HAZARD payload of a hazard-host wall (fire / thorn / freeze
 * wall). Reuses the ground-zone firing model — `effects` re-applied to every
 * living body OVERLAPPING the wall footprint every `intervalTicks` (firing ON
 * the spawn tick, like a zone), team-gated by `targets` relative to the wall
 * owner. Referenced LAZILY from the spawnWall effect (`effects` is the full
 * `EFFECT_SCHEMA` union, which itself contains `spawnWall`, so a direct
 * reference would be a cyclic schema). The concrete `WallHazard` interface
 * annotation keeps the mutually-recursive `Effect` type well-formed.
 */
export interface WallHazard {
  readonly targets: AreaTargets;
  readonly intervalTicks: number;
  readonly effects: readonly Effect[];
}
export const WALL_HAZARD_SCHEMA: z.ZodType<WallHazard> = z.strictObject({
  /** Team selector relative to the wall owner (`enemies|allies|all`). */
  targets: AREA_TARGETS_SCHEMA,
  /** Ticks between firings (>= 1); fires ON the spawn tick then every interval
   *  (mirrors the zone cadence). crossValidate pins it <= durationTicks. */
  intervalTicks: z.number().int().gte(1).lte(1 << 20),
  /** On-contact effects (>= 1). crossValidate gates them to the MVP allowlist
   *  and rejects Phase-B-only kinds (spawnWall/blink) that need a caster + aim. */
  effects: z.array(EFFECT_SCHEMA).min(1),
});

/**
 * Ability charges (#84). `max` charges, each refilling `rechargeTicks` after
 * the prior refill. Sequential recharge (one charge per interval). Integer
 * ticks only.
 */
export const CHARGES_SCHEMA = z.strictObject({
  max: z.number().int().gte(1).lte(8),
  rechargeTicks: z.number().int().gte(1).lte(1 << 24),
});
export type Charges = z.infer<typeof CHARGES_SCHEMA>;

/** Positional requirement relative to the TARGET's facing (#92). */
export const POSITIONAL_REQUIREMENT_SCHEMA = z.union([
  z.literal("behind"),
  z.literal("front"),
  z.literal("flank"),
]);
export type PositionalRequirement = z.infer<typeof POSITIONAL_REQUIREMENT_SCHEMA>;

/** Positional gate policy (#92, arc-configurable #132): which region relative
 *  to the target's facing the attacker must occupy for the cast to start.
 *
 *  #132 makes the arc CONFIGURABLE and DEFAULTS to 180/180 hemispheres:
 *    - `arcCosThresholdBp` OMITTED ⇒ `front` = frontal hemisphere (`dot > 0`),
 *      `behind` = rear hemisphere (`dot < 0`) — pure sign tests, no cross². This
 *      is the new default (WoW-backstab "you're behind or you're not").
 *    - `arcCosThresholdBp` PRESENT ⇒ a narrower cone centered on the region's
 *      axis (front = +facing, behind = −facing, flank = perpendicular), using
 *      the SAME bp-cosine arc test as facing #27. Set it to the 90°-equivalent
 *      to reproduce the old fixed cones.
 *    - `require: "flank"` has NO default region (front+behind tile the whole
 *      circle at 180/180), so it REQUIRES an explicit `arcCosThresholdBp` — an
 *      arc-less flank is a crossValidate LOAD error. */
export const POSITIONAL_POLICY_SCHEMA = z.strictObject({
  require: POSITIONAL_REQUIREMENT_SCHEMA,
  /** Optional per-ability arc half-angle as a bp cosine (same representation as
   *  facing #27). Omit ⇒ the 180/180 hemisphere default (front/behind only;
   *  flank requires it). */
  arcCosThresholdBp: z.number().int().gte(-10000).lte(10000).optional(),
});
export type PositionalPolicy = z.infer<typeof POSITIONAL_POLICY_SCHEMA>;

/**
 * Target HP% cast-legality gate (#329, "Execute-style"). The cast is gated at
 * START: the resolved TARGET's CURRENT hp must be STRICTLY BELOW
 * `belowMaxHpPercentBp` basis points of its (aura-modified) MAX hp, else the
 * cast fizzles `hpGateNotMet`. A PERCENTAGE gate — not a flat hp value — so
 * the SAME ability threshold scales correctly across champions with
 * different maxHp pools (the deliberate design choice over a flat-value
 * shortcut; this exact question recurs for every future execute-style
 * ability, so it is solved generally here rather than per-content). Only
 * meaningful for an entity-target ability (enemy/ally) — crossValidate
 * rejects it on self/ground/none, mirroring `positionalPolicy`. Evaluated via
 * an exact integer cross-multiplication (`hp * 10000 < maxHp *
 * belowMaxHpPercentBp`) — no division, no float. Pure read: a rejection
 * leaves the hash identical, same class as range/positional/facing.
 */
export const TARGET_HP_GATE_SCHEMA = z.strictObject({
  /** `>0` (a 0 threshold could never be satisfied — dead code) and `<=10000`
   *  (100% — "always legal", a harmless but pointless config). */
  belowMaxHpPercentBp: z.number().int().gt(0).lte(10000),
});
export type TargetHpGate = z.infer<typeof TARGET_HP_GATE_SCHEMA>;

/**
 * Re-cast / toggle config (#183). Optional block that opts an ability into the
 * "press-again second stage" subsystem: casting normally leaves a persistent
 * STAGE-1 entity (v1: a ground `zone`), and a SECOND press of the SAME slot —
 * while that entity is alive / within the recast window — routes as a RECAST
 * (a free, off-GCD press that fires the stage-1 entity's end-of-life transition)
 * instead of starting a fresh cast. The concrete stage-2 behavior lives in the
 * entity's own system; this block only OPTS IN + configures the window.
 *
 * Additive + defaulted-absent: an ability WITHOUT a `recast` block parses
 * byte-identically to pre-#183 content (no content-hash change for non-users —
 * same discipline as `onExpire`/`momentumBp`/`charges`).
 *
 * `windowTicks` (OQ1) is an OPTIONAL override of the recast window. ABSENT ⇒ the
 * window is the STAGE-1 ENTITY'S LIFETIME (recast allowed any time while it is
 * alive — #182 Chaos Void uses this default). PRESENT ⇒ an independent window of
 * `windowTicks` ticks from the stage-1 resolve (may be SHORTER than the entity
 * lifetime; a longer value still ends when the entity dies, since entity death
 * auto-clears the recast handle — OQ2).
 *
 * `targetKind` (#186) selects WHAT the second press acts on. Defaults to `zone`
 * (the #183 stage-1 ground zone — byte-identical to pre-#186 content, which
 * carries no `targetKind`):
 *   - `zone`   — a zone-delivery ability's spawned stage-1 ground zone; the
 *                second press ENDS it. crossValidate pins `delivery.kind: "zone"`.
 *   - `circle` — Demonic Circle: the cast stores a recall POINT at the caster's
 *                feet, and the second press blink-recalls the caster to it
 *                (range-capped at the ability's `rangeUnits`, gated by
 *                `canAct && canMove`). No backing entity, so `windowTicks` is
 *                REQUIRED and `delivery.kind: "instant"` — both cross-validated.
 */
export const RECAST_TARGET_KIND_SCHEMA = z.union([
  z.literal("zone"),
  z.literal("circle"),
]);
export type RecastTargetKind = z.infer<typeof RECAST_TARGET_KIND_SCHEMA>;

export const RECAST_SCHEMA = z.strictObject({
  windowTicks: z.number().int().gte(1).lte(1 << 20).optional(),
  targetKind: RECAST_TARGET_KIND_SCHEMA.default("zone"),
});
export type Recast = z.infer<typeof RECAST_SCHEMA>;

/**
 * Channeled-ability config (#185). Optional block that turns an ability into a
 * CHANNEL: after it starts it runs the ability's `effects`/`delivery` as a
 * PER-TICK payload over a locked `durationTicks`, holds the caster (movement
 * gated by `movementLocked`), and ends naturally when the duration elapses — or
 * EARLY on player cancel (a move command or the stop-cast key), cast-blocking
 * CC (silence/stun/mez/fear), the `interrupt` effect, or a locked target moving
 * out of range (see @pit/sim `advanceChannels`). Realizes the reserved
 * `AIM_SNAPSHOT_SCHEMA.duringChannel` seam.
 *
 * MUTUALLY EXCLUSIVE with a non-zero `castTimeTicks` (crossValidate): an ability
 * is INSTANT (`castTimeTicks === 0`, no channel), a CAST (`castTimeTicks > 0`),
 * OR a CHANNEL (`channel` present, `castTimeTicks === 0`). The channel reuses
 * the ability's existing `effects` + `delivery` for each fire:
 *   - `projectile` delivery → spawn ONE projectile per fire toward the aim (a
 *     skillshot channel like Destruction; the aim point is re-read each fire —
 *     the frozen ground point, or a lock-on target's live position).
 *   - `instant` delivery → apply the effects to the locked target / caster each
 *     fire (a lock-on channel like Drain Life), or fan an `area` out at the aim
 *     point.
 * crossValidate rejects a `zone` delivery (a channel dropping a persistent zone
 * every interval is out of scope) and the contradictory `offCast` /
 * `castWhileMoving` / `recast` subsystems.
 *
 * Additive + defaulted-absent: an ability WITHOUT a `channel` block parses
 * byte-identically to pre-#185 content (no content-hash change for non-users —
 * same discipline as `recast`/`charges`/`onExpire`).
 *
 *   durationTicks     — total channel length (> 0). The channel is ACTIVE for
 *                       exactly this many ticks: `[startTick, startTick +
 *                       durationTicks - 1]` INCLUSIVE (the zone-lifetime
 *                       convention — `endsAtTick` is derived, not free-floating).
 *   tickIntervalTicks — fire the channel's effects every N ticks (integer pacing
 *                       via `imod(tick - startTick, N)`). Must be
 *                       `<= durationTicks` (crossValidate — a longer interval
 *                       would fire only on the start tick, almost certainly a
 *                       content bug).
 *   fireOnStart       — fire ON the start tick (delta 0)? `true` gives an
 *                       instant-on-press first fire (Destruction's first shot);
 *                       `false` waits until the first interval boundary.
 *   movementLocked    — ROOT the caster for the channel (`true`) or let it walk
 *                       while channeling (`false`)? Per-ability (OQ1) — both
 *                       must work. A move command cancels a move-LOCKED channel;
 *                       a NOT-locked channel keeps going while the caster walks.
 *   reaimPerFire      — #219: live cursor re-aim mid-channel. `false` (default) ⇒
 *                       every fire uses the FROZEN start-of-channel aim (a
 *                       skillshot barrage that stays pointed where it started); a
 *                       lock-on channel is unaffected (it always re-reads its
 *                       target). `true` ⇒ a SKILLSHOT (point-aim / `targetType:
 *                       ground`) channel re-reads the caster's CURRENT cursor aim
 *                       at each fire tick from the ordinary cursor/aim input
 *                       (Afflictor's steerable Destruction). The aim is SAMPLED
 *                       ONLY on fire ticks (no continuous per-tick aim stream, no
 *                       stored cursor) — the same recorded aim inputs reproduce
 *                       the same shots on replay. crossValidate pins it to a
 *                       skillshot channel (a lock-on channel re-reads its target,
 *                       not the cursor).
 */
export const CHANNEL_SCHEMA = z.strictObject({
  durationTicks: z.number().int().gte(1).lte(1 << 20),
  tickIntervalTicks: z.number().int().gte(1).lte(1 << 20),
  fireOnStart: z.boolean(),
  movementLocked: z.boolean(),
  // #219: opt-in live re-aim. Defaulted so pre-#219 channel content (which omits
  // it) parses byte-identically as `false` and the sim always sees a concrete
  // boolean (no nullish branch). Only a skillshot channel may set it (crossValidate).
  reaimPerFire: z.boolean().default(false),
});
export type Channel = z.infer<typeof CHANNEL_SCHEMA>;

/**
 * A standalone ability DEFINITION (#190). Abilities are a first-class content
 * registry (`abilities/*.json`, keyed by `id`) like auras — a champion holds an
 * ordered LIST of ability IDs, not inlined ability objects. This is the on-disk
 * / authoring shape (`AbilityDef`); the loader RESOLVES a champion's id-list back
 * into concrete `Ability` objects (see the resolved `Ability` type), injecting
 * each ability's `slot` = its INDEX in the champion's list (OQ1 — slot is NOT a
 * def property, so the same ability def can sit in different slots on different
 * champions; sharing is allowed, OQ3). The sim consumes the resolved `Ability[]`
 * exactly as before — the split changes only the authoring/on-disk shape + the
 * loader, never the sim's per-tick reads (so the per-tick STATE hash is
 * untouched; only the CONTENT hash moves).
 */
export const ABILITY_SCHEMA = z.strictObject({
  /** #203: content-migration version stamp (see `SCHEMA_VERSION_SCHEMA`). */
  schemaVersion: SCHEMA_VERSION_SCHEMA,
  /** #190: opaque minted `abl_<nanoid>` id — the registry key + the champion's
   *  reference target. */
  id: ABILITY_ID_SCHEMA,
  /** #190: rename-safe human slug (the opaque `id` is the stable identity),
   *  mirroring `AURA_DEF_SCHEMA`/`CHAMPION_SCHEMA`. */
  slug: SLUG_SCHEMA,
  name: z.string().min(1).max(64),
  /**
   * #339: authored, player-facing tooltip text (name/damage/effect summary,
   * e.g. "Deals 150 fire damage." / "Heals an ally for 180 over time.").
   * PURELY COSMETIC — never read by the sim (no state-hash or behavior
   * impact) or by `crossValidate`'s gating logic; it exists solely so the
   * client's action-bar hover tooltip (`apps/client/src/ui/ActionBar.tsx`)
   * has real ability text to show instead of just the name.
   *
   * PLAIN `.optional()` (no `.default(...)`), unlike `aimSnapshot`/`delivery`
   * above — those default because SIM code branches on them every tick and
   * needs a concrete value; `description` is never read by sim code (no
   * nullish-check risk there), only by client-side presentation, which
   * already treats "absent" the same as "empty" (falls back to a name-only
   * tooltip). A `.default("")` would additionally make `description` a
   * REQUIRED field on the resolved `Ability` type (`Omit<AbilityDef, ...>`),
   * forcing every hand-built `Ability` fixture across `@pit/sim`'s test
   * suite (which never goes through this schema's parse) to add a value it
   * has no use for — `.optional()` keeps those fixtures byte-identical.
   * Every shipped ability in `packages/content/data/abilities/*.json` still
   * authors a real, non-empty `description`.
   */
  description: z.string().max(280).optional(),
  /**
   * Spell schools (#100). A NON-EMPTY set of the schools this ability belongs
   * to. REQUIRED — there is no default, because an ability's school set is a
   * real design decision (a silent default would ship wrong data). Multi-school
   * is allowed (e.g. `["frost","arcane"]`). Order is content-authored but the
   * value is semantically a SET: it does NOT affect the state hash or the
   * interrupt-lockout behavior (the sim indexes each school by the fixed
   * `SCHOOL_SCHEMA` order). Only interrupt-lockout consumes `schools` at MVP;
   * school-based resist/immunity/dispel are follow-ups that will READ it.
   */
  schools: z.array(SCHOOL_SCHEMA).min(1),
  targetType: TARGET_TYPE_SCHEMA,
  castTimeTicks: z.number().int().gte(0).lte(1 << 20),
  cooldownTicks: z.number().int().gte(0).lte(1 << 24),
  gcd: z.boolean(),
  /** Cost is content-gated (0 at MVP). Non-negative int for future
   * resource systems (mana/rage/energy...) — schema exists day one. */
  cost: z.number().int().gte(0).lte(1 << 20),
  /**
   * #637 4a: `resolveCastAim`'s `"self"` case (@pit/sim `cast.ts`) never reads
   * `rangeUnits` — a self-targeted ability has no aim point to range-check
   * against. Defaulted (not required) so self-targeted content can omit the
   * field entirely instead of authoring a dead value; every other
   * `targetType` still authors a real range explicitly. Absent ⇒ 0, matching
   * the "no dead-config surface" discipline `minRangeUnits`/`cost` already use.
   */
  rangeUnits: UNITS_SCHEMA.default(toUnits(0)),
  /**
   * #125: optional MINIMUM range / deadzone. Absent ⇒ 0 (no deadzone — every
   * ability is usable from distance 0, byte-identical to pre-#125 content).
   * Present ⇒ the usable band is `[minRangeUnits, rangeUnits]` INCLUSIVE:
   *   - entity/self target, NON-projectile delivery → a hard cast gate:
   *     `distSq(caster,target) < minRangeUnits²` rejects with `tooClose`
   *     (strict `<`, so exactly-at-`minRangeUnits` is allowed — the mirror of
   *     the max gate's strict `>`).
   *   - projectile delivery → an ARMING distance: the projectile always fires
   *     but is inert (deals no damage/effect) until it has travelled past
   *     `minRangeUnits` from its spawn origin (see @pit/sim projectile.ts).
   * crossValidate rejects `minRangeUnits >= rangeUnits` (an uncastable band).
   * Integer-exact, squared-distance compare (no sqrt) — the mirror of the
   * existing max-range `outOfRange` predicate.
   */
  minRangeUnits: UNITS_SCHEMA.optional(),
  /** Per-ability line-of-sight policy — the cast machine only reads
   * this object, never inlines LoS calls. See LOS_POLICY_SCHEMA. */
  losPolicy: LOS_POLICY_SCHEMA,
  effects: z.array(EFFECT_SCHEMA).min(0),
  /** Delivery mechanism. Optional in content JSON — defaults to `instant`
   *  so existing content + goldens are unaffected. The parsed Ability
   *  ALWAYS carries a concrete `delivery` (never undefined) so downstream
   *  sim code branches on `delivery.kind` without a nullish check. */
  delivery: DELIVERY_SCHEMA.default({ kind: "instant" }),
  /**
   * Aim-freeze timing (#36). Defaults to `"start"` (commit aim on press).
   * Applies to both lock-on and skillshot casts; no-op for instant casts.
   * The parsed Ability always carries a concrete value so sim code branches
   * without a nullish check.
   */
  aimSnapshot: AIM_SNAPSHOT_SCHEMA.default("start"),
  /**
   * Area-of-effect fan-out (#51). Optional — absent ⇒ single-target,
   * byte-identical to pre-#51 content. Present ⇒ effects apply to every
   * selected unit within `area.radius` of the resolved origin point.
   * Orthogonal to `delivery`; see AREA_SCHEMA.
   */
  area: AREA_SCHEMA.optional(),
  /**
   * Ability charges (#84). Absent ⇒ single-cooldown behavior (today), and
   * the entity's charge state carries no entry for this slot — byte-identical
   * to pre-#84 content in the hash. Present ⇒ the ability holds up to `max`
   * charges, each refilling `rechargeTicks` after the previous refill; a cast
   * consumes one charge if any are available. `cooldownTicks` is ignored for
   * a charged ability (the charge model is the single readiness source);
   * cross-validation requires `cooldownTicks === 0` when `charges` is set.
   */
  charges: CHARGES_SCHEMA.optional(),
  /**
   * Off-cast ability (#86). Default false. When true, this ability may be
   * used WHILE the caster has an in-progress cast/channel, and it resolves
   * WITHOUT touching that active cast (progress, target, completion all
   * unchanged). Constrained to instant delivery with castTimeTicks === 0
   * (cross-validated) — a mid-cast ability with its own cast time is out of
   * scope. Off-cast abilities are off-GCD by construction (the field stays
   * orthogonal, but cross-validation requires gcd === false).
   */
  offCast: z.boolean().optional(),
  /**
   * Positional gate (#92). Optional — absent ⇒ no positional constraint
   * (byte-identical to pre-#92 content). Present ⇒ the cast is gated at START
   * on the ATTACKER's position relative to the TARGET's facing:
   *   - `behind` — attacker is behind the target (facingDot < 0, within the
   *     rear 90° quadrant). Backstab.
   *   - `front`  — attacker is in the target's frontal 90° quadrant.
   *   - `flank`  — attacker is off to the side (more perpendicular than
   *     front/back).
   * Distinct from a caster-facing gate: this reads the TARGET's facing, so it
   * only applies to entity-target (enemy/ally) abilities. No hashed-state
   * change — a purely gating read at cast start.
   */
  positionalPolicy: POSITIONAL_POLICY_SCHEMA.optional(),
  /**
   * #329: target HP% cast-legality gate ("Execute-style"). Optional — absent
   * ⇒ no HP gate (byte-identical to pre-#329 content). See
   * `TARGET_HP_GATE_SCHEMA`.
   */
  targetHpGate: TARGET_HP_GATE_SCHEMA.optional(),
  /**
   * Per-ability facing policy (#27). Optional — absent ⇒ the LEGACY facing
   * behavior: the caster snaps to face the target on cast start and facing is
   * NEVER a gate (byte-identical to pre-#27 content). Present ⇒ the cast is
   * gated at START (`requireAtCastStart`) and/or RESOLVE (`requireAtResolve`)
   * on the CASTER's facing toward the target within an arc, and the cast-start
   * snap becomes opt-in via `snapToTargetOnUse`. See FACING_POLICY_SCHEMA.
   * Distinct from `positionalPolicy` (which reads the TARGET's facing).
   */
  facingPolicy: FACING_POLICY_SCHEMA.optional(),
  /**
   * #93: allow the caster's AUTO-ATTACK (#93 `weapon`) to fire while THIS
   * ability is casting. Default (omit/false) ⇒ the auto is SUPPRESSED for the
   * ability's whole cast — the common case (a hard-cast fireball blocks a wand
   * weave). `true` ⇒ the auto's swing timer keeps advancing and can land
   * mid-cast (e.g. a hunter's Steady Shot lets an auto weave through). A
   * per-ABILITY opt-in: the special case lives on the rare ability, not the
   * weapon. Pure gating read on the auto track — adds NO hashed state.
   */
  allowAutoWhileCasting: z.boolean().optional(),
  /**
   * #154: cast while moving. Default (omit/false) ⇒ starting a cast ROOTS the
   * caster — a directional (`moveDir`/WASD) movement command cancels the
   * cast per `tuning.movingCancelsCast` — the historical behavior, byte-
   * identical to pre-#154 content. `true` ⇒ the cast does NOT root: the caster
   * KEEPS moving via its held `moveDir` intent through the cast (it moves AND
   * casts via the shared movement path) WITHOUT cancelling the cast, and the
   * resolve-tick projectile inherits the caster's LIVE movement momentum
   * (#149, off by default via `momentumBp: 0`). Being DISPLACED by a
   * `displace` effect STILL cancels the cast (a hard shove wins); an
   * incidental push never cancels. Pure gating on the movement/cast paths —
   * adds NO hashed state (reuses `castState`).
   */
  castWhileMoving: z.boolean().optional(),
  /**
   * #183: re-cast / toggle config. Optional — absent ⇒ an ordinary one-shot
   * ability (byte-identical to pre-#183 content). Present ⇒ the ability opts
   * into the press-again second-stage subsystem (see RECAST_SCHEMA). v1 requires
   * `delivery.kind === "zone"` (the stage-1 entity is the spawned ground zone) —
   * cross-validated in `crossValidate`.
   */
  recast: RECAST_SCHEMA.optional(),
  /**
   * #185: channeled-ability config. Optional — absent ⇒ an ordinary instant/
   * cast ability (byte-identical to pre-#185 content). Present ⇒ the ability is
   * a CHANNEL that fires its `effects`/`delivery` per-tick over a locked
   * duration (see CHANNEL_SCHEMA). MUTUALLY EXCLUSIVE with a non-zero
   * `castTimeTicks` and with `offCast`/`castWhileMoving`/`recast`/`zone`
   * delivery — all cross-validated in `crossValidate`.
   */
  channel: CHANNEL_SCHEMA.optional(),
  /**
   * #389: UNINTERRUPTIBLE cast flag. Default (omit/false) ⇒ today's behavior,
   * byte-identical: an `interrupt` effect (#100) cancels this ability's
   * in-progress cast/channel exactly as it does for any other non-physical-
   * school ability. `true` ⇒ the `interrupt` EFFECT's resolution (`applyInterrupt`
   * in @pit/sim effects.ts) is a FULL NO-OP against a caster currently
   * casting/channeling THIS ability: neither the cast-cancel NOR the per-school
   * lockout apply (the interrupt fully "missed") — the exact same full-no-op
   * shape the engine already gives a purely-`physical`-school ability (see
   * `applyInterrupt`'s `hasNonPhysical` check), just opt-in per ability instead
   * of implied by school. Chosen over an implicit "physical is engine-wide
   * uninterruptible" rule so a future NON-physical ability (e.g. Aimed Shot,
   * #387/#388) can opt in explicitly without hardcoding school semantics.
   *
   * ORTHOGONAL to CC: stun/mez/fear still break an uninterruptible cast/channel
   * normally — those route through `breakCastOrChannel` directly from the
   * `stun`/`displace` effect arms (a SEPARATE call site from `applyInterrupt`),
   * never gated by this flag. "Not interruptible, but stoppable by stun/CC"
   * (#387's Aimed Shot spec) falls out of this flag touching ONLY the
   * `interrupt` effect's resolution.
   *
   * Pure gating read in `applyInterrupt` — adds NO hashed state (no new
   * Entity/state field; the flag lives on content, read at interrupt-resolve
   * time exactly like `ability.schools` already is).
   */
  uninterruptible: z.boolean().optional(),
});

/**
 * The on-disk / authoring ability def (#190) — inferred from `ABILITY_SCHEMA`.
 * Carries `id` + `slug` but NO `slot` (slot is positional, injected at
 * bundle-load). Standalone content: authored as `abilities/*.json`, referenced by
 * champions via id, validated internally (ability-level) independent of any
 * champion.
 */
export type AbilityDef = z.infer<typeof ABILITY_SCHEMA>;

/**
 * A RESOLVED ability (#190) — the shape the sim/render consume via
 * `Champion.abilities`. The loader produces these by resolving a champion's
 * ability id-list: each is an `AbilityDef` with its `slot` INJECTED (slot = the
 * ability's index in the referencing champion's id-list, OQ1). The sim reads
 * `ability.slot` (cooldown/charge attribution, cast events) + the behavioral
 * fields exactly as before the split — so NO sim-layer change was needed.
 *
 * `id`/`slug` are OPTIONAL here (not on `AbilityDef`, where they are REQUIRED):
 * the LOADER always carries them through from the def (`{ ...def, slot }`), but
 * the sim NEVER reads a resolved ability's identity — only its `slot` +
 * behavior — so hand-built sim/test champions may omit them. The single reader
 * of a resolved ability's `id` is the content hash's `bundleToView`, which only
 * ever runs over loader-produced champions (where `id` is present). `AbilitySlot`
 * stays branded.
 */
export type Ability = Omit<AbilityDef, "id" | "slug"> & {
  readonly slot: AbilitySlot;
  readonly id?: AbilityId;
  readonly slug?: string;
};

// ─── Champions ─────────────────────────────────────────────────────────────

/**
 * #453: an interval-fire HP regen rate — "heal `amount` every `intervalTicks`
 * ticks", NOT a per-tick trickle. A flat per-tick add (mirroring #89's
 * `regenPerTick`) can't express "1 HP / 5s" as a whole integer at 30 Hz
 * (1 HP/tick × 30 ticks/s = 30 HP/s, 30x too fast); this shape instead reuses
 * the SAME "every Nth tick" firing clock the periodic-aura/zone/wall-hazard
 * primitives already use (`imod(delta, intervalTicks) === 0`), which is the
 * established integer-only, no-fractional-state convention for a sub-1-per-
 * tick rate elsewhere in the sim. `amount` is a plain bounded integer (NOT
 * the `Hp` brand) — it is a per-firing RATE, the same shape as
 * `RESOURCE_DEF_SCHEMA.regenPerTick`, not a content-authored damage/heal
 * payload — `0` is a valid, explicit "no regen" override.
 */
export const CHAMPION_HP_REGEN_SCHEMA = z.strictObject({
  intervalTicks: z.number().int().gte(1).lte(1 << 20),
  amount: z.number().int().gte(0).lte(1 << 20),
});
export type ChampionHpRegen = z.infer<typeof CHAMPION_HP_REGEN_SCHEMA>;

export const CHAMPION_STATS_SCHEMA = z.strictObject({
  maxHp: HP_SCHEMA,
  moveSpeed: UNITS_PER_TICK_SCHEMA,
  /** Body radius (for body-blocking + collision + range center-to-center). */
  radius: UNITS_SCHEMA,
  /** Mass, for walk-push + weighted knockbacks (#28). Relative scale;
   *  only ratios matter. Heavier units win ground contests. */
  weight: WEIGHT_SCHEMA,
  /**
   * #121: base armor — physical-school damage mitigation expressed directly in
   * BASIS POINTS of reduction (armor 2500 = 25% less physical damage), capped
   * at `Tuning.armorMaxReductionBp` when applied. Optional — absent ⇒ 0 (no
   * mitigation, byte-identical to pre-#121 champions: armor is DERIVED at
   * damage time from this base + aura `statMod`s, so a 0-armor champion is
   * inert and adds no hashed state). Aura-modifiable via `statMod`
   * (add/addBp), floored at 0 by `getStat`.
   */
  armor: z.number().int().gte(0).lte(10000).optional(),
  /**
   * #453: per-champion override of the global passive HP regen rate
   * (`Tuning.hpRegenIntervalTicks` / `Tuning.hpRegenAmount`). Optional —
   * absent ⇒ inherits the global pair verbatim (byte-identical to "no
   * override" for every currently-shipped champion, since none sets this
   * yet). Present ⇒ BOTH fields are taken from this override together (no
   * mix-and-match with the global default) — a champion wanting only a
   * different amount at the SAME cadence still authors both fields
   * explicitly. An explicit `amount: 0` opts a champion OUT of passive
   * regen entirely (a true no-op, same convention as `regenPerTick === 0`
   * on `RESOURCE_DEF_SCHEMA`).
   */
  hpRegen: CHAMPION_HP_REGEN_SCHEMA.optional(),
});

/**
 * #542: an interval-fire, BIDIRECTIONAL resource change rate — "apply
 * `amount` (signed) every `intervalTicks` ticks", the exact structural twin
 * of #453's `CHAMPION_HP_REGEN_SCHEMA`, generalized to either direction.
 * "2 rage / 2s" at the confirmed 30 Hz `tickRate` is 2 rage per 60 ticks —
 * not expressible as a whole-integer flat per-tick rate (`regenPerTick`-style
 * would need a fractional-per-tick rate), so this reuses the SAME "every Nth
 * tick" firing clock instead of inventing a sub-unit accumulator.
 *
 * `amount` is SIGNED: negative drains (#502's original `decay` behavior,
 * e.g. Warrior's rage decay), positive generates (#540's "generates rage
 * over time" need — the capability this supersedes #502's drain-only field
 * to add). `amount === 0` is a valid, explicit no-op (same convention as
 * `RESOURCE_DEF_SCHEMA.regenPerTick`).
 *
 * #542: replaces #502's drain-only `RESOURCE_DECAY_SCHEMA` (removed, not
 * kept alongside this) — a pure sign-generalization of the same shape, not a
 * new parallel mechanism.
 */
export const RESOURCE_CHANGE_OVER_TIME_SCHEMA = z.strictObject({
  intervalTicks: z.number().int().gte(1).lte(1 << 20),
  amount: z.number().int().gte(-(1 << 20)).lte(1 << 20),
});
export type ResourceChangeOverTime = z.infer<typeof RESOURCE_CHANGE_OVER_TIME_SCHEMA>;

/**
 * #547: an event-fired resource GAIN — "add `amount` to the pool's own
 * holder's balance every time that holder takes a real hit" (the victim's
 * leg of rage-from-combat; #324's `gainResource` weapon effect already
 * covers the attacker's leg — "gain rage from DEALING damage"). Deliberately
 * NOT an aura primitive (see the doc comment on `RESOURCE_DEF_SCHEMA.onHitTaken`
 * below for why): declared directly on the resource pool def, the same
 * "champion-innate, not a temporary/dispellable buff" shape as `change`
 * (#542/#543). `amount` is a plain positive integer, the same bounds/shape as
 * `EFFECT_GAIN_RESOURCE_SCHEMA.amount` (this IS a `gainResource`-equivalent
 * magnitude, just fired from a different site) — no signed/drain direction
 * here (a resource-pool holder never LOSES balance from taking a hit; that's
 * a distinct, unauthored mechanic if ever needed).
 */
export const RESOURCE_GAIN_ON_HIT_TAKEN_SCHEMA = z.strictObject({
  /** Resource gained per qualifying hit taken; `>= 1`. */
  amount: z.number().int().gte(1).lte(1 << 16),
});
export type ResourceGainOnHitTaken = z.infer<typeof RESOURCE_GAIN_ON_HIT_TAKEN_SCHEMA>;

/**
 * Castable resource pool (#89) — mana / rage / energy. Optional on a champion;
 * absent ⇒ the champion has NO pool and every ability's `cost` must be 0
 * (crossValidate). Present ⇒ the champion spawns with a FULL pool by default
 * (`current = max`, unless `startingAmount` overrides it — #502), each
 * ability's `cost` is gated + deducted on cast, and `regenPerTick` refills
 * toward `max` every active tick.
 *
 *   id             — branded label naming the pool (energy/rage/...). A
 *                    content key; the runtime balance lives on
 *                    `Entity.resource`.
 *   max            — pool ceiling (≥ 1; crossValidate).
 *   regenPerTick   — integer units regenerated per active tick (≥ 0). 0 ⇒ a
 *                    non-regenerating pool (rage-like spend-only until
 *                    refunded by other means). Regen never overshoots `max`.
 *   startingAmount — #502: optional spawn-value override (≥ 0; validated
 *                    `<= max` in crossValidate, the same coupling shape as
 *                    `cost <= resource.max`). Absent ⇒ spawns FULL
 *                    (`current = max`), byte-identical to every pre-#502
 *                    resource pool. Present ⇒ spawns at exactly this value
 *                    instead — a Warrior wanting rage to start empty
 *                    authors `startingAmount: 0`.
 *   change         — #542: optional passive, interval-fire, BIDIRECTIONAL
 *                    rate (see `RESOURCE_CHANGE_OVER_TIME_SCHEMA`) — negative
 *                    `amount` drains (#502's original `decay`, now
 *                    superseded), positive `amount` generates. Absent ⇒ no
 *                    passive change, byte-identical to every pre-#502
 *                    resource pool.
 *   onHitTaken     — #547: optional EVENT-fired gain (see
 *                    `RESOURCE_GAIN_ON_HIT_TAKEN_SCHEMA`) — the pool's own
 *                    holder gains `amount` every time it takes a REAL hit
 *                    (post-absorb, post-clamp `hp` loss > 0 — the same gate
 *                    the sim's `hitTaken` SimEvent already fires on; a fully
 *                    shielded/zero-damage hit grants nothing). Deliberately a
 *                    direct field on this def, NOT an aura: an innate-at-spawn
 *                    aura would force an answer to "does this show up as a
 *                    permanent buff icon?" where both answers are wrong (UI
 *                    clutter for a non-temporary game rule, vs. bolting on
 *                    aura-invisibility machinery just to hide it) — this
 *                    schema field is already the champion-specific attachment
 *                    point (a resource-less champion has none, so the field
 *                    is inert for anyone without a matching pool), exactly
 *                    mirroring `change`'s "not a new parallel mechanism"
 *                    precedent. Scaled by the holder's own `resourceGainBp`
 *                    (#548) at apply time, same as any other `gainResource`-
 *                    shaped application. Absent ⇒ no event-fired gain,
 *                    byte-identical to every pre-#547 resource pool.
 */
export const RESOURCE_DEF_SCHEMA = z.strictObject({
  id: RESOURCE_ID_SCHEMA,
  max: z.number().int().gte(1).lte(1 << 20),
  regenPerTick: z.number().int().gte(0).lte(1 << 20),
  startingAmount: z.number().int().gte(0).lte(1 << 20).optional(),
  change: RESOURCE_CHANGE_OVER_TIME_SCHEMA.optional(),
  onHitTaken: RESOURCE_GAIN_ON_HIT_TAKEN_SCHEMA.optional(),
});
export type ResourceDef = z.infer<typeof RESOURCE_DEF_SCHEMA>;

/**
 * Builder/spender POINT pool (#90) — combo points / holy power / runes / chi.
 * A secondary resource DISTINCT from `RESOURCE_DEF_SCHEMA` (energy/mana): a
 * point pool starts EMPTY (`current = 0`), is grown by `buildPoints` effects
 * (clamped to `max`), consumed whole by `spendPoints`, and read by
 * `damage.perPointBonus` / `applyAura.durationPerPoint` scaling hooks. Optional
 * on a champion; a champion may declare BOTH a `resource` AND `points` (they
 * compose — energy funds the cast, points scale the finisher). The runtime
 * balance lives on `Entity.points.current`; `max` is static content, looked up
 * (not hashed) each tick.
 *
 *   id  — branded label naming the pool (combo/holyPower/...). A content key.
 *   max — pool ceiling (≥ 1; crossValidate). Combo points = 5, holy power = 3/5.
 */
export const POINTS_DEF_SCHEMA = z.strictObject({
  id: POINTS_ID_SCHEMA,
  max: z.number().int().gte(1).lte(1 << 12),
});
export type PointsDef = z.infer<typeof POINTS_DEF_SCHEMA>;

// ─── Auto-attack weapon (#93) ────────────────────────────────────────────────
// A recurring weapon attack the sim AUTO-CASTS on a swing timer while
// conditions hold — melee (instant) OR ranged/wand (projectile) via ONE path.
// Optional on a champion; absent ⇒ no auto-attack (byte-identical to pre-#93
// content). The sim DESUGARS this block into an internal auto-attack ability
// occupying a RESERVED slot (authors never hand-write the auto ability; they
// fill `weapon`), which resolves through the normal cast/effect pipeline — so
// schools / armor (#121) / shields (#103) / projectile delivery / facing (#27)
// / LoS all apply for free. The auto runs on its OWN parallel, off-GCD
// swing-timer track (`Entity.autoCastState` in @pit/sim), separate from the
// main `castState`; it costs no resource and never touches the GCD.

/** Auto-attack delivery: melee (instant, applied inline at swing resolve) vs
 *  ranged/wand (projectile, spawns a travelling body). The ONE knob that
 *  distinguishes melee from ranged autos — everything else is the same path. */
export const WEAPON_DELIVERY_SCHEMA = z.union([
  z.literal("instant"),
  z.literal("projectile"),
]);
export type WeaponDelivery = z.infer<typeof WEAPON_DELIVERY_SCHEMA>;

/** The projectile parameters for a ranged/wand auto. REQUIRED iff
 *  `weapon.delivery === "projectile"` (crossValidate); ABSENT for melee. */
export const WEAPON_PROJECTILE_SCHEMA = z.strictObject({
  /** Travel speed in world-units per tick. Must be > 0 (crossValidate). */
  speedUnitsPerTick: UNITS_PER_TICK_SCHEMA,
  /** Projectile body radius for the swept segment-vs-circle test. */
  radius: UNITS_SCHEMA,
  /** Which bodies can body-block the shot. Defaults to BOTH teams (standard
   *  body-block) so a minimal weapon block need only give speed + radius. */
  interceptable: INTERCEPTABLE_SCHEMA.default({ allies: true, enemies: true }),
  /** #149: projectile momentum for a ranged/wand auto (same meaning + bounds as
   *  the ability delivery's `momentumBp`). Absent ⇒ 0 (a wand fired while
   *  strafing is unaffected). A moving hunter's auto inherits its move velocity
   *  through the SHARED `spawnProjectile`. Bounded [-10000, 10000]. */
  momentumBp: z.number().int().gte(-10000).lte(10000).optional(),
});
export type WeaponProjectile = z.infer<typeof WEAPON_PROJECTILE_SCHEMA>;

/**
 * The `weapon` block (#93). One per champion; each class authors its own
 * ("hunter auto", "fire-mage auto") — same shape, unique values, NO per-class
 * engine code.
 *
 *   swingTicks     — the auto's INVISIBLE cast time / swing cadence (>= 1). Also
 *                    the weave gate: an idle auto still takes this long to land,
 *                    so weaving a wand between spells is INTENTIONAL, not free.
 *   damage         — flat hit magnitude (one `damage` effect through the pipeline;
 *                    HP_SCHEMA ⇒ >= 0).
 *   range          — max reach center-to-center (melee ~ contact; wand/bow long).
 *                    >= 1 (crossValidate); mirrors an ability's `rangeUnits`.
 *   delivery       — "instant" (melee) or "projectile" (ranged/wand).
 *   projectile     — REQUIRED iff `delivery === "projectile"` (crossValidate);
 *                    ABSENT for melee (crossValidate rejects a stray block).
 *   schools        — #100 school set (physical melee vs arcane wand …); drives
 *                    the armor interaction automatically via the damage arm.
 *                    Non-empty.
 *   requireFacing  — must the attacker be facing the target (within the default
 *                    facing arc) at swing resolve? melee true; ranged
 *                    per-champion (#27 arc).
 *   requireStationary
 *                  — #743: must the WIELDER be standing still to swing? Default
 *                    `false` ⇒ byte-identical to every pre-#743 weapon (an auto
 *                    swings while its owner kites, the shipped behavior since
 *                    #93). `true` ⇒ the swing gate ALSO fails whenever the
 *                    wielder holds directional movement intent (#252/#563 WASD,
 *                    the sim's only self-propelled movement source), so a
 *                    moving wielder cannot land an auto and — per the LOCKED
 *                    fizzle-resets-the-swing rule — a wielder who moves
 *                    mid-windup loses the whole windup, not just this tick.
 *                    A general weapon property, NOT a per-champion special
 *                    case: `@pit/sim`'s `autoSwingTargetSeat` reads THIS flag,
 *                    never a champion id. INTENT, not displacement — a
 *                    knockback/dash/charge/fear shove (someone else's
 *                    authority) never gates the swing, and a wielder holding a
 *                    key while rooted or walled DOES gate (they are trying to
 *                    move). See `autoAttack.ts`'s gate for the full rationale.
 *   minRangeUnits  — optional deadzone (#125). instant ⇒ a hard swing gate (too
 *                    close ⇒ the swing fizzles); projectile ⇒ an arming distance
 *                    (the shot fires but is inert until past it). < range
 *                    (crossValidate).
 *   effects        — #325: additional effects fired on EVERY successful
 *                    swing-hit, AFTER the flat `damage` above — same
 *                    array-order effect-application semantics as an
 *                    ability's `effects` (applied via the SAME `applyEffect`
 *                    router). Optional — absent ⇒ byte-identical to
 *                    pre-#325 weapons (only the flat `damage` lands). The
 *                    ADDITIVE path (LOCKED, #325): auto-attack stays "one
 *                    flat damage effect plus an optional effects tail", NOT
 *                    "auto-cast an authored ability" — no redesign of the
 *                    auto-attack desugar. Lets a swing apply an aura (a
 *                    slow-on-hit) AND/OR drive a resource-gain effect
 *                    (#324's `gainResource`) from one shipped weapon block.
 *                    `blink`/`spawnWall` are REJECTED (crossValidate) — both
 *                    resolve only via a top-level scan of an ABILITY's own
 *                    `effects` in the cast machine's Phase B, which a weapon
 *                    swing has neither the resolved aim point nor the
 *                    wall-spawn collector for.
 */
export const WEAPON_SCHEMA = z.strictObject({
  swingTicks: z.number().int().gte(1).lte(1 << 20),
  damage: HP_SCHEMA,
  range: UNITS_SCHEMA,
  delivery: WEAPON_DELIVERY_SCHEMA,
  projectile: WEAPON_PROJECTILE_SCHEMA.optional(),
  schools: z.array(SCHOOL_SCHEMA).min(1),
  requireFacing: z.boolean(),
  /** #743: `.default(false)` (not `.optional()`) so `Weapon.requireStationary`
   *  is ALWAYS a concrete boolean in the sim — the gate branches on a value,
   *  never on a nullish check. Omitting it in content is the pre-#743
   *  behavior. */
  requireStationary: z.boolean().default(false),
  minRangeUnits: UNITS_SCHEMA.optional(),
  effects: z.array(EFFECT_SCHEMA).optional(),
});
export type Weapon = z.infer<typeof WEAPON_SCHEMA>;

// ─── Conditional slot resolution (#76) ──────────────────────────────────────
// A slot's resolved ability can depend on the CASTER's CURRENT hashed state
// rather than being a fixed mapping — first concrete case: Rogue's Backstab
// slot resolves to Ambush while the caster holds Stealth. A CLOSED condition
// vocabulary (extend by adding a new typed variant only, never a free-form
// expression) + a total (`default`), array-ordered (`rules`, first match
// wins) rule list. `resolveSlotAbility` (@pit/sim `conditionalSlot.ts`) is the
// pure, deterministic reader; this is only the content SHAPE.

/**
 * #76: the ONE condition kind v1 needs — is the well-known `aura` currently
 * active (unexpired) on the caster? A pure presence/absence check against a
 * single well-known aura def id, mirroring the bundle-free, expiry-only gate
 * `isTargetableBy` already uses via `STEALTH_AURA_DEF_ID` (@pit/sim
 * `targeting.ts`'s `isStealthed`). `{ kind: "always" }` is an explicit,
 * unconditional fallthrough — never required at v1 (`ConditionalSlot.default`
 * already provides a total fallback) but kept in the union so a future rule
 * can be authored unconditionally without inventing a new vocabulary.
 */
export const SLOT_CONDITION_SCHEMA = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("holdsAura"), aura: AURA_DEF_ID_SCHEMA }),
  z.strictObject({ kind: z.literal("always") }),
]);
export type SlotCondition = z.infer<typeof SLOT_CONDITION_SCHEMA>;

/**
 * One conditional rule: WHEN `when` holds, USE this ability id instead of the
 * slot's normally-bound ability. `ConditionalSlot.rules` is evaluated in
 * ARRAY order — first match wins (an authored order, never a runtime sort —
 * same discipline as every other deterministic fan-out in the sim).
 */
export const CONDITIONAL_SLOT_RULE_SCHEMA = z.strictObject({
  when: SLOT_CONDITION_SCHEMA,
  use: ABILITY_ID_SCHEMA,
});
export type ConditionalSlotRule = z.infer<typeof CONDITIONAL_SLOT_RULE_SCHEMA>;

/**
 * #76: a state-conditional override for ONE ability slot. `rules` is
 * evaluated in order (first match wins); `default` is the ability used when
 * NO rule matches. `default` is REQUIRED to equal the id already bound to
 * `slot` in the champion's `abilities` id-list (crossValidate) — this
 * guarantees the resolver's no-match path is byte-identical to the pre-#76
 * unconditional `abilityOnSlot` read (no divergence between "the ability this
 * slot normally holds" and "what the conditional default falls back to").
 * `rules` non-empty — a `ConditionalSlot` with zero rules is a pointless
 * no-op (content should simply not declare one for that slot).
 */
export const CONDITIONAL_SLOT_SCHEMA = z.strictObject({
  slot: ABILITY_SLOT_SCHEMA,
  rules: z.array(CONDITIONAL_SLOT_RULE_SCHEMA).min(1),
  default: ABILITY_ID_SCHEMA,
});
export type ConditionalSlot = z.infer<typeof CONDITIONAL_SLOT_SCHEMA>;

/**
 * #617: named WoW-class-color palette KEY. This is a reference into the class
 * color palette (`CLASS_COLORS` in the client's
 * `apps/client/src/champions/championColors.ts`) — NOT a raw hex/numeric
 * value. `@pit/protocol` has no rendering concern (no palette hex values live
 * here); this enum only pins the FIXED SET of class-archetype names a
 * champion's `classColorKey` may reference, so a content author can't pick an
 * arbitrary out-of-palette color that breaks the "WoW class color reads a
 * unit's kit at a glance" convention `championColors.ts`'s header commits to.
 * Keep this literal list in sync with `CLASS_COLORS`'s keys — the client
 * enforces that with a type-level `satisfies Record<ClassColorKey, number>`
 * check on its palette object, so a drift is a compile error, not a runtime
 * surprise.
 */
export const CLASS_COLOR_KEY_SCHEMA = z.enum([
  "deathKnight",
  "demonHunter",
  "druid",
  "evoker",
  "hunter",
  "mage",
  "monk",
  "paladin",
  "priest",
  "rogue",
  "shaman",
  "warlock",
  "warrior",
]);
export type ClassColorKey = z.infer<typeof CLASS_COLOR_KEY_SCHEMA>;

export const CHAMPION_SCHEMA = z.strictObject({
  /** #203: content-migration version stamp (see `SCHEMA_VERSION_SCHEMA`). */
  schemaVersion: SCHEMA_VERSION_SCHEMA,
  id: CHAMPION_ID_SCHEMA,
  /** #163: rename-safe human name (opaque `id` is the stable identity). Distinct
   *  from `name` (the display title); the slug is the stable authoring handle. */
  slug: SLUG_SCHEMA,
  name: z.string().min(1).max(64),
  stats: CHAMPION_STATS_SCHEMA,
  /**
   * #190: an ordered LIST of ability IDs (references into the standalone ability
   * registry, `ContentBundle.abilities`), NOT inlined ability objects. The list
   * ORDER is the SLOT order (index = slot, OQ1) — the wire's cast `slot` indexes
   * into this list. Bounded to `MAX_ABILITY_SLOT + 1` entries so every index is a
   * valid `AbilitySlot` (the loader injects `slot: index` when resolving). The
   * loader resolves this id-list into `Champion.abilities: Ability[]` at bundle
   * load; referential integrity (every id resolves) is a content-validation
   * concern (crossValidate, mirroring the aura registry). Duplicate ids ACROSS
   * champions are valid (sharing, OQ3).
   */
  abilities: z.array(ABILITY_ID_SCHEMA).min(1).max(MAX_ABILITY_SLOT + 1),
  /**
   * #76: state-conditional slot overrides. Optional — absent ⇒ byte-identical
   * to pre-#76 content (every slot resolves unconditionally to
   * `abilities[slot]`, exactly as `abilityOnSlot` always has). Present ⇒ one
   * entry per OVERRIDDEN slot (not every slot needs one); `resolveSlotAbility`
   * (@pit/sim) reads the caster's CURRENT hashed state to pick between the
   * slot's normal ability and a rule's override, at every cast-pipeline call
   * site that used to read `abilities[slot]` directly. See
   * `CONDITIONAL_SLOT_SCHEMA`.
   */
  conditionalSlots: z.array(CONDITIONAL_SLOT_SCHEMA).max(MAX_ABILITY_SLOT + 1).optional(),
  /**
   * Auto-attack weapon (#93). Optional — absent ⇒ the champion has no
   * auto-attack (byte-identical to pre-#93 content: the sim never starts a
   * swing for a weapon-less champion). Present ⇒ the sim auto-casts it on a
   * swing timer at the champion's hostile target whenever the swing gates pass.
   * See WEAPON_SCHEMA.
   */
  weapon: WEAPON_SCHEMA.optional(),
  /**
   * Castable resource pool (#89). Optional — absent ⇒ a resource-less
   * champion (every ability `cost` must be 0, byte-identical to pre-#89
   * content: `Entity.resource` is `null` and hashes as a bare marker).
   * Present ⇒ casts gate on + deduct `cost` from the pool, which regens
   * each active tick.
   */
  resource: RESOURCE_DEF_SCHEMA.optional(),
  /**
   * Builder/spender point pool (#90). Optional — absent ⇒ a point-less champion
   * (`buildPoints`/`spendPoints`/`perPointBonus`/`durationPerPoint` are rejected
   * by crossValidate, and `Entity.points` is `null`, hashed as a bare marker —
   * byte-identical to pre-#90 content). Present ⇒ the champion spawns with an
   * EMPTY pool (`current = 0`); builders add points and finishers consume + scale
   * off them. Composes with `resource` (a champion may declare both).
   */
  points: POINTS_DEF_SCHEMA.optional(),
  /**
   * #617: per-champion identity color, referenced by CLASS-ARCHETYPE KEY (not
   * a raw hex) — see `CLASS_COLOR_KEY_SCHEMA`. Optional — absent ⇒ the client
   * falls back to a STABLE hashed-palette color keyed off `id` (see
   * `championColor` in `apps/client/src/champions/championColors.ts`),
   * preserving the pre-#617 "config-authored champion with no curated color
   * yet" behavior — just by omission now instead of a missing row in a
   * separate client-side table (the exact failure mode #419 hit: 5 shipped
   * champions silently missing a row). PURELY COSMETIC/PRESENTATION — the sim
   * never reads this field, and it is explicitly STRIPPED from the content
   * hash (`@pit/content`'s `hash.ts`, alongside `schemaVersion`) so a pure
   * recolor never bumps the content hash, moves a replay golden, or
   * invalidates a persisted match's `contentHash`.
   */
  classColorKey: CLASS_COLOR_KEY_SCHEMA.optional(),
});

/**
 * The on-disk / authoring champion def (#190) — inferred from `CHAMPION_SCHEMA`.
 * `abilities` is an `AbilityId[]` (an ordered id-list). `parseChampion` returns
 * THIS; the content loader resolves it into a `Champion` (below) before the sim
 * ever sees it.
 */
export type ChampionDef = z.infer<typeof CHAMPION_SCHEMA>;

/**
 * A RESOLVED champion (#190) — a `ChampionDef` whose `abilities` id-list has been
 * resolved by the loader into concrete `Ability[]` (in slot order). This is what
 * `ContentBundle.champions` holds and the sim/server/render consume; the sim's
 * `champion.abilities.map(...)` / `champion.abilities[slot]` reads are unchanged
 * from before the split.
 */
export type Champion = Omit<ChampionDef, "abilities"> & {
  readonly abilities: readonly Ability[];
};

// ─── Map ───────────────────────────────────────────────────────────────────

export const SPAWN_POINT_SCHEMA = z.strictObject({
  x: UNITS_SCHEMA,
  y: UNITS_SCHEMA,
});

/**
 * #235: one team's spawn points (3 per side at MVP — see `MVP_TEAM_SPAWN_
 * COUNT` in `@pit/content`'s `crossValidate.ts`, which pins the exact MVP
 * count; this schema only requires "at least one" so non-MVP/test content
 * isn't forced into the 3-count shape). Index 1 of 3 is the canonical
 * "middle" point a 1v1 (team size 1) seat spawns at (`state.ts`); a 3v3
 * (team size 3) seat spawns one-per-seat across all 3, in seat order.
 */
export const TEAM_SPAWNS_SCHEMA = z.array(SPAWN_POINT_SCHEMA).min(1);

export const PILLAR_SCHEMA = z.strictObject({
  x: UNITS_SCHEMA,
  y: UNITS_SCHEMA,
  radius: UNITS_SCHEMA,
});

export const MAP_SCHEMA = z.strictObject({
  /** #203: content-migration version stamp (see `SCHEMA_VERSION_SCHEMA`). */
  schemaVersion: SCHEMA_VERSION_SCHEMA,
  id: MAP_ID_SCHEMA,
  /** #163: rename-safe human name (opaque `id` is the stable identity). */
  slug: SLUG_SCHEMA,
  width: UNITS_SCHEMA,
  height: UNITS_SCHEMA,
  /**
   * #235: `spawns[team]` is now an ARRAY of spawn points (was a single
   * point per team pre-#235) — outer index is the team, inner is the
   * spawn-point index within that team's 3-point set.
   */
  spawns: z.array(TEAM_SPAWNS_SCHEMA).min(1),
  pillars: z.array(PILLAR_SCHEMA),
});

export type PitMap = z.infer<typeof MAP_SCHEMA>;

// Re-export the branded team/seat schemas here for content cross-checks
// downstream (content package uses them without pulling messages.ts).
export { SEAT_INDEX_SCHEMA, TEAM_INDEX_SCHEMA };

// ─── Tuning knobs ──────────────────────────────────────────────────────────

// Draw resolution when both teams reach zero living entities on the same
// tick. "draw" is spec §11 default; "lastKillerWins" is a future knob.
export const SIMULTANEOUS_DEATH_SCHEMA = z.union([
  z.literal("draw"),
  z.literal("lastKillerWins"),
]);

export type SimultaneousDeath = z.infer<typeof SIMULTANEOUS_DEATH_SCHEMA>;

export const TUNING_SCHEMA = z.strictObject({
  /** Sim tick rate in ticks/second. */
  tickRate: z.number().int().gte(1).lte(240),
  /** GCD duration in ticks. */
  gcdTicks: z.number().int().gte(0).lte(1 << 12),
  /** Pre-match countdown length in ticks. */
  countdownTicks: z.number().int().gte(0).lte(1 << 16),
  /** Client render delay behind server tick, in ticks. */
  interpolationDelayTicks: z.number().int().gte(0).lte(1 << 8),
  /** Per-tick, per-seat input rate cap (server-enforced). */
  maxInputsPerTick: z.number().int().gte(1).lte(64),
  /** Does an active movement input (`moveDir`) cancel an in-progress cast?
   *  WP4/WoW default: true. */
  movingCancelsCast: z.boolean(),
  /**
   * Walk-push relaxation passes per tick (#28). Higher = overlaps in a
   * multi-unit scrum resolve more completely within one tick; lower =
   * cheaper but squishier. Bounded so the solver's per-tick work is
   * fixed (determinism + no runaway cost). 0 disables push entirely
   * (units hard-stop on contact, pre-#28 behavior).
   */
  pushIterations: z.number().int().gte(0).lte(16),
  /** Resolution when both teams' living count hits zero on the same tick. */
  simultaneousDeath: SIMULTANEOUS_DEATH_SCHEMA,
  /**
   * #27: global default facing-arc threshold for `facingPolicy.require*`
   * gates whose ability omits its own `arcCosThresholdBp`. Encoded as the
   * cosine of the arc half-angle in basis points (`round(10000·cos(α))`): a
   * caster faces its target iff the angle between its facing and the direction
   * to the target is ≤ α (see FACING_POLICY_SCHEMA for the exact integer
   * dot/cross test). 0 = a 180° arc (target anywhere in the frontal half-plane
   * counts as "facing" — the default); 10000 = must face exactly; negative
   * widens past 180° toward omni. Integer, [-10000, 10000].
   */
  facingArcCosThresholdBp: z.number().int().gte(-10000).lte(10000).default(0),
  /**
   * #121: maximum physical-damage reduction from armor, in basis points. A
   * hit's effective armor (base + aura statMods, floored at 0) is capped at
   * this value before mitigation, so no amount of armor stacking exceeds it:
   * `dealt = idiv(amount·(10000 − min(armor, armorMaxReductionBp)), 10000)`
   * for a purely-physical hit. Integer, [0, 10000]; 10000 would allow full
   * (100%) physical immunity, 0 disables armor entirely. Defaults to 7500
   * (75% cap). Armor is a no-op at base 0 regardless of this cap, so shipped
   * pre-armor content is behaviourally unchanged.
   */
  armorMaxReductionBp: z.number().int().gte(0).lte(10000).default(7500),
  /**
   * #247: global "feel dial" — a basis-points multiplier applied ONCE, at
   * content-load time (`@pit/content`'s `loadContentFrom`), to every
   * `speedUnitsPerTick`-shaped value in the bundle: champion `stats.moveSpeed`,
   * a weapon's auto-attack `projectile.speedUnitsPerTick`, an ability
   * `delivery: "projectile"`'s `speedUnitsPerTick`, and a `delivery: "zone"`'s
   * `onExpire` collapse-bolt `speedUnitsPerTick`. `10000` = 1x (today's
   * authored speeds, unchanged); `5000` = half speed; `20000` = double speed.
   * Defaults to `10000` so existing content is BYTE-IDENTICAL until someone
   * turns the dial — this is a pacing/feel knob meant to be re-tuned as
   * development continues, not a one-shot edit across every champion/weapon
   * JSON file. The scale is applied via floored integer division
   * (`idiv(value * globalSpeedScaleBp, 10000)`); a scaled value that falls
   * outside `UnitsPerTick`'s bounds (e.g. scaled to 0, or past the 256 cap) is
   * reported as a load-time `ContentError`, never silently clamped. Bounded
   * [1, 100000] (0.0001x .. 10x) — 0 would zero every speed in the bundle
   * (an unplayable, and un-debuggable, all-or-nothing state), so the floor is
   * 1 instead. See `@pit/content`'s `applyGlobalSpeedScale`.
   */
  globalSpeedScaleBp: z.number().int().gte(1).lte(100000).default(10000),
  /**
   * #624: independent range/radius scale dials — SEVEN separate basis-points
   * multipliers (this field + the six below), each applied ONCE, at
   * content-load time (`@pit/content`'s `loadContentFrom`, `applyGlobalRangeScales`),
   * to its OWN field class only. Modeled on `globalSpeedScaleBp` (#247) but
   * deliberately NOT collapsed into one shared dial — Joseph's #624 request is
   * "change the range on all of these independently easily (e.g. make all
   * cones shorter)", so each field class gets its own knob and turning one
   * never perturbs the other six. All seven share the same shape/bounds/
   * basis-points convention as `globalSpeedScaleBp`: integer, [1, 100000]
   * (0.0001x .. 10x; 0 would zero the field class entirely, an unplayable,
   * un-debuggable state, so the floor is 1), `10000` = 1x (byte-identical to
   * authored content), floored integer division
   * (`idiv(value * scaleBp, 10000)`). A scaled value that falls outside
   * `Units`' bounds is reported as a load-time `ContentError` (never silently
   * clamped) and the PRE-scale value is left in place, exactly mirroring
   * `globalSpeedScaleBp`'s never-throws contract. See
   * `@pit/content`'s `rangeScale.ts`.
   *
   * `abilityRangeScaleBp` scales every `ABILITY_SCHEMA.rangeUnits` (an
   * ability's cast range).
   */
  abilityRangeScaleBp: z.number().int().gte(1).lte(100000).default(10000),
  /**
   * #624: scales every `ABILITY_SCHEMA.minRangeUnits` (the optional ability
   * minimum-range / arming-distance deadzone). See `abilityRangeScaleBp`'s
   * doc comment for the shared shape/contract.
   */
  abilityMinRangeScaleBp: z.number().int().gte(1).lte(100000).default(10000),
  /**
   * #624: scales every `WEAPON_SCHEMA.range` (a champion's auto-attack
   * weapon range). See `abilityRangeScaleBp`'s doc comment for the shared
   * shape/contract.
   */
  weaponRangeScaleBp: z.number().int().gte(1).lte(100000).default(10000),
  /**
   * #624: scales every `WEAPON_SCHEMA.minRangeUnits` (the optional weapon
   * deadzone, mirrors the ability's). See `abilityRangeScaleBp`'s doc
   * comment for the shared shape/contract.
   */
  weaponMinRangeScaleBp: z.number().int().gte(1).lte(100000).default(10000),
  /**
   * #624: scales every `AREA_CIRCLE_SCHEMA.radius` (an ability's AOE splash
   * radius). Independent of `areaConeRangeScaleBp` — a circle and a cone are
   * different shapes, tuned separately. See `abilityRangeScaleBp`'s doc
   * comment for the shared shape/contract.
   */
  areaCircleRadiusScaleBp: z.number().int().gte(1).lte(100000).default(10000),
  /**
   * #624: scales BOTH `AREA_CONE_SCHEMA.minRangeUnits` and `maxRangeUnits`
   * together, under this ONE dial — a cone's inner and outer radius are the
   * same geometric feature (the wedge's reach), not two independently-tuned
   * knobs; Joseph's own example ("make all cones shorter") treats a cone as
   * one thing to shrink. Independent of `areaCircleRadiusScaleBp`. See
   * `abilityRangeScaleBp`'s doc comment for the shared shape/contract.
   */
  areaConeRangeScaleBp: z.number().int().gte(1).lte(100000).default(10000),
  /**
   * #624: scales every `DAMAGE_TARGET_SELECTOR_SCHEMA.rangeUnits` (the
   * cleave/nearby-target search radius a `damage` effect's optional
   * `targetSelector` reads). See `abilityRangeScaleBp`'s doc comment for the
   * shared shape/contract.
   */
  damageSelectorRangeScaleBp: z.number().int().gte(1).lte(100000).default(10000),
  /**
   * #252: WASD strafe (A/D) speed, as a fraction of the champion's base
   * `moveSpeed`, in basis points. Default 10000 (100% of forward speed) —
   * strafing is the expected DOMINANT movement style under WASD, so it is
   * NOT penalized by default. Independently configurable from
   * `backwardSpeedScaleBp` (never derived from it) so strafe and backward
   * speed can be tuned separately. Integer, [0, 10000].
   */
  strafeSpeedScaleBp: z.number().int().gte(0).lte(10000).default(10000),
  /**
   * #252: WASD backward (S) speed, as a fraction of the champion's base
   * `moveSpeed`, in basis points. Default 5000 (50% of forward speed) — a
   * deliberate balance lever, not a placeholder: moving backward is meant to
   * be a real defensive choice specifically because it slows outgoing
   * projectile momentum (#149). Integer, [0, 10000].
   */
  backwardSpeedScaleBp: z.number().int().gte(0).lte(10000).default(5000),
  /**
   * #448: global default `breakOnDamageThreshold` (#118) for every `fear`-
   * classed CC aura whose def does NOT author an explicit override — one
   * shared tuning constant for the whole `ccClasses: ["fear"]` group, mirroring
   * how `tickRate`/`armorMaxReductionBp` are single global knobs rather than
   * per-content-file numbers. Before #448, all 4 shipped fears
   * (`fear`/`death-coil-fear`/`intimidating-shout-fear`/`psychic-scream`)
   * carried NO `breakOnDamageThreshold` at all (never broke on damage),
   * contradicting the intended "fear breaks like other hard CC" design — this
   * constant fixes that uniformly instead of hand-authoring the same number
   * onto every fear file. `1` ⇒ breaks on ANY positive post-absorb damage
   * (matches the existing `mez` precedent — both shipped mez auras already
   * use an explicit `1`). FLAGGED for design sign-off (Joseph): this exact
   * value is an implementer proposal, not a confirmed balance number — see
   * the #448 PR description.
   */
  fearBreakOnDamageThreshold: z.number().int().gte(0).lte(1 << 24).default(1),
  /**
   * #448: global default `breakOnDamageThreshold` for every `root`-classed CC
   * aura whose def does NOT author an explicit override — same shared-constant
   * pattern as `fearBreakOnDamageThreshold`. An individual root MAY still
   * override it (`frost-nova-root`'s existing `80`, preserved unchanged as an
   * intentional per-ability divergence — 10% of Mage max HP, presumably a
   * deliberate "tankier root" balance choice, NOT flattened to this default).
   * `bola-root` (previously unset ⇒ never broke) now inherits this default;
   * `hamstring-root`'s existing explicit `1` is unchanged (already equal to
   * this default, authored as its own explicit value, not derived from it).
   * FLAGGED for design sign-off (Joseph) — see the #448 PR description.
   */
  rootBreakOnDamageThreshold: z.number().int().gte(0).lte(1 << 24).default(1),
  /**
   * #448: global default `breakOnDamageThreshold` for every `mez`-classed CC
   * aura whose def does NOT author an explicit override — locks in today's
   * coincidental agreement between the two shipped mez auras
   * (`poison-dart-mez`/`polymorph-mez`, both already `1`) as an ENFORCED
   * default rather than a happy accident a third mez could silently diverge
   * from. A mez def MAY still author an explicit `breakOnDamageThreshold`
   * that differs from this default (the same override latitude `root`
   * already has) — that is a real, intentional per-ability balance choice,
   * not rejected, just no longer able to drift by OMISSION. FLAGGED for design
   * sign-off (Joseph) — see the #448 PR description.
   */
  mezBreakOnDamageThreshold: z.number().int().gte(0).lte(1 << 24).default(1),
  /**
   * #453: global default passive HP regen INTERVAL, in ticks — every
   * champion whose `stats.hpRegen` override is absent heals
   * `hpRegenAmount` every `hpRegenIntervalTicks` ticks, combat-agnostic (no
   * "out of combat" gating — see the #453 PR description for that explicit
   * design call). Default 150 ticks = 5s @ the confirmed 30 Hz `tickRate`.
   * Deliberately an INTERVAL, not a per-tick amount (see
   * `CHAMPION_HP_REGEN_SCHEMA`'s doc comment for why a flat per-tick add
   * can't express a sub-1-per-tick rate as a whole integer). Runs only
   * while `phase.kind === "active"` (the same gate `regenResources`/
   * `rechargeCharges` already use), so it never fires during `countdown`.
   */
  hpRegenIntervalTicks: z.number().int().gte(1).lte(1 << 20).default(150),
  /**
   * #453: global default passive HP regen AMOUNT (per firing, every
   * `hpRegenIntervalTicks` ticks). Default 1 (1 HP / 5s @ the default
   * interval). `0` would make regen a global no-op — kept legal (matches
   * `regenPerTick`'s own `gte(0)` floor) rather than special-cased, since a
   * future test/tuning pass may want to disable it wholesale without also
   * touching every champion.
   */
  hpRegenAmount: z.number().int().gte(0).lte(1 << 20).default(1),
});

export type Tuning = z.infer<typeof TUNING_SCHEMA>;
